import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import './App.css';

// Deterministic star positions for splash screen (avoids re-render randomness)
const SPLASH_STARS = Array.from({ length: 70 }, (_, i) => ({
  x: ((Math.sin(i * 7.391) * 0.5 + 0.5) * 100).toFixed(2),
  y: ((Math.cos(i * 4.173) * 0.5 + 0.5) * 100).toFixed(2),
  size: ((Math.sin(i * 2.917) * 0.5 + 0.5) * 2 + 0.5).toFixed(2),
  delay: ((Math.cos(i * 1.731) * 0.5 + 0.5) * 3).toFixed(2),
  dur:   ((Math.sin(i * 3.312) * 0.5 + 0.5) * 2 + 1.5).toFixed(2),
}));

const BEST_TIMES = {
  Instagram: { emoji: '📸', days: 'Tue – Fri', times: '9 – 11am & 7 – 9pm', tz: 'local time' },
  LinkedIn:  { emoji: '💼', days: 'Tue – Thu', times: '8 – 10am & 12 – 1pm', tz: 'local time' },
  Facebook:  { emoji: '📘', days: 'Wed – Fri', times: '1 – 4pm', tz: 'local time' },
  TikTok:    { emoji: '🎵', days: 'Tue, Thu, Fri', times: '6 – 9am & 7 – 11pm', tz: 'local time' },
};

const COMMON_TIMEZONES = [
  { label: 'Sydney / Melbourne (AEDT)', value: 'Australia/Sydney' },
  { label: 'Brisbane (AEST)', value: 'Australia/Brisbane' },
  { label: 'Perth (AWST)', value: 'Australia/Perth' },
  { label: 'Adelaide (ACDT)', value: 'Australia/Adelaide' },
  { label: 'Auckland (NZDT)', value: 'Pacific/Auckland' },
  { label: 'London (GMT/BST)', value: 'Europe/London' },
  { label: 'Paris / Berlin (CET)', value: 'Europe/Paris' },
  { label: 'Dubai (GST)', value: 'Asia/Dubai' },
  { label: 'Singapore / KL (SGT)', value: 'Asia/Singapore' },
  { label: 'Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: 'Mumbai / Delhi (IST)', value: 'Asia/Kolkata' },
  { label: 'Johannesburg (SAST)', value: 'Africa/Johannesburg' },
  { label: 'New York (ET)', value: 'America/New_York' },
  { label: 'Chicago (CT)', value: 'America/Chicago' },
  { label: 'Los Angeles (PT)', value: 'America/Los_Angeles' },
  { label: 'Toronto (ET)', value: 'America/Toronto' },
  { label: 'Vancouver (PT)', value: 'America/Vancouver' },
];

const LANGUAGES = [
  { name: 'English',              flag: '🇬🇧', priority: true },
  { name: 'Spanish',              flag: '🇪🇸', priority: true },
  { name: 'Portuguese',           flag: '🇧🇷', priority: true },
  { name: 'French',               flag: '🇫🇷', priority: true },
  { name: 'German',               flag: '🇩🇪', priority: true },
  { name: 'Italian',              flag: '🇮🇹', priority: true },
  { name: 'Dutch',                flag: '🇳🇱', priority: true },
  { name: 'Arabic',               flag: '🇸🇦', priority: true },
  { name: 'Hindi',                flag: '🇮🇳', priority: true },
  { name: 'Japanese',             flag: '🇯🇵', priority: true },
  { name: 'Korean',               flag: '🇰🇷', priority: true },
  { name: 'Chinese (Simplified)', flag: '🇨🇳', priority: true },
  { name: 'Afrikaans',   flag: '🇿🇦' },
  { name: 'Albanian',    flag: '🇦🇱' },
  { name: 'Amharic',     flag: '🇪🇹' },
  { name: 'Bengali',     flag: '🇧🇩' },
  { name: 'Bulgarian',   flag: '🇧🇬' },
  { name: 'Catalan',     flag: '🇪🇸' },
  { name: 'Croatian',    flag: '🇭🇷' },
  { name: 'Czech',       flag: '🇨🇿' },
  { name: 'Danish',      flag: '🇩🇰' },
  { name: 'Estonian',    flag: '🇪🇪' },
  { name: 'Finnish',     flag: '🇫🇮' },
  { name: 'Greek',       flag: '🇬🇷' },
  { name: 'Gujarati',    flag: '🇮🇳' },
  { name: 'Hebrew',      flag: '🇮🇱' },
  { name: 'Hungarian',   flag: '🇭🇺' },
  { name: 'Indonesian',  flag: '🇮🇩' },
  { name: 'Latvian',     flag: '🇱🇻' },
  { name: 'Lithuanian',  flag: '🇱🇹' },
  { name: 'Malay',       flag: '🇲🇾' },
  { name: 'Marathi',     flag: '🇮🇳' },
  { name: 'Norwegian',   flag: '🇳🇴' },
  { name: 'Persian',     flag: '🇮🇷' },
  { name: 'Polish',      flag: '🇵🇱' },
  { name: 'Romanian',    flag: '🇷🇴' },
  { name: 'Russian',     flag: '🇷🇺' },
  { name: 'Serbian',     flag: '🇷🇸' },
  { name: 'Slovak',      flag: '🇸🇰' },
  { name: 'Slovenian',   flag: '🇸🇮' },
  { name: 'Swahili',     flag: '🇰🇪' },
  { name: 'Swedish',     flag: '🇸🇪' },
  { name: 'Tagalog',     flag: '🇵🇭' },
  { name: 'Tamil',       flag: '🇮🇳' },
  { name: 'Telugu',      flag: '🇮🇳' },
  { name: 'Thai',        flag: '🇹🇭' },
  { name: 'Turkish',     flag: '🇹🇷' },
  { name: 'Ukrainian',   flag: '🇺🇦' },
  { name: 'Urdu',        flag: '🇵🇰' },
  { name: 'Vietnamese',  flag: '🇻🇳' },
  { name: 'Welsh',       flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },
  { name: 'Zulu',        flag: '🇿🇦' },
];

const AI_TONES = [
  { slug: 'professional',  label: 'Professional',  icon: '💼', desc: 'Polished, credible, business-ready' },
  { slug: 'casual',        label: 'Casual',         icon: '😊', desc: 'Friendly, relaxed, like a real person' },
  { slug: 'witty',         label: 'Witty',          icon: '😄', desc: 'Clever wordplay, humour, memorable hooks' },
  { slug: 'inspirational', label: 'Inspirational',  icon: '✨', desc: 'Motivating, uplifting, emotionally resonant' },
  { slug: 'educational',   label: 'Educational',    icon: '📚', desc: 'Teach something, add value, build authority' },
  { slug: 'storytelling',  label: 'Storytelling',   icon: '📖', desc: 'Narrative arc, personal, draws people in' },
  { slug: 'promotional',   label: 'Promotional',    icon: '📣', desc: 'Direct, sales-focused, clear CTA' },
];

// Convert a date + time string in a specific IANA timezone to a UTC Date
function zonedTimeToUtc(dateStr, timeStr, timezone) {
  const ref = new Date(`${dateStr}T${timeStr}:00`);
  const fmt = tz => new Intl.DateTimeFormat('sv-SE', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).format(ref).replace(' ', 'T') + 'Z';
  const offsetMs = new Date(fmt(timezone)) - new Date(fmt('UTC'));
  return new Date(new Date(`${dateStr}T${timeStr}:00Z`) - offsetMs);
}

const MAX_DIMENSION = 1568;
const JPEG_QUALITY = 0.92;
const MAX_BASE64_BYTES = 4.5 * 1024 * 1024; // stay under Claude's 5 MB limit

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width > height) { height = Math.round((height * MAX_DIMENSION) / width); width = MAX_DIMENSION; }
        else { width = Math.round((width * MAX_DIMENSION) / height); height = MAX_DIMENSION; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);

      // Reduce quality until output fits under Claude's 5 MB base64 limit
      let quality = JPEG_QUALITY;
      let base64;
      do {
        base64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
        quality = Math.round(quality * 0.8 * 100) / 100;
      } while (base64.length > MAX_BASE64_BYTES && quality > 0.1);

      resolve(base64);
    };
    img.onerror = reject;
    img.src = url;
  });
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STEP_LABELS = ['Media', 'Edit & Logo', 'Generate', 'Posts'];

// Post goal shapes the AI caption (tone + structure). 'conversion' unlocks the
// offer/CTA/audience fields and a framework picker. Keys are sent to the backend.
const POST_GOALS = [
  { key: 'awareness',  emoji: '🔔', label: 'Awareness',  desc: 'Grow reach and visibility' },
  { key: 'engagement', emoji: '💬', label: 'Engagement', desc: 'Spark comments, saves, shares' },
  { key: 'conversion', emoji: '🎯', label: 'Conversion', desc: 'Drive a specific action' },
];

const CONVERSION_FRAMEWORKS = [
  { key: 'fear_crusher',   label: 'Fear Crusher',        desc: 'Address the #1 objection, then make the offer' },
  { key: 'transformation', label: 'Transformation Hook', desc: 'Show a relatable before/after result' },
  { key: 'curiosity',      label: 'Curiosity Hook',      desc: 'Tease what happens, drive link clicks' },
  { key: 'social_proof',   label: 'Social Proof',        desc: 'Real member result + offer' },
  { key: 'direct_offer',   label: 'Direct Offer',        desc: 'Clean, simple CTA — no fluff' },
];

// Tap-to-append CTAs shown under the caption editor. Static for now.
const QUICK_CTAS = [
  'Free first class — link in bio',
  'DM us FREE to claim your spot',
  'Book now — link in bio',
  'Limited spots — link in bio',
];

const CAMERA_RATIOS = [
  { label: 'Free', value: 'free' },
  { label: '1:1', value: '1:1' },
  { label: '4:3', value: '4:3' },
  { label: '9:16', value: '9:16' },
  { label: '16:9', value: '16:9' },
];

const formatTime = s => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const CAMERA_FILTERS = [
  { label: 'Normal', value: 'none' },
  { label: 'Vivid', value: 'contrast(1.2) saturate(1.6) brightness(1.08)' },
  { label: 'Warm', value: 'sepia(0.35) saturate(1.4) brightness(1.05)' },
  { label: 'Cool', value: 'hue-rotate(20deg) saturate(0.9) brightness(1.05)' },
  { label: 'B&W', value: 'grayscale(1)' },
  { label: 'Fade', value: 'contrast(0.8) brightness(1.15) saturate(0.7)' },
  { label: 'Crisp', value: 'contrast(1.3) saturate(1.1)' },
];

function AdminPanel({ supabase }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [actionStatus, setActionStatus] = useState({}); // userId -> message
  const [limitInput, setLimitInput] = useState({}); // userId -> string

  const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  };

  const load = async () => {
    setLoading(true);
    const token = await getToken();
    const res = await fetch('https://blinkapost.onrender.com/admin/users', {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    setUsers(Array.isArray(data) ? data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setStatus = (userId, msg) => {
    setActionStatus(s => ({ ...s, [userId]: msg }));
    setTimeout(() => setActionStatus(s => ({ ...s, [userId]: null })), 4000);
  };

  const sendMagicLink = async (u) => {
    setStatus(u.id, 'Sending...');
    const token = await getToken();
    const res = await fetch('https://blinkapost.onrender.com/admin/magic-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ email: u.email })
    });
    setStatus(u.id, res.ok ? 'Magic link sent!' : 'Failed to send');
  };

  const resendConfirmation = async (u) => {
    setStatus(u.id, 'Sending...');
    const token = await getToken();
    const res = await fetch('https://blinkapost.onrender.com/admin/resend-confirmation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ userId: u.id })
    });
    setStatus(u.id, res.ok ? 'Confirmation sent!' : 'Failed to send');
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    setStatus(u.id, 'Deleting...');
    const token = await getToken();
    const res = await fetch(`https://blinkapost.onrender.com/admin/delete-user/${u.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      setUsers(prev => prev.filter(x => x.id !== u.id));
    } else {
      setStatus(u.id, 'Failed to delete');
    }
  };

  const setGenerationLimit = async (u) => {
    const val = parseInt(limitInput[u.id], 10);
    if (isNaN(val) || val < 0) { setStatus(u.id, 'Enter a valid number'); return; }
    setStatus(u.id, 'Saving...');
    const token = await getToken();
    const res = await fetch('https://blinkapost.onrender.com/admin/set-limit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ userId: u.id, limit: val })
    });
    if (res.ok) {
      setStatus(u.id, 'Limit updated!');
      load();
    } else {
      setStatus(u.id, 'Failed to update');
    }
  };

  const resetUsage = async (u) => {
    setStatus(u.id, 'Resetting...');
    const token = await getToken();
    const res = await fetch('https://blinkapost.onrender.com/admin/reset-usage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ userId: u.id })
    });
    if (res.ok) {
      setStatus(u.id, 'Usage reset!');
      load();
    } else {
      setStatus(u.id, 'Failed to reset');
    }
  };

  const PLATFORM_COLORS = { linkedin: '#0A66C2', facebook: '#1877F2', instagram: '#E1306C', tiktok: '#010101', google_business: '#34A853' };
  const PLATFORM_ICONS = { linkedin: 'in', facebook: 'f', instagram: '📷', tiktok: '♪', google_business: 'G' };
  const btnStyle = (color) => ({ flex: 1, padding: '8px 6px', background: color, border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: 'white', cursor: 'pointer' });

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#111', margin: '0 0 4px' }}>Admin Panel</h2>
      <p style={{ fontSize: '13px', color: '#888', margin: '0 0 20px' }}>{users.length} users signed up</p>
      {loading && <p style={{ color: '#888', textAlign: 'center' }}>Loading...</p>}
      {!loading && users.length === 0 && <p style={{ color: '#888', textAlign: 'center' }}>No users found.</p>}
      {users.map(u => (
        <div key={u.id} style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '10px', overflow: 'hidden' }}>
          <button
            onClick={() => setExpanded(expanded === u.id ? null : u.id)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                {u.email}
                {!u.confirmed && <span style={{ fontSize: '10px', background: '#fef3c7', color: '#d97706', padding: '1px 6px', borderRadius: '4px', fontWeight: '700' }}>unconfirmed</span>}
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <span>Joined {new Date(u.created_at).toLocaleDateString()}</span>
                {u.beta && <span style={{ color: '#6366f1' }}>{u.beta.generations_used}/{u.beta.generation_limit} generations</span>}
                {u.accounts.length > 0 && <span>{[...new Set(u.accounts.map(a => a.platform))].join(', ')}</span>}
              </div>
            </div>
            <span style={{ color: '#ccc', marginLeft: '8px' }}>{expanded === u.id ? '▲' : '▼'}</span>
          </button>

          {expanded === u.id && (
            <div style={{ borderTop: '1px solid #f0f0f0', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

              {/* Last sign in */}
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                Last sign in: {u.last_sign_in ? new Date(u.last_sign_in).toLocaleString() : 'Never'}
              </div>

              {/* Connected accounts */}
              {u.accounts.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {u.accounts.map((acc, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f9fafb', borderRadius: '8px', padding: '6px 10px' }}>
                      <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: PLATFORM_COLORS[acc.platform] || '#999', color: 'white', fontSize: '9px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{PLATFORM_ICONS[acc.platform] || '?'}</span>
                      <span style={{ fontSize: '12px', color: '#374151' }}>{acc.page_name || acc.platform}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={() => sendMagicLink(u)} style={btnStyle('#6366f1')}>Send magic link</button>
                {!u.confirmed && <button onClick={() => resendConfirmation(u)} style={btnStyle('#f59e0b')}>Resend confirmation</button>}
              </div>

              {/* Generation limit */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="number"
                  placeholder={u.beta ? String(u.beta.generation_limit) : 'Set limit'}
                  value={limitInput[u.id] || ''}
                  onChange={e => setLimitInput(s => ({ ...s, [u.id]: e.target.value }))}
                  style={{ flex: 1, minWidth: '80px', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
                />
                <button onClick={() => setGenerationLimit(u)} style={{ ...btnStyle('#10b981'), flexShrink: 0 }}>Set limit</button>
                {u.beta && <button onClick={() => resetUsage(u)} style={{ ...btnStyle('#6b7280'), flexShrink: 0 }}>Reset usage</button>}
              </div>

              {/* Status message */}
              {actionStatus[u.id] && (
                <div style={{ fontSize: '12px', color: '#6366f1', fontWeight: '600', textAlign: 'center' }}>{actionStatus[u.id]}</div>
              )}

              {/* Danger zone */}
              <button onClick={() => deleteUser(u)} style={{ width: '100%', padding: '8px', background: 'none', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#ef4444', cursor: 'pointer' }}>
                Delete user
              </button>
            </div>
          )}
        </div>
      ))}
      <button onClick={load} style={{ width: '100%', marginTop: '8px', padding: '12px', background: '#f3f4f6', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
        Refresh
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Themed Template Editor — colour palette + frames + text overlay
//  Self-contained modal. Loads a photo, derives a colour theme from
//  its dominant colours, lets the user pick a frame style and add
//  draggable styled text, then composites everything onto one canvas
//  and returns a JPEG blob to the post composer. Non-destructive:
//  the original upload is untouched; we only read it.
// ─────────────────────────────────────────────────────────────

function rgbToHex([r, g, b]) {
  return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}

// Median-cut dominant-colour extraction. Fully client-side, no dependency —
// downsample the image, then repeatedly split the colour box with the widest
// channel range until we have `count` buckets, and average each bucket.
function extractPalette(img, count = 6) {
  const fallback = ['#4F46E5', '#7c3aed', '#ec4899', '#f59e0b', '#10b981', '#111827'];
  try {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    const scale = Math.min(1, 96 / Math.max(iw, ih));
    const w = Math.max(1, Math.round(iw * scale));
    const h = Math.max(1, Math.round(ih * scale));
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    const pixels = [];
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 125) continue;            // skip near-transparent
      pixels.push([data[i], data[i + 1], data[i + 2]]);
    }
    if (pixels.length < count) return fallback;
    let buckets = [pixels];
    while (buckets.length < count) {
      let bestIdx = -1, bestRange = -1, bestCh = 0;
      for (let b = 0; b < buckets.length; b++) {
        const bk = buckets[b];
        if (bk.length < 2) continue;
        const min = [255, 255, 255], max = [0, 0, 0];
        for (const p of bk) for (let ch = 0; ch < 3; ch++) {
          if (p[ch] < min[ch]) min[ch] = p[ch];
          if (p[ch] > max[ch]) max[ch] = p[ch];
        }
        for (let ch = 0; ch < 3; ch++) {
          const range = max[ch] - min[ch];
          if (range > bestRange) { bestRange = range; bestIdx = b; bestCh = ch; }
        }
      }
      if (bestIdx < 0) break;
      const bk = buckets[bestIdx];
      bk.sort((p, q) => p[bestCh] - q[bestCh]);
      const mid = bk.length >> 1;
      buckets.splice(bestIdx, 1, bk.slice(0, mid), bk.slice(mid));
    }
    const colors = buckets.map(bk => {
      const sum = [0, 0, 0];
      for (const p of bk) { sum[0] += p[0]; sum[1] += p[1]; sum[2] += p[2]; }
      return { rgb: [sum[0] / bk.length, sum[1] / bk.length, sum[2] / bk.length], pop: bk.length };
    }).sort((a, b) => b.pop - a.pop);              // most-common colours first
    const hexes = colors.map(col => rgbToHex(col.rgb));
    while (hexes.length < count) hexes.push(fallback[hexes.length]);
    return hexes;
  } catch {
    return fallback;
  }
}

const BORDER_STYLES = [
  { key: 'none',     label: 'None' },
  { key: 'solid',    label: 'Solid band' },
  { key: 'gradient', label: 'Gradient' },
  { key: 'corners',  label: 'Corners' },
  { key: 'double',   label: 'Double line' },
];

const EDITOR_FONTS = [
  { label: 'Poster',  value: '"Impact", "Arial Black", sans-serif' },
  { label: 'Clean',   value: 'Arial, Helvetica, sans-serif' },
  { label: 'Elegant', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Mono',    value: '"Courier New", monospace' },
  { label: 'Script',  value: '"Brush Script MT", "Segoe Script", cursive' },
];

const TEXT_PRESETS = [
  { label: '⬆ Top banner',   yFrac: 0.12 },
  { label: '⊙ Centre',       yFrac: 0.50 },
  { label: '⬇ Bottom third', yFrac: 0.82 },
];

let _layerSeq = 0;

function ImageTemplateEditor({ imageSrc, onApply, onClose }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const boxesRef = useRef({});   // layer id -> preview-space bounding box, for hit-testing
  const dragRef = useRef(null);

  const [imgLoaded, setImgLoaded] = useState(false);
  const [palette, setPalette] = useState(['#4F46E5', '#7c3aed', '#ec4899', '#f59e0b', '#10b981', '#111827']);
  const [preview, setPreview] = useState({ w: 320, h: 320 });
  const [borderStyle, setBorderStyle] = useState('solid');
  const [borderColorIdx, setBorderColorIdx] = useState(0);
  const [textLayers, setTextLayers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const selectedLayer = textLayers.find(l => l.id === selectedId) || null;

  // Load image + derive theme
  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      imgRef.current = image;
      const iw = image.naturalWidth || image.width;
      const ih = image.naturalHeight || image.height;
      const s = Math.min(340 / iw, 420 / ih);
      setPreview({ w: Math.max(1, Math.round(iw * s)), h: Math.max(1, Math.round(ih * s)) });
      setPalette(extractPalette(image, 6));
      setImgLoaded(true);
    };
    image.onerror = () => setImgLoaded(false);
    image.src = imageSrc;
  }, [imageSrc]);

  // ── Shared rendering pipeline — used by both live preview and export ──
  const drawBorder = (ctx, W, H) => {
    if (borderStyle === 'none') return;
    const t = Math.max(2, Math.round(Math.min(W, H) * 0.045));
    const col = palette[borderColorIdx] || palette[0];
    const col2 = palette[(borderColorIdx + 1) % palette.length] || col;
    const band = (style) => {
      ctx.fillStyle = style;
      ctx.fillRect(0, 0, W, t); ctx.fillRect(0, H - t, W, t);
      ctx.fillRect(0, 0, t, H); ctx.fillRect(W - t, 0, t, H);
    };
    if (borderStyle === 'solid') {
      band(col);
    } else if (borderStyle === 'gradient') {
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, col); g.addColorStop(1, col2);
      band(g);
    } else if (borderStyle === 'corners') {
      const len = Math.round(Math.min(W, H) * 0.16);
      ctx.fillStyle = col;
      ctx.fillRect(0, 0, len, t);         ctx.fillRect(0, 0, t, len);          // TL
      ctx.fillRect(W - len, 0, len, t);   ctx.fillRect(W - t, 0, t, len);      // TR
      ctx.fillRect(0, H - t, len, t);     ctx.fillRect(0, H - len, t, len);    // BL
      ctx.fillRect(W - len, H - t, len, t); ctx.fillRect(W - t, H - len, t, len); // BR
    } else if (borderStyle === 'double') {
      const lw = Math.max(2, Math.round(t * 0.28));
      const m1 = Math.round(t * 0.45);
      const m2 = m1 + Math.round(t * 0.75);
      ctx.strokeStyle = col; ctx.lineWidth = lw;
      ctx.strokeRect(m1 + lw / 2, m1 + lw / 2, W - 2 * m1 - lw, H - 2 * m1 - lw);
      ctx.strokeStyle = col2; ctx.lineWidth = Math.max(1, Math.round(lw * 0.7));
      ctx.strokeRect(m2 + lw / 2, m2 + lw / 2, W - 2 * m2 - lw, H - 2 * m2 - lw);
    }
  };

  const drawTexts = (ctx, W, H, record) => {
    const boxes = {};
    for (const layer of textLayers) {
      const fontPx = Math.max(6, layer.sizeFrac * W);
      const fam = EDITOR_FONTS[layer.fontIdx]?.value || EDITOR_FONTS[0].value;
      ctx.font = `700 ${fontPx}px ${fam}`;
      ctx.textAlign = layer.align;
      ctx.textBaseline = 'middle';
      const x = layer.xFrac * W;
      const y = layer.yFrac * H;
      const lines = String(layer.text || ' ').split('\n');
      const lineH = fontPx * 1.18;
      if (layer.shadow) {
        ctx.shadowColor = 'rgba(0,0,0,0.55)';
        ctx.shadowBlur = fontPx * 0.16;
        ctx.shadowOffsetY = fontPx * 0.06;
      }
      ctx.fillStyle = layer.color;
      lines.forEach((line, li) => {
        ctx.fillText(line, x, y + (li - (lines.length - 1) / 2) * lineH);
      });
      ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
      if (record) {
        let maxw = 0;
        lines.forEach(line => { const m = ctx.measureText(line || ' '); if (m.width > maxw) maxw = m.width; });
        const totalH = lineH * lines.length;
        let bx = x - maxw / 2;
        if (layer.align === 'left') bx = x;
        else if (layer.align === 'right') bx = x - maxw;
        boxes[layer.id] = { x: bx, y: y - totalH / 2, w: maxw, h: totalH };
      }
    }
    if (record) {
      boxesRef.current = boxes;
      const b = selectedId && boxes[selectedId];
      if (b) {                                       // dashed selection outline (preview only)
        ctx.save();
        ctx.strokeStyle = '#4F46E5';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 4]);
        ctx.strokeRect(b.x - 5, b.y - 5, b.w + 10, b.h + 10);
        ctx.restore();
      }
    }
  };

  const renderTo = (ctx, W, H, record) => {
    ctx.clearRect(0, 0, W, H);
    if (imgRef.current) ctx.drawImage(imgRef.current, 0, 0, W, H);
    drawBorder(ctx, W, H);
    drawTexts(ctx, W, H, record);
  };

  // Live preview redraw whenever anything changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgLoaded) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(preview.w * dpr);
    canvas.height = Math.round(preview.h * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    renderTo(ctx, preview.w, preview.h, true);
  }, [imgLoaded, preview, borderStyle, borderColorIdx, textLayers, palette, selectedId]);

  // ── Drag-to-position text ──
  const posFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const onPointerDown = (e) => {
    if (!textLayers.length) return;
    const { x, y } = posFromEvent(e);
    for (let i = textLayers.length - 1; i >= 0; i--) {   // topmost first
      const b = boxesRef.current[textLayers[i].id];
      if (b && x >= b.x - 8 && x <= b.x + b.w + 8 && y >= b.y - 8 && y <= b.y + b.h + 8) {
        setSelectedId(textLayers[i].id);
        dragRef.current = { id: textLayers[i].id, sx: x, sy: y, ox: textLayers[i].xFrac, oy: textLayers[i].yFrac };
        try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* not fatal */ }
        return;
      }
    }
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const { x, y } = posFromEvent(e);
    const nx = Math.min(0.98, Math.max(0.02, d.ox + (x - d.sx) / preview.w));
    const ny = Math.min(0.97, Math.max(0.03, d.oy + (y - d.sy) / preview.h));
    setTextLayers(prev => prev.map(l => l.id === d.id ? { ...l, xFrac: nx, yFrac: ny } : l));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const addText = (yFrac = 0.5) => {
    const id = ++_layerSeq;
    setTextLayers(prev => [...prev, {
      id, text: 'Your text', fontIdx: 0, sizeFrac: 0.085,
      color: '#ffffff', align: 'center', shadow: true, xFrac: 0.5, yFrac,
    }]);
    setSelectedId(id);
  };
  const updateLayer = (patch) => {
    if (!selectedId) return;
    setTextLayers(prev => prev.map(l => l.id === selectedId ? { ...l, ...patch } : l));
  };
  const deleteLayer = () => {
    if (!selectedId) return;
    setTextLayers(prev => prev.filter(l => l.id !== selectedId));
    setSelectedId(null);
  };

  const handleApply = () => {
    const image = imgRef.current;
    if (!image) return;
    setExporting(true);
    const W = image.naturalWidth || image.width;
    const H = image.naturalHeight || image.height;
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    renderTo(c.getContext('2d'), W, H, false);       // full-res, no selection chrome
    c.toBlob(blob => {
      setExporting(false);
      if (blob) onApply(blob);
    }, 'image/jpeg', 0.92);
  };

  const LBL = { fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' };
  const SUB = { fontSize: '11px', fontWeight: 600, color: '#999', margin: '0 0 6px' };
  const chip = (active) => ({ padding: '7px 12px', borderRadius: '8px', border: `1.5px solid ${active ? '#4F46E5' : '#ddd'}`, background: active ? '#EEF2FF' : '#fff', color: active ? '#4F46E5' : '#444', fontSize: '13px', fontWeight: 600, cursor: 'pointer' });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,8,30,0.88)', zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '12px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '460px', margin: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111' }}>🎨 Frame &amp; text</h3>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', fontSize: '16px', cursor: 'pointer', color: '#555' }}>✕</button>
        </div>

        {/* Live preview */}
        <div style={{ background: '#1a1730', padding: '16px 16px 8px', display: 'flex', justifyContent: 'center' }}>
          {imgLoaded ? (
            <canvas ref={canvasRef}
              style={{ width: preview.w + 'px', height: preview.h + 'px', maxWidth: '100%', borderRadius: '6px', touchAction: 'none', boxShadow: '0 6px 20px rgba(0,0,0,0.45)', cursor: textLayers.length ? 'move' : 'default' }}
              onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} />
          ) : (
            <div style={{ color: '#bbb', padding: '70px 0', fontSize: '14px' }}>Loading…</div>
          )}
        </div>
        <p style={{ margin: 0, padding: '0 16px 12px', fontSize: '11px', color: '#9a96b5', textAlign: 'center', background: '#1a1730' }}>
          {textLayers.length ? 'Drag text on the image to reposition' : 'Pick a frame, then add text below'}
        </p>

        {/* Controls */}
        <div style={{ padding: '14px 16px', maxHeight: '42vh', overflowY: 'auto' }}>
          {/* Frame style */}
          <p style={LBL}>Frame style</p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {BORDER_STYLES.map(b => (
              <button key={b.key} onClick={() => setBorderStyle(b.key)} style={chip(borderStyle === b.key)}>{b.label}</button>
            ))}
          </div>

          {/* Themed palette → frame colour */}
          {borderStyle !== 'none' && (
            <>
              <p style={LBL}>Frame colour <span style={{ color: '#bbb', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>· from your photo</span></p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                {palette.map((c, i) => (
                  <button key={i} onClick={() => setBorderColorIdx(i)} title={c}
                    style={{ width: '30px', height: '30px', borderRadius: '8px', background: c, cursor: 'pointer', border: borderColorIdx === i ? '3px solid #4F46E5' : '2px solid #eee' }} />
                ))}
              </div>
            </>
          )}

          {/* Text */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <p style={{ ...LBL, marginBottom: 0 }}>Text</p>
            <button onClick={() => addText(0.5)} style={{ background: '#4F46E5', color: '#fff', border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>+ Add text</button>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {TEXT_PRESETS.map(p => (
              <button key={p.label} onClick={() => addText(p.yFrac)} style={chip(false)}>{p.label}</button>
            ))}
          </div>

          {/* Layer switcher */}
          {textLayers.length > 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
              {textLayers.map(l => (
                <button key={l.id} onClick={() => setSelectedId(l.id)}
                  style={{ ...chip(selectedId === l.id), maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.text || '(empty)'}
                </button>
              ))}
            </div>
          )}

          {/* Selected-layer controls */}
          {selectedLayer && (
            <div style={{ border: '1px solid #eee', borderRadius: '12px', padding: '12px', background: '#fafafa' }}>
              <input value={selectedLayer.text} onChange={e => updateLayer({ text: e.target.value })} placeholder="Type your text"
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '14px', marginBottom: '10px', outline: 'none' }} />

              <p style={SUB}>Font</p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {EDITOR_FONTS.map((f, i) => (
                  <button key={f.label} onClick={() => updateLayer({ fontIdx: i })} style={{ ...chip(selectedLayer.fontIdx === i), fontFamily: f.value }}>{f.label}</button>
                ))}
              </div>

              <p style={SUB}>Size</p>
              <input type="range" min="0.04" max="0.2" step="0.005" value={selectedLayer.sizeFrac}
                onChange={e => updateLayer({ sizeFrac: parseFloat(e.target.value) })}
                style={{ width: '100%', accentColor: '#4F46E5', marginBottom: '10px' }} />

              <p style={SUB}>Alignment</p>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                {[['left', '⬅'], ['center', '⬌'], ['right', '➡']].map(([a, glyph]) => (
                  <button key={a} onClick={() => updateLayer({ align: a })} style={chip(selectedLayer.align === a)}>{glyph}</button>
                ))}
              </div>

              <p style={SUB}>Colour <span style={{ color: '#bbb' }}>· quick-pick from theme</span></p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {['#ffffff', '#000000', ...palette].map((c, i) => (
                  <button key={i} onClick={() => updateLayer({ color: c })} title={c}
                    style={{ width: '26px', height: '26px', borderRadius: '7px', background: c, cursor: 'pointer', border: selectedLayer.color.toLowerCase() === c.toLowerCase() ? '3px solid #4F46E5' : '2px solid #eee' }} />
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#444', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedLayer.shadow} onChange={e => updateLayer({ shadow: e.target.checked })} />
                  Drop shadow
                </label>
                <button onClick={deleteLayer} style={{ background: 'none', border: '1.5px solid #ef4444', color: '#ef4444', borderRadius: '7px', padding: '6px 12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '10px', padding: '14px 16px', borderTop: '1px solid #f0f0f0' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', background: '#fff', color: '#555', border: '1.5px solid #ddd', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleApply} disabled={!imgLoaded || exporting}
            style={{ flex: 2, padding: '12px', background: '#4F46E5', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: imgLoaded ? 'pointer' : 'not-allowed', opacity: imgLoaded && !exporting ? 1 : 0.6 }}>
            {exporting ? 'Saving…' : '✓ Add to post'}
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [splashVisible, setSplashVisible] = useState(true);
  const [splashFading, setSplashFading] = useState(false);
  const [splashPhase, setSplashPhase] = useState('shuttle'); // 'shuttle' | 'solar'
  const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  };
  const [activeTab, setActiveTab] = useState('create');
  const [showLogin, setShowLogin] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [betaCode, setBetaCode] = useState('');
  const [signupStep, setSignupStep] = useState('form'); // 'form' | 'trivia' | 'done'
  const [triviaAnswer, setTriviaAnswer] = useState('');
  const [triviaCorrect, setTriviaCorrect] = useState(false);
  const [betaUsed, setBetaUsed] = useState(0);
  const [betaLimit, setBetaLimit] = useState(null);
  const neuralCanvasRef = useRef(null);
  const stepsCanvasRef = useRef(null);
  const [stepsVisible, setStepsVisible] = useState(false);
  const stepsRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const filterCanvasRefs = useRef([]);
  const [sloganIndex, setSloganIndex] = useState(0);
  const [sloganFade, setSloganFade] = useState(true);

  // Restore session on load and listen for auth changes (handles Google OAuth callback)
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) { setUser(session.user); setAccessToken(session.access_token); loadBetaUsage(session.user); }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAccessToken(session?.access_token ?? null);
      if (session?.user) loadBetaUsage(session.user);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Handle Web Share Target — loads file shared from phone's share sheet
  useEffect(() => {
    if (!window.location.search.includes('shared=1')) return;
    if (!('caches' in window)) return;
    window.history.replaceState({}, '', '/');
    caches.open('blinkapost-share-v1').then(cache =>
      cache.match('/shared-file').then(async response => {
        if (!response) return;
        const blob = await response.blob();
        const name = response.headers.get('X-File-Name') || 'shared-media';
        const file = new File([blob], name, { type: blob.type });
        cache.delete('/shared-file');
        if (file.type.startsWith('video/')) handleVideoSelect(file);
        else handleImageSelect(file);
      })
    );
  }, []); // eslint-disable-line

  // Remove the HTML pre-loader the moment React mounts — cinematic splash covers it
  useEffect(() => {
    const el = document.getElementById('pwa-splash');
    if (el) {
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 260);
    }
  }, []);

  // Splash: launch → orbit → approach → fade out
  useEffect(() => {
    const t1 = setTimeout(() => setSplashPhase('orbit'),    1500);
    const t2 = setTimeout(() => setSplashPhase('approach'), 4100);
    const t3 = setTimeout(() => setSplashFading(true),      5600);
    const t4 = setTimeout(() => setSplashVisible(false),    6300);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, []);

  // Wizard
  const [step, setStep] = useState(1);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [originalImagePreview, setOriginalImagePreview] = useState(null);
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [mediaType, setMediaType] = useState('image');
  const [videoRatio, setVideoRatio] = useState('original');
  const [videoLogoPosition, setVideoLogoPosition] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoTrimStart, setVideoTrimStart] = useState(0);
  const [videoTrimEnd, setVideoTrimEnd] = useState(0);
  const trimVideoRef = useRef(null);
  const [postContext, setPostContext] = useState('');
  // Post goal + conversion-template state (feeds the AI caption prompt)
  const [postGoal, setPostGoal] = useState(null); // null | 'awareness' | 'engagement' | 'conversion'
  const [convFramework, setConvFramework] = useState(null);
  const [convOffer, setConvOffer] = useState('');
  const [convCta, setConvCta] = useState('');
  const [convAudience, setConvAudience] = useState('');
  const [posts, setPosts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isListeningContext, setIsListeningContext] = useState(false);

  // Logos
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [savedLogos, setSavedLogos] = useState([]);
  const [logosLoading, setLogosLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  // History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedPost, setExpandedPost] = useState(null);

  // Brand voice
  const [brandVoice, setBrandVoice] = useState('');
  const [brandVoiceSaved, setBrandVoiceSaved] = useState(false);

  // User settings (stored in brand_settings table)
  const [userLanguage, setUserLanguage]       = useState('English');
  const [aiTone, setAiTone]                   = useState(null);
  const [bizDescription, setBizDescription]   = useState('');
  const [bizGoal, setBizGoal]                 = useState('');
  const [bizAudience, setBizAudience]         = useState('');
  const [postingStyle, setPostingStyle]       = useState('');
  const [postingExamples, setPostingExamples] = useState('');
  const [bizName, setBizName]                 = useState('');
  const [bizIndustry, setBizIndustry]         = useState('');
  const [contentPillars, setContentPillars]   = useState('');
  const [preferredCta, setPreferredCta]       = useState('');
  const [avoidWords, setAvoidWords]           = useState('');
  const [keyProducts, setKeyProducts]         = useState('');
  const [savedHashtags, setSavedHashtags]     = useState('');
  const [suggestedHashtags, setSuggestedHashtags] = useState([]);
  const [hashtagsLoading, setHashtagsLoading] = useState(false);
  const [settingsSaved, setSettingsSaved]     = useState(false);
  // Settings panel UI
  const [settingsTab, setSettingsTab]         = useState('language');
  const [langSearch, setLangSearch]           = useState('');
  // Onboarding language step
  const [showObLanguage, setShowObLanguage]   = useState(false);
  const [obLanguage, setObLanguage]           = useState('English');
  const [obLangSearch, setObLangSearch]       = useState('');

  // Voice setup interview
  const [voiceSetupActive, setVoiceSetupActive]         = useState(false);
  const [voiceSetupPhase, setVoiceSetupPhase]           = useState('settings'); // 'settings' | 'questions'
  const [voiceSetupStep, setVoiceSetupStep]             = useState(0);
  const [voiceSetupTranscript, setVoiceSetupTranscript] = useState('');
  const [voiceSetupListening, setVoiceSetupListening]   = useState(false);
  const [voiceSetupSpeaking, setVoiceSetupSpeaking]     = useState(false);
  const [vsVoiceType, setVsVoiceType]                   = useState('warm');
  const [vsGender, setVsGender]                         = useState('female');
  const [vsSpeed, setVsSpeed]                           = useState('normal');
  const voiceSetupRecRef      = useRef(null);
  const voiceSetupTransRef    = useRef('');
  const voiceSetupAdvancing   = useRef(false);

  // Image editing
  const [imageEditOptions, setImageEditOptions] = useState([]);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [isListeningImageEdit, setIsListeningImageEdit] = useState(false);
  const [editTextInput, setEditTextInput] = useState('');

  // Menu
  const [showMenu, setShowMenu] = useState(false);
  const [menuSection, setMenuSection] = useState(null); // null | 'privacy' | 'about' | 'pro' | 'subscription' | 'admin' | 'settings'

  // Camera
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState('photo');
  const [isRecording, setIsRecording] = useState(false);
  const [cameraFacing, setCameraFacing] = useState('environment');
  const [cameraFilter, setCameraFilter] = useState('none');
  const [cameraRatio, setCameraRatio] = useState('free');

  // Post editing
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  // LinkedIn
  const [linkedinConnected, setLinkedinConnected] = useState(false);
  const [linkedinName, setLinkedinName] = useState('');
  const [linkedinPosting, setLinkedinPosting] = useState(false);
  const [postedPlatforms, setPostedPlatforms] = useState({});
  const [failedPlatforms, setFailedPlatforms] = useState({});
  const [postAllResults, setPostAllResults] = useState(null); // { LinkedIn: 'success'|'failed', ... }

  // Facebook + Instagram
  const [facebookConnected, setFacebookConnected] = useState(false);
  const [facebookPageName, setFacebookPageName] = useState('');
  const [igConnected, setIgConnected] = useState(false);
  const [igUsername, setIgUsername] = useState('');
  const [facebookPosting, setFacebookPosting] = useState(false);
  const [instagramPosting, setInstagramPosting] = useState(false);
  const [fbPostType, setFbPostType] = useState('post');   // 'post' | 'story'
  const [igPostType, setIgPostType] = useState('post');   // 'post' | 'story' | 'reel'

  // Google Business
  const [gbConnected, setGbConnected] = useState(false);
  const [gbLocations, setGbLocations] = useState([]);
  const [gbSelectedLocation, setGbSelectedLocation] = useState(null);
  const [gbPosting, setGbPosting] = useState(false);

  // TikTok
  const [tiktokConnected, setTiktokConnected] = useState(false);
  const [tiktokDisplayName, setTiktokDisplayName] = useState('');
  const [tiktokPosting, setTiktokPosting] = useState(false);
  const [showTikTokConsent, setShowTikTokConsent] = useState(false);
  const [tiktokConsentFrom, setTiktokConsentFrom] = useState('app');
  const [postingAll, setPostingAll] = useState(false);
  const [postingMsgIdx, setPostingMsgIdx] = useState(0);
  const MISSION_GEN_MSGS = [
    '🛰 Uploading to the mothership...',
    '☄️ Dodging space debris...',
    '🌌 Consulting the cosmos...',
    '👾 Bribing the AI overlords...',
    '🚀 Warming up the thrusters...',
    '🌙 Asking the moon for captions...',
    '🛸 Intercepting alien engagement data...',
    '⭐ Calibrating the post cannons...',
    '🪐 Looping around Saturn for inspiration...',
    '🔭 Scanning for viral content signals...',
  ];
  const MISSION_LAUNCH_MSGS = [
    '🚀 Initiating launch sequence...',
    '🔧 Running pre-flight checks...',
    '⛽ Fuelling the rocket...',
    '📡 Establishing satellite link...',
    '🧑‍🚀 Astronaut strapped in, visor down...',
    '🌍 Calculating orbital trajectory...',
    '🔩 Tightening the last bolt...',
    '🛸 AI reading your payload...',
    '✍️ Writing captions for all platforms...',
    '📋 Mission Control confirms go for launch...',
    '🌡️ All systems nominal...',
    '⏳ T-minus 10... 9... 8...',
    '🔥 Engines at full thrust...',
    '💨 Clearing the launch tower...',
    '🌌 Entering orbit...',
    '📬 Scheduling posts across the galaxy...',
    '🛰️ Satellites locked and loaded...',
    '✅ Mission is in orbit. Standing by...',
  ];
  const [missionMsgIdx, setMissionMsgIdx] = useState(0);

  const POSTING_MSGS = [
    '🚀 Launching into the feed...',
    '📡 Beaming your post to the internet...',
    '🐢 Faster than dial-up, we promise...',
    '☕ Grab a coffee, almost there...',
    '🤞 Asking the algorithm nicely...',
    '✨ Sprinkling some engagement dust...',
    '📬 Your post is putting on its shoes...',
    '🧘 The servers are meditating...',
    '🎯 Taking aim at the timeline...',
    '🌍 Reaching all 8 billion people... ok maybe just your followers...',
  ];
  const [showRegenerateMenu, setShowRegenerateMenu] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0); // 0=welcome, 1-4=platforms, 5=done
  const [tutorialSlide, setTutorialSlide] = useState(0); // 0-3 for the 4 tutorial slides
  const [showMissionWalkthrough, setShowMissionWalkthrough] = useState(false);
  const [missionWalkthroughSlide, setMissionWalkthroughSlide] = useState(0);
  const [showTermsConsent, setShowTermsConsent] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [onboardingJustConnected, setOnboardingJustConnected] = useState(false);
  const [connectConfirm, setConnectConfirm] = useState(null);

  // Multi-account support
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [renamingAccountId, setRenamingAccountId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState({});

  // Scheduling
  const [schedulingPlatform, setSchedulingPlatform] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d; });
  const [calendarSelectedDay, setCalendarSelectedDay] = useState(null);
  const [editingScheduledId, setEditingScheduledId] = useState(null);
  const [editingScheduledText, setEditingScheduledText] = useState('');

  // Mission Control
  const [missions, setMissions] = useState([]);
  const [missionLoading, setMissionLoading] = useState(false);
  const [missionLaunching, setMissionLaunching] = useState(false);
  const [missionName, setMissionName] = useState('');
  const [missionMedia, setMissionMedia] = useState([]);
  const [missionPlatforms, setMissionPlatforms] = useState([]);
  const [missionAccountIds, setMissionAccountIds] = useState({}); // platformKey -> chosen account id
  const [missionPostsPerDay, setMissionPostsPerDay] = useState(1);
  const [missionStartDate, setMissionStartDate] = useState('');
  const [missionLaunchTime, setMissionLaunchTime] = useState('09:00');
  const [missionTimezone, setMissionTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [missionStep, setMissionStep] = useState('dashboard'); // dashboard | create
  const [expandedMission, setExpandedMission] = useState(null);
  const [abortConfirmId, setAbortConfirmId] = useState(null);
  const [missionLogoEnabled, setMissionLogoEnabled] = useState(false);
  const [missionLogoSource, setMissionLogoSource] = useState(null); // { url, name } — from saved or new upload
  const [missionLogoPosition, setMissionLogoPosition] = useState('bottom-right');
  const [missionLogoUploading, setMissionLogoUploading] = useState(false);
  const [missionBatchFilter, setMissionBatchFilter] = useState('none');
  const [missionGeneratedPosts, setMissionGeneratedPosts] = useState([]);
  const [missionGenerating, setMissionGenerating] = useState(false);
  const [missionGenMsgIdx, setMissionGenMsgIdx] = useState(0);
  const [missionTimingMode, setMissionTimingMode] = useState('same'); // 'same' | 'individual'
  const [missionPlatformTimes, setMissionPlatformTimes] = useState({});

  // Global voice commands (header mic)
  const [isListening, setIsListening] = useState(false);
  const [voiceToast, setVoiceToast] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const recognitionRef = useRef(null);
  const contextRecognitionRef = useRef(null);
  const contextTranscriptRef = useRef('');
  const [isListeningMission, setIsListeningMission] = useState(false);
  const missionRecognitionRef = useRef(null);
  const missionTranscriptRef = useRef('');
  const [missionContext, setMissionContext] = useState('');
  const [isListeningMissionContext, setIsListeningMissionContext] = useState(false);
  const missionContextRecognitionRef = useRef(null);
  const missionContextTranscriptRef = useRef('');

  useEffect(() => {
    if (!user) return;

    loadSavedLogos(); loadHistory(); loadUserSettings(); loadScheduledPosts(); loadMissions();
    loadConnectedAccounts();

    // Handle OAuth result — works for both popup (postMessage) and legacy redirect (URL params)
    const handleOAuthResult = (data) => {
      if (data.linkedin === 'connected') {
        showToast(`LinkedIn connected${data.name ? ` as ${data.name}` : ''}!`);
        checkLinkedinStatus(); loadConnectedAccounts();
      } else if (data.facebook === 'connected') {
        const pages = parseInt(data.pages || '1', 10);
        showToast(pages > 1 ? `Facebook connected — ${pages} pages found!` : `Facebook connected${data.name ? ` as "${data.name}"` : ''}!`);
        checkFacebookStatus(); loadConnectedAccounts();
      } else if (data.instagram === 'connected') {
        showToast(`Instagram connected${data.igname ? ` as @${data.igname}` : ''}!`);
        checkInstagramStatus(); loadConnectedAccounts();
      } else if (data.tiktok === 'connected') {
        showToast(`TikTok connected${data.name ? ` as ${data.name}` : ''}!`);
        checkTikTokStatus(); loadConnectedAccounts();
      } else if (data.google_business === 'connected') {
        showToast('Google Business connected!');
        checkGBStatus(); loadConnectedAccounts();
      } else {
        const platform = data.linkedin ? 'LinkedIn' : data.facebook ? 'Facebook' : data.instagram ? 'Instagram' : data.google_business ? 'Google Business' : 'TikTok';
        const reason = data.reason;
        showToast(
          reason === 'no_pages' ? 'No Facebook Pages found. Create a Page first.' :
          reason === 'no_ig' ? 'No Instagram Business account found linked to your Facebook pages.' :
          reason === 'personal_account' ? 'Instagram personal accounts can\'t post via API. Switch your Instagram to a Business or Creator account in the Instagram app, then reconnect.' :
          `${platform} connection failed. Please try again.`
        );
      }
    };

    // Primary: localStorage storage event — immune to Meta/Instagram COOP headers that break window.opener
    const onStorageOAuth = (e) => {
      if (e.key !== 'blinkapost_oauth_result' || !e.newValue) return;
      try {
        const data = JSON.parse(e.newValue);
        handleOAuthResult(data);
        localStorage.removeItem('blinkapost_oauth_result');
      } catch {}
    };
    window.addEventListener('storage', onStorageOAuth);

    // Also clear any stale result left in storage from a previous session
    const staleResult = localStorage.getItem('blinkapost_oauth_result');
    if (staleResult) { try { localStorage.removeItem('blinkapost_oauth_result'); } catch {} }

    // Secondary: postMessage — works for non-Meta OAuth flows where opener survives
    const onOAuthMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'blinkapost_oauth') return;
      handleOAuthResult(event.data.data || {});
    };
    window.addEventListener('message', onOAuthMessage);

    // Tertiary: URL params — fallback for any browser that blocks popups entirely
    const params = new URLSearchParams(window.location.search);
    const oauthParam = params.get('linkedin') || params.get('facebook') || params.get('instagram') || params.get('tiktok') || params.get('google_business');
    if (oauthParam) {
      const legacyData = {};
      params.forEach((v, k) => { legacyData[k] = v; });
      handleOAuthResult(legacyData);
      window.history.replaceState({}, '', '/');
    } else {
      checkLinkedinStatus(); checkFacebookStatus(); checkInstagramStatus(); checkTikTokStatus(); checkGBStatus();
    }

    return () => {
      window.removeEventListener('storage', onStorageOAuth);
      window.removeEventListener('message', onOAuthMessage);
    };
  }, [user]); // eslint-disable-line

  const checkLinkedinStatus = async () => {
    if (!user) return;
    try {
      const res = await fetch(`https://blinkapost.onrender.com/auth/linkedin/status?userId=${user.id}`);
      const data = await res.json();
      setLinkedinConnected(data.connected);
      setLinkedinName(data.name || '');
    } catch {}
  };

  const openOAuthPopup = (url) => {
    const w = 600, h = 700;
    const left = Math.max(0, (window.screen.width - w) / 2);
    const top = Math.max(0, (window.screen.height - h) / 2);
    window.open(url, 'blinkapost_oauth', `width=${w},height=${h},top=${top},left=${left},scrollbars=yes,resizable=yes`);
  };

  const connectLinkedin = (from = 'app') => {
    if (from === 'onboarding') { localStorage.setItem('returnToOnboarding', 'true'); localStorage.setItem('onboardingStep', String(onboardingStep)); }
    openOAuthPopup(`https://blinkapost.onrender.com/auth/linkedin?userId=${user.id}`);
  };

  const disconnectLinkedin = async () => {
    await fetch(`https://blinkapost.onrender.com/auth/linkedin/disconnect?userId=${user.id}`, { method: 'DELETE' });
    setLinkedinConnected(false);
    setLinkedinName('');
    showToast('LinkedIn disconnected.');
  };

  const postToLinkedin = async (content) => {
    setLinkedinPosting(true);
    try {
      const imageUrl = image ? await uploadImageForMeta() : null;
      const res = await fetch('https://blinkapost.onrender.com/post/linkedin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, content, imageUrl, accountId: getSelectedAccount('linkedin')?.id })
      });
      const data = await res.json();
      if (data.success) { showToast('Posted to LinkedIn!'); setPostedPlatforms(p => ({ ...p, LinkedIn: true })); setFailedPlatforms(p => { const n = { ...p }; delete n.LinkedIn; return n; }); }
      else { showToast('Failed to post. Please try again.'); setFailedPlatforms(p => ({ ...p, LinkedIn: true })); }
    } catch {
      showToast('Failed to post. Please try again.'); setFailedPlatforms(p => ({ ...p, LinkedIn: true }));
    } finally {
      setLinkedinPosting(false);
    }
  };

  const checkFacebookStatus = async () => {
    if (!user) return;
    try {
      const res = await fetch(`https://blinkapost.onrender.com/auth/facebook/status?userId=${user.id}`);
      const data = await res.json();
      setFacebookConnected(data.connected);
      setFacebookPageName(data.pageName || '');
    } catch {}
  };

  const connectFacebook = (from = 'app') => {
    if (from === 'onboarding') { localStorage.setItem('returnToOnboarding', 'true'); localStorage.setItem('onboardingStep', String(onboardingStep)); }
    openOAuthPopup(`https://blinkapost.onrender.com/auth/facebook?userId=${user.id}`);
  };

  const disconnectFacebook = async () => {
    await fetch(`https://blinkapost.onrender.com/auth/facebook/disconnect?userId=${user.id}`, { method: 'DELETE' });
    setFacebookConnected(false); setFacebookPageName('');
    showToast('Facebook disconnected.');
  };

  const checkGBStatus = async () => {
    if (!user) return;
    try {
      const res = await fetch(`https://blinkapost.onrender.com/auth/google-business/status?userId=${user.id}`);
      const data = await res.json();
      setGbConnected(data.connected);
      setGbLocations(data.locations || []);
      if (data.locations?.length > 0) setGbSelectedLocation(data.locations[0]);
    } catch {}
  };

  const loadGBLocations = async () => {
    showToast('Loading locations...');
    try {
      const res = await fetch(`https://blinkapost.onrender.com/auth/google-business/locations?userId=${user.id}`);
      const data = await res.json();
      if (data.locations?.length > 0) {
        setGbLocations(data.locations);
        setGbSelectedLocation(data.locations[0]);
        showToast('Location loaded!');
      } else {
        showToast('No locations found. Make sure your Google Business profile is verified.');
      }
    } catch { showToast('Failed to load locations.'); }
  };

  const connectGB = (from = 'app') => {
    if (from === 'onboarding') { localStorage.setItem('returnToOnboarding', 'true'); localStorage.setItem('onboardingStep', String(onboardingStep)); }
    openOAuthPopup(`https://blinkapost.onrender.com/auth/google-business?userId=${user.id}`);
  };

  const disconnectGB = async () => {
    await fetch(`https://blinkapost.onrender.com/auth/google-business/disconnect?userId=${user.id}`, { method: 'DELETE' });
    setGbConnected(false); setGbLocations([]); setGbSelectedLocation(null);
    showToast('Google Business disconnected.');
  };

  const postToGoogleBusiness = async (content) => {
    if (!gbConnected || !gbSelectedLocation) { showToast('Connect a Google Business location first.'); return; }
    setGbPosting(true);
    try {
      const imageUrl = image ? await uploadImageForMeta() : null;
      const res = await fetch('https://blinkapost.onrender.com/post/google-business', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, content, imageUrl, locationId: gbSelectedLocation.page_id }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Posted to Google Business!');
        setPostedPlatforms(p => ({ ...p, 'Google Business': true }));
        setFailedPlatforms(p => { const n = { ...p }; delete n['Google Business']; return n; });
      } else {
        showToast(data.error || 'Failed to post to Google Business.');
        setFailedPlatforms(p => ({ ...p, 'Google Business': true }));
      }
    } catch { showToast('Failed to post to Google Business.'); setFailedPlatforms(p => ({ ...p, 'Google Business': true })); }
    finally { setGbPosting(false); }
  };

  const checkInstagramStatus = async () => {
    if (!user) return;
    try {
      const res = await fetch(`https://blinkapost.onrender.com/auth/instagram/status?userId=${user.id}`);
      const data = await res.json();
      setIgConnected(data.connected);
      setIgUsername(data.igUsername || '');
    } catch {}
  };

  const connectInstagram = (from = 'app') => {
    if (from === 'onboarding') { localStorage.setItem('returnToOnboarding', 'true'); localStorage.setItem('onboardingStep', String(onboardingStep)); }
    openOAuthPopup(`https://blinkapost.onrender.com/auth/instagram?userId=${user.id}`);
  };

  const disconnectInstagram = async () => {
    await fetch(`https://blinkapost.onrender.com/auth/instagram/disconnect?userId=${user.id}`, { method: 'DELETE' });
    setIgConnected(false); setIgUsername('');
    showToast('Instagram disconnected.');
  };

  const checkTikTokStatus = async () => {
    if (!user) return;
    try {
      const res = await fetch(`https://blinkapost.onrender.com/auth/tiktok/status?userId=${user.id}`);
      const data = await res.json();
      setTiktokConnected(data.connected);
      setTiktokDisplayName(data.displayName || '');
    } catch {}
  };

  const connectTikTok = (from = 'app') => {
    setTiktokConsentFrom(from);
    setShowTikTokConsent(true);
  };

  const doConnectTikTok = () => {
    const from = tiktokConsentFrom;
    setShowTikTokConsent(false);
    if (from === 'onboarding') { localStorage.setItem('returnToOnboarding', 'true'); localStorage.setItem('onboardingStep', String(onboardingStep)); }
    openOAuthPopup(`https://blinkapost.onrender.com/auth/tiktok?userId=${user.id}`);
  };

  const disconnectTikTok = async () => {
    await fetch(`https://blinkapost.onrender.com/auth/tiktok/disconnect?userId=${user.id}`, { method: 'DELETE' });
    setTiktokConnected(false); setTiktokDisplayName('');
    showToast('TikTok disconnected.');
  };

  const getAccountsForPlatform = (platform) => connectedAccounts.filter(a => a.platform === platform);
  const getSelectedAccount = (platform) => {
    const id = selectedAccountIds[platform];
    if (id) return connectedAccounts.find(a => a.id === id);
    return connectedAccounts.find(a => a.platform === platform);
  };

  const loadConnectedAccounts = async () => {
    try {
      const res = await fetch(`https://blinkapost.onrender.com/accounts?userId=${user.id}`);
      const data = await res.json();
      if (!Array.isArray(data)) return;
      setConnectedAccounts(data);
      setSelectedAccountIds(prev => {
        const next = { ...prev };
        ['linkedin', 'facebook', 'instagram', 'tiktok'].forEach(p => {
          const accs = data.filter(a => a.platform === p);
          if (accs.length > 0 && !next[p]) next[p] = accs[0].id;
        });
        return next;
      });
      // Keep legacy connected state in sync
      const li = data.filter(a => a.platform === 'linkedin');
      if (li.length > 0) { setLinkedinConnected(true); setLinkedinName(li[0].page_name); }
      const fb = data.filter(a => a.platform === 'facebook');
      if (fb.length > 0) { setFacebookConnected(true); setFacebookPageName(fb[0].page_name); }
      const ig = data.filter(a => a.platform === 'instagram');
      if (ig.length > 0) { setIgConnected(true); setIgUsername(ig[0].page_name); }
      const tt = data.filter(a => a.platform === 'tiktok');
      if (tt.length > 0) { setTiktokConnected(true); setTiktokDisplayName(tt[0].page_name); }

      // Onboarding: show if returning from platform OAuth during onboarding, or if no accounts yet
      const returningToOnboarding = localStorage.getItem('returnToOnboarding');
      if (returningToOnboarding) {
        localStorage.removeItem('returnToOnboarding');
        const savedStep = parseInt(localStorage.getItem('onboardingStep') || '1', 10);
        setOnboardingStep(savedStep);
        setOnboardingJustConnected(true);
        setShowOnboarding(true);
      } else {
        const onboardingDone = localStorage.getItem(`blinkapost_onboarded_${user.id}`);
        if (!onboardingDone) {
          const langDone = localStorage.getItem(`blinkapost_lang_${user.id}`);
          if (!langDone) setShowObLanguage(true);
          setShowOnboarding(true);
        }
      }
    } catch {}
  };

  const disconnectAccount = async (accountId, platform) => {
    if (!accountId) return;
    try {
      await fetch(`https://blinkapost.onrender.com/accounts/${accountId}?userId=${user.id}`, { method: 'DELETE' });
    } catch {}
    const remaining = connectedAccounts.filter(a => a.id !== accountId && a.platform === platform);
    setConnectedAccounts(prev => prev.filter(a => a.id !== accountId));
    setSelectedAccountIds(prev => prev[platform] === accountId ? { ...prev, [platform]: remaining[0]?.id } : prev);
    if (remaining.length === 0) {
      if (platform === 'linkedin') { setLinkedinConnected(false); setLinkedinName(''); }
      else if (platform === 'facebook') { setFacebookConnected(false); setFacebookPageName(''); }
      else if (platform === 'instagram') { setIgConnected(false); setIgUsername(''); }
      else if (platform === 'tiktok') { setTiktokConnected(false); setTiktokDisplayName(''); }
    } else {
      // Update legacy state to reflect the new selection
      const newSel = remaining[0];
      if (platform === 'linkedin') setLinkedinName(newSel.page_name);
      else if (platform === 'facebook') setFacebookPageName(newSel.page_name);
      else if (platform === 'instagram') setIgUsername(newSel.page_name);
      else if (platform === 'tiktok') setTiktokDisplayName(newSel.page_name);
    }
    showToast('Account disconnected.');
  };

  const renameAccount = async (accountId, newName) => {
    if (!newName.trim()) return;
    try {
      await fetch(`https://blinkapost.onrender.com/accounts/${accountId}/rename?userId=${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() })
      });
      setConnectedAccounts(prev => prev.map(a => a.id === accountId ? { ...a, page_name: newName.trim() } : a));
      setRenamingAccountId(null);
      setRenameValue('');
      showToast('Account renamed.');
    } catch { showToast('Failed to rename.'); }
  };

  const postToTikTok = async (content) => {
    if (!image && !video) { showToast('No media found — please upload an image or video first.'); return; }
    setTiktokPosting(true);
    try {
      let imageUrl = null, videoUrl = null;
      if (video) {
        showToast('Uploading video for TikTok...');
        const formData = new FormData();
        formData.append('video', video);
        formData.append('userId', user.id);
        const uploadRes = await fetch('https://blinkapost.onrender.com/upload/video', { method: 'POST', body: formData });
        const uploadData = await uploadRes.json();
        videoUrl = uploadData.url || null;
        if (!videoUrl) { showToast('Video upload failed. Please try again.'); setTiktokPosting(false); return; }
      } else {
        showToast('Posting to TikTok...');
        imageUrl = await uploadImageForMeta();
        if (!imageUrl) { showToast('Image upload failed. Please try again.'); setTiktokPosting(false); return; }
      }
      const res = await fetch('https://blinkapost.onrender.com/post/tiktok', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, content, imageUrl, videoUrl, accountId: getSelectedAccount('tiktok')?.id })
      });
      const data = await res.json();
      if (data.success) { showToast('Sent to TikTok drafts — open TikTok to review & publish!'); setPostedPlatforms(p => ({ ...p, TikTok: true })); setFailedPlatforms(p => { const n = { ...p }; delete n.TikTok; return n; }); }
      else { showToast('Failed to post. Please try again.'); setFailedPlatforms(p => ({ ...p, TikTok: true })); }
    } catch { showToast('Failed to post. Please try again.'); setFailedPlatforms(p => ({ ...p, TikTok: true })); }
    finally { setTiktokPosting(false); }
  };

  const postToAll = async () => {
    if (!posts) return;
    setPostingAll(true);
    const results = [];

    // Upload media once directly to Supabase, reuse URL across all platforms
    let imageUrl = null, videoUrl = null;
    if (video) {
      showToast('Compressing & uploading video...');
      try {
        const formData = new FormData();
        formData.append('video', video);
        formData.append('userId', user.id);
        const uploadRes = await fetch('https://blinkapost.onrender.com/upload/video', { method: 'POST', body: formData });
        const uploadData = await uploadRes.json();
        if (!uploadData.url) throw new Error(uploadData.error || 'Video upload failed');
        videoUrl = uploadData.url;
      } catch (e) {
        showToast('Video upload failed: ' + (e.message || 'Try again'));
        setPostingAll(false);
        return;
      }
    } else if (image) {
      imageUrl = await uploadImageForMeta();
    }

    const platformResults = {};
    const tryPost = async (label, fn) => {
      try { await fn(); results.push(`${label} ✓`); platformResults[label] = 'success'; }
      catch (e) { console.error(`${label} post error:`, e); results.push(`${label} ✗ ${e.message || ''}`); platformResults[label] = 'failed'; }
    };

    if (linkedinConnected) await tryPost('LinkedIn', async () => {
      const res = await fetch('https://blinkapost.onrender.com/post/linkedin', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: user.id, content: posts.linkedin, imageUrl, accountId: getSelectedAccount('linkedin')?.id }) });
      const d = await res.json(); if (!d.success) throw new Error(d.error);
    });
    if (facebookConnected) await tryPost('Facebook', async () => {
      const res = await fetch('https://blinkapost.onrender.com/post/facebook', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: user.id, content: posts.facebook, imageUrl, videoUrl, postType: 'post', accountId: getSelectedAccount('facebook')?.id }) });
      const d = await res.json(); if (!d.success) throw new Error(d.error);
    });
    if (igConnected) await tryPost('Instagram', async () => {
      const postType = videoUrl ? 'video' : 'post';
      const res = await fetch('https://blinkapost.onrender.com/post/instagram', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: user.id, content: posts.instagram, imageUrl, videoUrl, postType, accountId: getSelectedAccount('instagram')?.id }) });
      const d = await res.json(); if (!d.success) throw new Error(d.error);
    });
    if (tiktokConnected) await tryPost('TikTok', async () => {
      const res = await fetch('https://blinkapost.onrender.com/post/tiktok', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: user.id, content: posts.tiktok, videoUrl, accountId: getSelectedAccount('tiktok')?.id }) });
      const d = await res.json(); if (!d.success) throw new Error(d.error);
    });
    if (gbConnected && gbSelectedLocation) await tryPost('Google Business', async () => {
      const res = await fetch('https://blinkapost.onrender.com/post/google-business', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: user.id, content: posts.google_business || posts.facebook, imageUrl, locationId: gbSelectedLocation.page_id }) });
      const d = await res.json(); if (!d.success) throw new Error(d.error);
    });

    if (results.length === 0) showToast('No platforms connected — connect at least one below.');
    setPostAllResults(Object.keys(platformResults).length > 0 ? platformResults : null);
    setPostingAll(false);
  };

  const uploadImageForMeta = async () => {
    if (!image) return null;
    try {
      const formData = new FormData();
      formData.append('image', image);
      formData.append('userId', user.id);
      const res = await fetch('https://blinkapost.onrender.com/upload/image', { method: 'POST', body: formData });
      const data = await res.json();
      return data.url || null;
    } catch { return null; }
  };

  const postToFacebook = async (content) => {
    setFacebookPosting(true);
    try {
      showToast(fbPostType === 'story' ? 'Uploading story...' : video ? 'Uploading video to Facebook...' : 'Posting to Facebook...');
      let imageUrl = null, videoUrl = null;
      if (video) {
        const formData = new FormData();
        formData.append('video', video);
        formData.append('userId', user.id);
        const uploadRes = await fetch('https://blinkapost.onrender.com/upload/video', { method: 'POST', body: formData });
        const uploadData = await uploadRes.json();
        videoUrl = uploadData.url || null;
      } else if (image) {
        imageUrl = await uploadImageForMeta();
      }
      const res = await fetch('https://blinkapost.onrender.com/post/facebook', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, content, imageUrl, videoUrl, postType: fbPostType, accountId: getSelectedAccount('facebook')?.id })
      });
      const data = await res.json();
      if (data.success) { showToast(fbPostType === 'story' ? 'Facebook Story posted!' : video ? 'Video posted to Facebook!' : 'Posted to Facebook!'); setPostedPlatforms(p => ({ ...p, Facebook: true })); setFailedPlatforms(p => { const n = { ...p }; delete n.Facebook; return n; }); }
      else { showToast('Failed to post. Please try again.'); setFailedPlatforms(p => ({ ...p, Facebook: true })); }
    } catch { showToast('Failed to post. Please try again.'); setFailedPlatforms(p => ({ ...p, Facebook: true })); }
    finally { setFacebookPosting(false); }
  };

  const postToInstagram = async (content) => {
    if (!image && igPostType !== 'reel' && igPostType !== 'video') { showToast('No image found — please upload an image first.'); return; }
    if ((igPostType === 'reel' || igPostType === 'video') && !video) { showToast('No video found — please upload a video first.'); return; }
    setInstagramPosting(true);
    try {
      let imageUrl = null, videoUrl = null;
      if (igPostType === 'reel' || igPostType === 'video') {
        showToast(`Uploading video for ${igPostType === 'reel' ? 'Reel' : 'Video post'}...`);
        const formData = new FormData();
        formData.append('video', video);
        formData.append('userId', user.id);
        const uploadRes = await fetch('https://blinkapost.onrender.com/upload/video', { method: 'POST', body: formData });
        const uploadData = await uploadRes.json();
        videoUrl = uploadData.url || null;
        if (!videoUrl) { showToast('Video upload failed. Please try again.'); setInstagramPosting(false); return; }
      } else {
        showToast(igPostType === 'story' ? 'Uploading story...' : 'Posting to Instagram...');
        imageUrl = await uploadImageForMeta();
        if (!imageUrl) { showToast('Image upload failed. Please try again.'); setInstagramPosting(false); return; }
      }
      const res = await fetch('https://blinkapost.onrender.com/post/instagram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, content, imageUrl, videoUrl, postType: igPostType, accountId: getSelectedAccount('instagram')?.id })
      });
      const data = await res.json();
      if (data.success) { showToast(igPostType === 'reel' ? 'Reel submitted! Instagram is processing — check your profile in 2–5 mins.' : igPostType === 'video' ? 'Video submitted! Check your Instagram profile in 2–5 mins.' : igPostType === 'story' ? 'Instagram Story posted!' : 'Posted to Instagram!'); setPostedPlatforms(p => ({ ...p, Instagram: true })); setFailedPlatforms(p => { const n = { ...p }; delete n.Instagram; return n; }); }
      else { console.error('Instagram post failed:', data); showToast(data.error || 'Failed to post to Instagram.'); setFailedPlatforms(p => ({ ...p, Instagram: true })); }
    } catch (e) { console.error('Instagram post error:', e); showToast('Failed to post. Please try again.'); setFailedPlatforms(p => ({ ...p, Instagram: true })); }
    finally { setInstagramPosting(false); }
  };

  // ── Data loading ──────────────────────────────────
  const loadSavedLogos = async () => {
    setLogosLoading(true);
    try {
      const { data, error } = await supabase.storage.from('logos').list(user.id, { sortBy: { column: 'created_at', order: 'desc' } });
      if (error) throw error;
      setSavedLogos((data || []).filter(f => !f.name.startsWith('.')).map(f => ({
        name: f.name,
        url: supabase.storage.from('logos').getPublicUrl(`${user.id}/${f.name}`).data.publicUrl
      })));
    } catch (e) { console.error(e); }
    finally { setLogosLoading(false); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data, error } = await supabase.from('posts').select('*').order('created_at', { ascending: false }).limit(50);
      if (error) throw error;
      setHistory(data || []);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  };

  const loadUserSettings = async () => {
    const { data } = await supabase.from('brand_settings')
      .select('brand_voice, language, ai_tone, biz_description, biz_goal, biz_audience, posting_style, posting_examples, biz_name, biz_industry, content_pillars, preferred_cta, avoid_words, key_products, saved_hashtags')
      .eq('user_id', user.id).single();
    if (data) {
      if (data.brand_voice)      setBrandVoice(data.brand_voice);
      if (data.language)         setUserLanguage(data.language);
      if (data.ai_tone)          setAiTone(data.ai_tone);
      if (data.biz_description)  setBizDescription(data.biz_description);
      if (data.biz_goal)         setBizGoal(data.biz_goal);
      if (data.biz_audience)     setBizAudience(data.biz_audience);
      if (data.posting_style)    setPostingStyle(data.posting_style);
      if (data.posting_examples) setPostingExamples(data.posting_examples);
      if (data.biz_name)         setBizName(data.biz_name);
      if (data.biz_industry)     setBizIndustry(data.biz_industry);
      if (data.content_pillars)  setContentPillars(data.content_pillars);
      if (data.preferred_cta)    setPreferredCta(data.preferred_cta);
      if (data.avoid_words)      setAvoidWords(data.avoid_words);
      if (data.key_products)     setKeyProducts(data.key_products);
      if (data.saved_hashtags)   setSavedHashtags(data.saved_hashtags);
    }
  };

  const saveUserSettings = async (partial = {}) => {
    const payload = {
      user_id:          user.id,
      brand_voice:      brandVoice,
      language:         userLanguage,
      ai_tone:          aiTone,
      biz_description:  bizDescription,
      biz_goal:         bizGoal,
      biz_audience:     bizAudience,
      posting_style:    postingStyle,
      posting_examples: postingExamples,
      biz_name:         bizName,
      biz_industry:     bizIndustry,
      content_pillars:  contentPillars,
      preferred_cta:    preferredCta,
      avoid_words:      avoidWords,
      key_products:     keyProducts,
      saved_hashtags:   savedHashtags,
      updated_at:       new Date().toISOString(),
      ...partial,
    };
    await supabase.from('brand_settings').upsert(payload, { onConflict: 'user_id' });
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2500);
  };

  // Keep legacy saveBrandVoice working (used in existing brand voice UI)
  const saveBrandVoice = async () => {
    await saveUserSettings({ brand_voice: brandVoice });
    setBrandVoiceSaved(true);
    setTimeout(() => setBrandVoiceSaved(false), 2000);
  };

  const saveToHistory = async (postData) => {
    try {
      const { instagram, linkedin, facebook, tiktok, google_business } = postData;
      await supabase.from('posts').insert({ user_id: user.id, instagram, linkedin, facebook, tiktok, google_business });
      loadHistory();
    } catch (e) { console.error('saveToHistory error:', e); }
  };

  const loadScheduledPosts = async () => {
    setScheduleLoading(true);
    try {
      const { data, error } = await supabase
        .from('scheduled_posts')
        .select('*')
        .order('scheduled_at', { ascending: true });
      if (error) throw error;
      setScheduledPosts(data || []);
    } catch (e) { console.error(e); }
    finally { setScheduleLoading(false); }
  };

  const loadMissions = async () => {
    if (!user) return;
    setMissionLoading(true);
    try {
      const res = await fetch(`https://blinkapost.onrender.com/missions?userId=${user.id}`);
      const data = await res.json();
      setMissions(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setMissionLoading(false); }
  };

  const handleMissionMediaAdd = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 20 - missionMedia.length;
    if (remaining <= 0) { showToast('Maximum 20 items per mission reached.'); return; }
    const toAdd = files.slice(0, remaining).map(f => ({ file: f, url: URL.createObjectURL(f), context: '' }));
    setMissionMedia(prev => [...prev, ...toAdd]);
  };

  const handleMissionLaunch = async () => {
    if (!missionStartDate) { showToast('Set a launch date.'); return; }
    if (missionGeneratedPosts.length === 0) { showToast('No transmissions to launch.'); return; }
    setMissionLaunching(true);
    setMissionMsgIdx(0);
    showToast('🚀 Initiating launch sequence...');
    const missionMsgInterval = setInterval(() => setMissionMsgIdx(i => (i + 1) % MISSION_LAUNCH_MSGS.length), 2000);
    try {
      const logoUrl = missionLogoEnabled && missionLogoSource?.url ? missionLogoSource.url : null;
      const filter = missionBatchFilter !== 'none' ? missionBatchFilter : null;
      const preGeneratedPosts = [];
      for (const item of missionGeneratedPosts) {
        let imageUrl = item.imageUrl;
        if (item.file) {
          try {
            const processedFile = await applyMissionProcessing(item.file, filter, logoUrl, missionLogoPosition);
            const formData = new FormData();
            formData.append('image', processedFile);
            formData.append('userId', user.id);
            const uploadRes = await fetch('https://blinkapost.onrender.com/upload/image', { method: 'POST', body: formData });
            const uploadData = await uploadRes.json();
            imageUrl = uploadData.url || null;
          } catch (e) { console.error('Upload failed:', e.message); imageUrl = null; }
        }
        // A blob: URL only exists in this browser tab — the backend and Meta/LinkedIn
        // can't fetch it, so launching with one produces posts that can never send.
        // Abort the launch instead of silently queueing unpostable posts.
        if (!imageUrl || !/^https?:\/\//i.test(imageUrl)) {
          clearInterval(missionMsgInterval);
          setMissionLaunching(false);
          showToast('Image upload failed — please check your connection and launch again.');
          return;
        }
        preGeneratedPosts.push({ imageUrl, generated: item.generated });
      }
      // Resolve the chosen account for each platform (defaults to the first connected one)
      const platformAccounts = {};
      for (const p of missionPlatforms) {
        const key = p.toLowerCase().replace(' ', '_');
        const accs = getAccountsForPlatform(key);
        if (accs.length > 0) platformAccounts[key] = missionAccountIds[key] || accs[0].id;
      }
      // Build per-platform UTC times if individual mode
      let platformTimesUTC = null;
      if (missionTimingMode === 'individual') {
        platformTimesUTC = {};
        for (const p of missionPlatforms) {
          const t = missionPlatformTimes[p] || missionLaunchTime;
          const utcDate = zonedTimeToUtc(missionStartDate, t, missionTimezone);
          platformTimesUTC[p] = `${String(utcDate.getUTCHours()).padStart(2,'0')}:${String(utcDate.getUTCMinutes()).padStart(2,'0')}`;
        }
      }
      const res = await fetch('https://blinkapost.onrender.com/missions/launch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await getToken()}` },
        body: JSON.stringify({
          userId: user.id,
          name: missionName,
          platforms: missionPlatforms,
          postsPerDay: missionPostsPerDay,
          startDate: zonedTimeToUtc(missionStartDate, missionTimingMode === 'same' ? missionLaunchTime : '00:00', missionTimezone).toISOString(),
          launchTime: missionTimingMode === 'same' ? missionLaunchTime : null,
          platformTimes: platformTimesUTC,
          platformAccounts,
          preGeneratedPosts,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const failMsg = data.failedCount > 0 ? ` (${data.failedCount} image${data.failedCount > 1 ? 's' : ''} couldn't be processed)` : '';
        showToast(`🚀 Mission launched! ${data.totalPosts} posts entered orbit.${failMsg}`, data.failedCount > 0 ? 6000 : 3000);
        setMissionStep('dashboard');
        setMissionName(''); setMissionContext(''); setMissionMedia([]); setMissionPlatforms([]); setMissionAccountIds({});
        setMissionStartDate(''); setMissionPostsPerDay(1); setMissionLogoEnabled(false);
        setMissionLogoSource(null); setMissionLogoPosition('bottom-right');
        setMissionBatchFilter('none'); setMissionGeneratedPosts([]); setMissionTimingMode('same');
        setMissionPlatformTimes({});
        loadMissions();
      } else {
        showToast(data.error || 'Launch failed.');
      }
    } catch (e) {
      showToast('Launch failed — check your connection.');
    }
    clearInterval(missionMsgInterval);
    setMissionLaunching(false);
  };

  const applyMissionLogo = (imgFile, logoUrl, position) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      fetch(logoUrl)
        .then(r => r.blob())
        .then(blob => {
          const logoObjectUrl = URL.createObjectURL(blob);
          const logoImg = new Image();
          logoImg.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width; canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const padding = Math.round(img.width * 0.03);
            const logoW = Math.round(img.width * 0.2);
            const logoH = Math.round((logoImg.height / logoImg.width) * logoW);
            let x, y;
            if (position === 'top-right')         { x = img.width - logoW - padding; y = padding; }
            else if (position === 'top-left')     { x = padding; y = padding; }
            else if (position === 'bottom-left')  { x = padding; y = img.height - logoH - padding; }
            else if (position === 'center')       { x = (img.width - logoW) / 2; y = (img.height - logoH) / 2; }
            else                                  { x = img.width - logoW - padding; y = img.height - logoH - padding; } // bottom-right default
            ctx.globalAlpha = 0.85;
            ctx.drawImage(logoImg, x, y, logoW, logoH);
            ctx.globalAlpha = 1;
            URL.revokeObjectURL(logoObjectUrl);
            canvas.toBlob(blob2 => {
              resolve(new File([blob2], imgFile.name, { type: 'image/jpeg' }));
            }, 'image/jpeg', 0.92);
          };
          logoImg.src = logoObjectUrl;
        })
        .catch(() => resolve(imgFile)); // fallback: return original if logo fails
    };
    img.src = URL.createObjectURL(imgFile);
  });

  const applyMissionProcessing = (imgFile, filter, logoUrl, logoPosition) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width; canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (filter && filter !== 'none') ctx.filter = filter;
      ctx.drawImage(img, 0, 0);
      ctx.filter = 'none';
      if (!logoUrl) {
        canvas.toBlob(blob => resolve(new File([blob], imgFile.name, { type: 'image/jpeg' })), 'image/jpeg', 0.92);
        return;
      }
      fetch(logoUrl).then(r => r.blob()).then(blob => {
        const logoObjectUrl = URL.createObjectURL(blob);
        const logoImg = new Image();
        logoImg.onload = () => {
          const padding = Math.round(img.width * 0.03);
          const logoW = Math.round(img.width * 0.2);
          const logoH = Math.round((logoImg.height / logoImg.width) * logoW);
          let x, y;
          if (logoPosition === 'top-right')        { x = img.width - logoW - padding; y = padding; }
          else if (logoPosition === 'top-left')    { x = padding; y = padding; }
          else if (logoPosition === 'bottom-left') { x = padding; y = img.height - logoH - padding; }
          else if (logoPosition === 'center')      { x = (img.width - logoW) / 2; y = (img.height - logoH) / 2; }
          else                                     { x = img.width - logoW - padding; y = img.height - logoH - padding; }
          ctx.globalAlpha = 0.85;
          ctx.drawImage(logoImg, x, y, logoW, logoH);
          ctx.globalAlpha = 1;
          URL.revokeObjectURL(logoObjectUrl);
          canvas.toBlob(blob2 => resolve(new File([blob2], imgFile.name, { type: 'image/jpeg' })), 'image/jpeg', 0.92);
        };
        logoImg.src = logoObjectUrl;
      }).catch(() => resolve(imgFile));
    };
    img.src = URL.createObjectURL(imgFile);
  });

  const generateMissionPosts = async () => {
    setMissionGenerating(true);
    setMissionGenMsgIdx(0);
    const genMsgInterval = setInterval(() => setMissionGenMsgIdx(i => (i + 1) % MISSION_GEN_MSGS.length), 2500);
    const BATCH_SIZE = 3;
    const results = [];
    for (let b = 0; b < missionMedia.length; b += BATCH_SIZE) {
      const batch = missionMedia.slice(b, b + BATCH_SIZE);
      const batchResults = await Promise.all(batch.map(async (item) => {
        try {
          const base64 = await resizeImage(item.file);
          const response = await fetch('https://blinkapost.onrender.com/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await getToken()}` },
            body: JSON.stringify({ imageBase64: base64, postContext: missionContext || '' }),
          });
          const data = await response.json();
          return { imageUrl: item.url, file: item.file, generated: data };
        } catch (e) {
          console.error('Transmission generation failed:', e.message);
          return { imageUrl: item.url, file: item.file, generated: {} };
        }
      }));
      results.push(...batchResults);
    }
    clearInterval(genMsgInterval);
    setMissionGeneratedPosts(results);
    setMissionGenerating(false);
    setMissionStep('preview');
  };

  const handleMissionLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMissionLogoUploading(true);
    try {
      const path = `${user.id}/${Date.now()}-${file.name}`;
      const { error } = await supabase.storage.from('logos').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('logos').getPublicUrl(path);
      setMissionLogoSource({ url: publicUrl, name: file.name });
      loadSavedLogos();
      showToast('Logo uploaded!');
    } catch { showToast('Logo upload failed — try again.'); }
    finally { setMissionLogoUploading(false); }
  };

  const toggleMissionVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast('Voice not supported on this browser.'); return; }
    if (isListeningMission) { missionRecognitionRef.current?.stop(); return; }
    missionTranscriptRef.current = '';
    const recognition = new SR();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = false;
    missionRecognitionRef.current = recognition;
    recognition.onstart = () => setIsListeningMission(true);
    recognition.onend = () => {
      setIsListeningMission(false);
      const transcript = missionTranscriptRef.current.trim();
      if (transcript) setMissionName(prev => prev || transcript);
    };
    recognition.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) missionTranscriptRef.current += e.results[i][0].transcript + ' ';
      }
    };
    recognition.onerror = () => { setIsListeningMission(false); showToast('Could not hear you — try again.'); };
    recognition.start();
  };

  const toggleMissionContextVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast('Voice not supported on this browser.'); return; }
    if (isListeningMissionContext) { missionContextRecognitionRef.current?.stop(); return; }
    missionContextTranscriptRef.current = '';
    const recognition = new SR();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = false;
    missionContextRecognitionRef.current = recognition;
    recognition.onstart = () => setIsListeningMissionContext(true);
    recognition.onend = () => {
      setIsListeningMissionContext(false);
      const transcript = missionContextTranscriptRef.current.trim();
      if (transcript) setMissionContext(prev => prev ? prev + ' ' + transcript : transcript);
    };
    recognition.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) missionContextTranscriptRef.current += e.results[i][0].transcript + ' ';
      }
    };
    recognition.onerror = () => { setIsListeningMissionContext(false); showToast('Could not hear you — try again.'); };
    recognition.start();
  };

  const deleteMission = async (id) => {
    await fetch(`https://blinkapost.onrender.com/missions/${id}?userId=${user.id}`, { method: 'DELETE' });
    setMissions(prev => prev.filter(m => m.id !== id));
    setAbortConfirmId(null);
    showToast('🫡 Roger that! Mission aborted.');
  };

  const handleSchedule = async (platform, content) => {
    if (!scheduleTime) { showToast('Please pick a date and time.'); return; }
    try {
      showToast('Saving scheduled post...');
      let imageUrl = null, videoUrl = null;
      if (video) {
        showToast('Compressing & uploading video...');
        const formData = new FormData();
        formData.append('video', video);
        formData.append('userId', user.id);
        const uploadRes = await fetch('https://blinkapost.onrender.com/upload/video', { method: 'POST', body: formData });
        const uploadData = await uploadRes.json();
        if (!uploadData.url) throw new Error(uploadData.error || 'Video upload failed');
        videoUrl = uploadData.url;
      } else if (image) {
        imageUrl = await uploadImageForMeta();
      }
      const accountId = platform === 'Google Business'
        ? (gbSelectedLocation?.page_id || null)
        : (getSelectedAccount(platform.toLowerCase())?.id || null);
      const postType = platform === 'Instagram' ? igPostType
        : platform === 'Facebook' ? fbPostType
        : null;
      await supabase.from('scheduled_posts').insert({
        user_id: user.id,
        platform,
        content,
        scheduled_at: new Date(scheduleTime).toISOString(),
        status: 'pending',
        image_url: imageUrl,
        video_url: videoUrl,
        account_id: accountId,
        post_type: postType,
      });
      setSchedulingPlatform(null);
      setScheduleTime('');
      showToast(`${platform} post scheduled!`);
      loadScheduledPosts();
    } catch (e) {
      console.error(e);
      showToast('Failed to schedule. Please try again.');
    }
  };

  const deleteScheduledPost = async (id) => {
    await supabase.from('scheduled_posts').delete().eq('id', id);
    setScheduledPosts(prev => prev.filter(p => p.id !== id));
  };

  const [retryingPostId, setRetryingPostId] = useState(null);

  const retryPost = async (post) => {
    setRetryingPostId(post.id);
    try {
      const PLATFORM_ENDPOINTS = { LinkedIn: 'linkedin', Facebook: 'facebook', Instagram: 'instagram', TikTok: 'tiktok', 'Google Business': 'google-business' };
      const endpoint = PLATFORM_ENDPOINTS[post.platform];
      if (!endpoint) { showToast(`Unknown platform: ${post.platform}`); return; }

      const body = { userId: user.id, content: post.content };
      if (post.image_url) body.imageUrl = post.image_url;
      if (post.platform === 'Google Business') {
        if (post.account_id) body.locationId = post.account_id;
      } else {
        if (post.account_id) body.accountId = post.account_id;
      }

      const res = await fetch(`https://blinkapost.onrender.com/post/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        await supabase.from('scheduled_posts').update({ status: 'posted', error_message: null }).eq('id', post.id);
        setScheduledPosts(prev => prev.map(p => p.id === post.id ? { ...p, status: 'posted', error_message: null } : p));
        showToast(`Posted to ${post.platform}!`);
      } else {
        showToast(data.error || `Failed to post to ${post.platform}.`);
      }
    } catch (e) {
      showToast('Something went wrong. Please try again.');
    } finally {
      setRetryingPostId(null);
    }
  };

  const saveScheduledPostEdit = async (id) => {
    await supabase.from('scheduled_posts').update({ content: editingScheduledText }).eq('id', id);
    setScheduledPosts(prev => prev.map(p => p.id === id ? { ...p, content: editingScheduledText } : p));
    setEditingScheduledId(null);
    setEditingScheduledText('');
  };

  const applyCanvasEdit = (drawFn) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      drawFn(canvas, ctx, img);
      canvas.toBlob(blob => {
        const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
        setImage(file);
        setImagePreview(URL.createObjectURL(blob));
        setImageEditOptions([]);
      }, 'image/jpeg', 0.92);
    };
    img.src = imagePreview;
  };

  // Receives the composited blob from the themed template editor and
  // feeds it into the post pipeline, same as the other canvas edits.
  const applyTemplateResult = (blob) => {
    const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
    setImage(file);
    setImagePreview(URL.createObjectURL(blob));
    setImageEditOptions([]);
    setShowTemplateEditor(false);
    showToast('Template applied ✨');
  };

  const applyRotate = (deg) => applyCanvasEdit((canvas, ctx, img) => {
    const rad = (deg * Math.PI) / 180;
    if (deg === 90 || deg === 270) { canvas.width = img.height; canvas.height = img.width; }
    else { canvas.width = img.width; canvas.height = img.height; }
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(rad);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
  });

  const applyFlip = (dir) => applyCanvasEdit((canvas, ctx, img) => {
    canvas.width = img.width; canvas.height = img.height;
    if (dir === 'h') { ctx.scale(-1, 1); ctx.drawImage(img, -img.width, 0); }
    else { ctx.scale(1, -1); ctx.drawImage(img, 0, -img.height); }
  });

  const applyCrop = (ratio) => applyCanvasEdit((canvas, ctx, img) => {
    let sw, sh, sx, sy;
    if (ratio === 'square') {
      sw = sh = Math.min(img.width, img.height);
      sx = (img.width - sw) / 2; sy = (img.height - sh) / 2;
    } else if (ratio === '9:16') {
      sh = img.height; sw = Math.round(sh * 9 / 16);
      if (sw > img.width) { sw = img.width; sh = Math.round(sw * 16 / 9); }
      sx = (img.width - sw) / 2; sy = (img.height - sh) / 2;
    } else { // 16:9
      sw = img.width; sh = Math.round(sw * 9 / 16);
      if (sh > img.height) { sh = img.height; sw = Math.round(sh * 16 / 9); }
      sx = (img.width - sw) / 2; sy = (img.height - sh) / 2;
    }
    canvas.width = sw; canvas.height = sh;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  });

  const applyFilter = (filterStr) => applyCanvasEdit((canvas, ctx, img) => {
    canvas.width = img.width; canvas.height = img.height;
    ctx.filter = filterStr;
    ctx.drawImage(img, 0, 0);
  });

  const applyLogo = (position = 'top-right') => {
    if (!selectedLogo) { showToast('No logo selected — upload a logo first.'); return; }
    const img = new Image();
    img.onload = () => {
      // Fetch logo as blob to avoid canvas CORS taint
      fetch(selectedLogo.url)
        .then(r => r.blob())
        .then(blob => {
          const logoUrl = URL.createObjectURL(blob);
          const logoImg = new Image();
          logoImg.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width; canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const padding = Math.round(img.width * 0.03);
            const logoW = Math.round(img.width * 0.2);
            const logoH = Math.round((logoImg.height / logoImg.width) * logoW);
            let x, y;
            if (position === 'top-right')         { x = img.width - logoW - padding; y = padding; }
            else if (position === 'top-left')     { x = padding; y = padding; }
            else if (position === 'bottom-right') { x = img.width - logoW - padding; y = img.height - logoH - padding; }
            else if (position === 'bottom-left')  { x = padding; y = img.height - logoH - padding; }
            else                                  { x = (img.width - logoW) / 2; y = (img.height - logoH) / 2; }
            ctx.globalAlpha = 0.85;
            ctx.drawImage(logoImg, x, y, logoW, logoH);
            ctx.globalAlpha = 1;
            URL.revokeObjectURL(logoUrl);
            canvas.toBlob(blob2 => {
              const file = new File([blob2], 'image.jpg', { type: 'image/jpeg' });
              setImage(file);
              setImagePreview(URL.createObjectURL(blob2));
              setImageEditOptions([]);
            }, 'image/jpeg', 0.92);
          };
          logoImg.src = logoUrl;
        })
        .catch(() => showToast('Failed to load logo — try again.'));
    };
    img.src = imagePreview;
  };

  const handleImageVoiceCommand = (transcript) => {
    const t = transcript.toLowerCase();
    if (t.includes('rotat') || t.includes('turn')) {
      setImageEditOptions([
        { label: '↻ 90°', action: () => applyRotate(90) },
        { label: '↻ 180°', action: () => applyRotate(180) },
        { label: '↺ 270°', action: () => applyRotate(270) },
      ]);
    } else if (t.includes('flip') || t.includes('mirror')) {
      setImageEditOptions([
        { label: '↔ Flip horizontal', action: () => applyFlip('h') },
        { label: '↕ Flip vertical', action: () => applyFlip('v') },
      ]);
    } else if (t.includes('crop') || t.includes('square') || t.includes('portrait') || t.includes('landscape')) {
      setImageEditOptions([
        { label: '⬛ Square (1:1)', action: () => applyCrop('square') },
        { label: '📱 Portrait (9:16)', action: () => applyCrop('9:16') },
        { label: '🖥 Landscape (16:9)', action: () => applyCrop('16:9') },
      ]);
    } else if (t.includes('bright') || t.includes('lighten') || t.includes('light')) {
      setImageEditOptions([
        { label: '☀️ Brighten a little', action: () => applyFilter('brightness(1.2)') },
        { label: '☀️☀️ Brighten more', action: () => applyFilter('brightness(1.45)') },
        { label: '☀️☀️☀️ Brighten a lot', action: () => applyFilter('brightness(1.7)') },
      ]);
    } else if (t.includes('dark') || t.includes('shadow') || t.includes('dim')) {
      setImageEditOptions([
        { label: '🌑 Darken a little', action: () => applyFilter('brightness(0.8)') },
        { label: '🌑🌑 Darken more', action: () => applyFilter('brightness(0.6)') },
        { label: '🌑🌑🌑 Darken a lot', action: () => applyFilter('brightness(0.4)') },
      ]);
    } else if (t.includes('enhance') || t.includes('improve') || t.includes('boost') || t.includes('pop')) {
      setImageEditOptions([
        { label: '✨ Auto-enhance', action: () => applyFilter('contrast(1.15) saturate(1.25) brightness(1.05)') },
        { label: '🎨 Vivid', action: () => applyFilter('contrast(1.2) saturate(1.6) brightness(1.08)') },
        { label: '📷 Crisp', action: () => applyFilter('contrast(1.3) saturate(1.1)') },
      ]);
    } else if (t.includes('contrast')) {
      setImageEditOptions([
        { label: '◑ More contrast', action: () => applyFilter('contrast(1.4)') },
        { label: '◒ High contrast', action: () => applyFilter('contrast(1.8)') },
        { label: '◐ Less contrast', action: () => applyFilter('contrast(0.75)') },
      ]);
    } else if (t.includes('grey') || t.includes('gray') || t.includes('black') || t.includes('white') || t.includes('mono')) {
      setImageEditOptions([
        { label: '⬜ Black & white', action: () => applyFilter('grayscale(1)') },
        { label: '🌫️ Faded B&W', action: () => applyFilter('grayscale(1) brightness(1.1) contrast(0.85)') },
        { label: '🎞️ High-contrast B&W', action: () => applyFilter('grayscale(1) contrast(1.5)') },
      ]);
    } else if (t.includes('warm') || t.includes('sunset') || t.includes('golden')) {
      setImageEditOptions([
        { label: '🌅 Warm', action: () => applyFilter('sepia(0.35) saturate(1.4) brightness(1.05)') },
        { label: '🔥 Hot', action: () => applyFilter('sepia(0.55) saturate(1.7) brightness(1.1)') },
      ]);
    } else if (t.includes('cool') || t.includes('cold') || t.includes('blue')) {
      setImageEditOptions([
        { label: '❄️ Cool', action: () => applyFilter('hue-rotate(20deg) saturate(0.9) brightness(1.05)') },
        { label: '🌊 Arctic', action: () => applyFilter('hue-rotate(35deg) saturate(0.75) brightness(1.1)') },
      ]);
    } else if (t.includes('fade') || t.includes('matte') || t.includes('vintage')) {
      setImageEditOptions([
        { label: '📷 Vintage', action: () => applyFilter('sepia(0.4) contrast(0.9) brightness(1.1)') },
        { label: '🌫️ Faded', action: () => applyFilter('contrast(0.8) brightness(1.15) saturate(0.7)') },
        { label: '🤎 Matte', action: () => applyFilter('contrast(0.85) saturate(0.8) brightness(1.08)') },
      ]);
    } else {
      setImageEditOptions([
        { label: '↻ Rotate', action: () => setImageEditOptions([
          { label: '↻ 90°', action: () => applyRotate(90) },
          { label: '↻ 180°', action: () => applyRotate(180) },
        ])},
        { label: '↔ Flip', action: () => applyFlip('h') },
        { label: '⬛ Crop', action: () => setImageEditOptions([
          { label: '⬛ Square (1:1)', action: () => applyCrop('square') },
          { label: '📱 Portrait (9:16)', action: () => applyCrop('9:16') },
          { label: '🖥 Landscape (16:9)', action: () => applyCrop('16:9') },
        ])},
        { label: '🏷️ Add Logo', action: () => setImageEditOptions([
          { label: '↗ Top Right', action: () => applyLogo('top-right') },
          { label: '↖ Top Left', action: () => applyLogo('top-left') },
          { label: '↘ Bottom Right', action: () => applyLogo('bottom-right') },
          { label: '↙ Bottom Left', action: () => applyLogo('bottom-left') },
          { label: '⊙ Center', action: () => applyLogo('center') },
        ])},
        { label: '✨ Enhance', action: () => applyFilter('contrast(1.15) saturate(1.25) brightness(1.05)') },
        { label: '☀️ Brighten', action: () => applyFilter('brightness(1.3)') },
        { label: '🌑 Darken', action: () => applyFilter('brightness(0.7)') },
        { label: '⬜ B&W', action: () => applyFilter('grayscale(1)') },
        { label: '🌅 Warm', action: () => applyFilter('sepia(0.35) saturate(1.4)') },
        { label: '❄️ Cool', action: () => applyFilter('hue-rotate(20deg) saturate(0.9)') },
        ...(originalImagePreview ? [{ label: '↩ Reset to original', action: () => { setImage(originalImage); setImagePreview(originalImagePreview); setImageEditOptions([]); } }] : []),
      ]);
    }
  };

  const listenForImageEdit = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast('Voice input is not supported in this browser — please type instead.'); return; }
    const recognition = new SR();
    recognition.lang = 'en-US'; recognition.continuous = false; recognition.interimResults = false;
    recognition.onstart = () => setIsListeningImageEdit(true);
    recognition.onend = () => setIsListeningImageEdit(false);
    recognition.onresult = (e) => handleImageVoiceCommand(e.results[0][0].transcript);
    recognition.onerror = () => { setIsListeningImageEdit(false); showToast('Could not hear you — try again.'); };
    recognition.start();
  };

  const handlePostNow = (platform, content) => {
    navigator.clipboard.writeText(content);
    const urls = {
      Instagram: 'https://www.instagram.com/',
      LinkedIn: 'https://www.linkedin.com/feed/',
      Facebook: 'https://www.facebook.com/',
      TikTok: 'https://www.tiktok.com/upload'
    };
    showToast(`Copied! Opening ${platform}...`);
    setTimeout(() => window.open(urls[platform], '_blank'), 800);
  };

  const deleteFromHistory = async (id) => {
    await supabase.from('posts').delete().eq('id', id);
    setHistory(prev => prev.filter(p => p.id !== id));
    if (expandedPost === id) setExpandedPost(null);
  };

  // ── Logo upload ───────────────────────────────────
  const handleNewLogoUpload = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      const fileName = `${Date.now()}.${ext}`;
      const path = `${user.id}/${fileName}`;
      const { error } = await supabase.storage.from('logos').upload(path, file);
      if (error) throw error;
      const url = supabase.storage.from('logos').getPublicUrl(path).data.publicUrl;
      const newLogo = { name: fileName, url };
      setSavedLogos(prev => [newLogo, ...prev]);
      selectLogo(newLogo);
    } catch (e) { alert('Failed to upload logo. Please try again.'); console.error(e); }
    finally { setLogoUploading(false); }
  };

  const handleDeleteLogo = async (e, logo) => {
    e.stopPropagation();
    const { error } = await supabase.storage.from('logos').remove([`${user.id}/${logo.name}`]);
    if (error) { console.error(error); return; }
    setSavedLogos(prev => prev.filter(l => l.name !== logo.name));
    if (selectedLogo?.name === logo.name) setSelectedLogo(null);
  };

  const selectLogo = (logo) => {
    setSelectedLogo(logo);
  };

  // ── Image selection ───────────────────────────────
  const handleImageSelect = (file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setImage(file);
    setImagePreview(previewUrl);
    setOriginalImage(file);
    setOriginalImagePreview(previewUrl);
    setTimeout(() => setStep(2), 400);
  };

  // ── Generation ────────────────────────────────────
  // Safe JSON parser — gives a clean error when the backend returns non-JSON
  const safeJson = async (res) => {
    const text = await res.text();
    try { return JSON.parse(text); }
    catch { throw new Error('The AI returned an unexpected response — please try again.'); }
  };

  // Bundle user profile fields into a single string for the backend
  const buildUserProfile = () => {
    const parts = [];
    if (bizName)         parts.push(`Business name: ${bizName}`);
    if (bizIndustry)     parts.push(`Industry: ${bizIndustry}`);
    if (bizDescription)  parts.push(`About the business: ${bizDescription}`);
    if (keyProducts)     parts.push(`Key products/services: ${keyProducts}`);
    if (bizGoal)         parts.push(`Goals: ${bizGoal}`);
    if (bizAudience)     parts.push(`Target audience: ${bizAudience}`);
    if (contentPillars)  parts.push(`Content pillars (recurring themes): ${contentPillars}`);
    if (preferredCta)    parts.push(`Preferred call to action: ${preferredCta}`);
    if (avoidWords)      parts.push(`Words/phrases to avoid: ${avoidWords}`);
    if (postingStyle)    parts.push(`Posting style: ${postingStyle}`);
    if (postingExamples) parts.push(`Example captions:\n${postingExamples}`);
    return parts.join('\n\n');
  };

  const suggestHashtags = async () => {
    setHashtagsLoading(true);
    setSuggestedHashtags([]);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'https://blinkapost.onrender.com'}/suggest-hashtags`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'Authorization': `Bearer ${await getToken()}` },
        body: JSON.stringify({ userProfile: buildUserProfile(), savedHashtags }),
      });
      const data = await res.json();
      setSuggestedHashtags(data.hashtags || []);
    } catch {
      showToast('Could not load suggestions — try again.');
    }
    setHashtagsLoading(false);
  };

  const showToast = (msg, duration) => {
    setVoiceToast(msg);
    setTimeout(() => setVoiceToast(''), duration || (msg.toLowerCase().includes('error') || msg.toLowerCase().includes('fail') ? 6000 : 3500));
  };

  // Pull a useful message out of a backend error response (uses the `detail` field
  // the video endpoints now return), so failures aren't a generic "something went wrong".
  const readVideoError = async (res, fallback) => {
    try {
      const j = await res.json();
      if (j?.error === 'beta_limit_reached') return 'beta_limit';
      if (j?.detail) return `${fallback}: ${j.detail}`;
      if (j?.error) return `${fallback}: ${j.error}`;
    } catch { /* non-JSON body */ }
    return `${fallback} (${res.status})`;
  };

  // Trim (if needed) → upload to Supabase → hand the public URL to the backend, which
  // streams it into the Gemini Files API. Returns the Gemini file refs. Shared by
  // generate() and regenerateSingle() so both upload identically.
  const uploadVideoForGemini = async (signal) => {
    let videoToUpload = video;
    const needsTrim = videoDuration > 0 && (videoTrimStart > 0.1 || videoTrimEnd < videoDuration - 0.1);
    if (needsTrim && trimVideoRef.current) {
      showToast('Trimming video...');
      try {
        const trimmed = await trimVideoInBrowser(trimVideoRef.current, videoTrimStart, videoTrimEnd);
        if (trimmed) {
          const ext = trimmed.type.includes('mp4') ? 'mp4' : 'webm';
          videoToUpload = new File([trimmed], `trimmed_${Date.now()}.${ext}`, { type: trimmed.type });
        }
      } catch { /* trim failed — use original */ }
    }

    showToast('Uploading video...');
    const videoExt = videoToUpload.name.split('.').pop() || 'mp4';
    const videoPath = `temp/${user.id}/${Date.now()}.${videoExt}`;
    const { error: storageErr } = await supabase.storage
      .from('videos')
      .upload(videoPath, videoToUpload, { contentType: videoToUpload.type, upsert: true });
    if (storageErr) {
      console.error('Supabase storage error:', storageErr);
      throw new Error("Couldn't upload the video: " + (storageErr.message || storageErr.error || JSON.stringify(storageErr)));
    }
    const videoUrl = supabase.storage.from('videos').getPublicUrl(videoPath).data.publicUrl;
    const cleanup = () => supabase.storage.from('videos').remove([videoPath]).catch(() => {});

    showToast('Sending to AI...');
    // Send the ACTUAL uploaded file's mime + size — a browser-trimmed clip is webm,
    // not the original's type — so Gemini's resumable upload gets a matching header.
    const uploadRes = await fetch('https://blinkapost.onrender.com/generate-video/upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await getToken()}` },
      body: JSON.stringify({ videoUrl, mimeType: videoToUpload.type || 'video/mp4', size: videoToUpload.size }),
      signal,
    });
    if (!uploadRes.ok) { cleanup(); throw new Error(await readVideoError(uploadRes, 'Upload to AI failed')); }
    const json = await uploadRes.json();
    cleanup(); // Gemini has the bytes now — drop the temp Supabase object
    return { fileUri: json.fileUri, fileName: json.fileName, mimeType: json.mimeType };
  };

  const generate = async ({ toneOverride, addHashtags, context } = {}) => {
    const hasMedia = mediaType === 'video' ? !!video : !!image;
    if (!hasMedia) { showToast('Please upload an image or video first.'); return; }
    setStep(4);
    setLoading(true);
    setPosts(null);
    // Show "waking up" hint if server takes > 5s
    const wakeTimer = setTimeout(() => showToast('Waking up server… hang tight!'), 5000);
    // Goal/framework fields that tune the AI caption (only sent when set)
    const goalFields = {
      ...(postGoal && { postGoal }),
      ...(postGoal === 'conversion' && convFramework && { framework: convFramework }),
      ...(postGoal === 'conversion' && convOffer.trim()    && { offer: convOffer.trim() }),
      ...(postGoal === 'conversion' && convCta.trim()      && { cta: convCta.trim() }),
      ...(postGoal === 'conversion' && convAudience.trim() && { audience: convAudience.trim() }),
    };
    try {
      let data;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 150000); // 2.5 min max (covers Render cold start + Gemini)
      if (mediaType === 'video') {
        // Trim → Supabase → Gemini (shared helper, sends correct mime + size)
        const { fileUri, fileName, mimeType } = await uploadVideoForGemini(controller.signal);

        // Poll + generate posts
        showToast('Analysing video with AI...');
        const genController = new AbortController();
        const genTimeout = setTimeout(() => genController.abort(), 150000);
        const genRes = await fetch('https://blinkapost.onrender.com/generate-video/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await getToken()}` },
          body: JSON.stringify({
            fileUri, fileName, mimeType,
            videoRatio: videoRatio || 'original',
            brandVoice, toneOverride,
            addHashtags: addHashtags || false,
            postContext: context ?? postContext ?? '',
            ...goalFields,
            ...(userLanguage !== 'English' && { language: userLanguage }),
            ...(aiTone && { defaultTone: aiTone }),
            ...(buildUserProfile() && { userProfile: buildUserProfile() }),
            ...(savedHashtags && { savedHashtags }),
          }),
          signal: genController.signal
        });
        clearTimeout(genTimeout);
        clearTimeout(timeout);
        if (!genRes.ok) throw new Error(await readVideoError(genRes, "AI couldn't process this video"));
        data = await safeJson(genRes);

      } else {
        const imageBase64 = await resizeImage(image);
        const response = await fetch('https://blinkapost.onrender.com/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await getToken()}` },
          body: JSON.stringify({
            imageBase64, brandVoice, toneOverride, addHashtags,
            postContext: context ?? postContext,
            ...goalFields,
            ...(userLanguage !== 'English' && { language: userLanguage }),
            ...(aiTone && { defaultTone: aiTone }),
            ...(buildUserProfile() && { userProfile: buildUserProfile() }),
            ...(savedHashtags && { savedHashtags }),
          }),
          signal: controller.signal
        });
        clearTimeout(timeout);
        if (response.status === 429) {
          const errData = await response.json();
          if (errData.error === 'beta_limit_reached') throw new Error(`beta_limit`);
        }
        if (!response.ok) throw new Error(`Server error ${response.status}`);
        data = await safeJson(response);
        setBetaUsed(u => u + 1);
      }
      clearTimeout(wakeTimer);
      setPosts(data);
      saveToHistory(data);
    } catch (err) {
      clearTimeout(wakeTimer);
      console.error('Generate error:', err);
      if (err.message === 'beta_limit') {
        showToast(`You've used all ${betaLimit} of your beta launches. Thanks for testing!`);
      } else if (err.name === 'AbortError') {
        showToast('Request timed out — please try again.');
      } else {
        showToast(err.message || 'Something went wrong. Please try again!');
      }
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const regenerateSingle = async (platform) => {
    const key = platform.toLowerCase();
    setShowRegenerateMenu(false);
    setLoading(true);
    const wakeTimer = setTimeout(() => showToast('Waking up server… hang tight!'), 5000);
    try {
      let data;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 180000);
      if (mediaType === 'video') {
        // Re-upload via the shared helper (Gemini files are deleted after each generate)
        const { fileUri, fileName, mimeType } = await uploadVideoForGemini(controller.signal);
        const genRes = await fetch('https://blinkapost.onrender.com/generate-video/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await getToken()}` },
          body: JSON.stringify({
            fileUri, fileName, mimeType, videoRatio: videoRatio || 'original', brandVoice,
            postContext: postContext ?? '',
            ...(userLanguage !== 'English' && { language: userLanguage }),
            ...(aiTone && { defaultTone: aiTone }),
            ...(buildUserProfile() && { userProfile: buildUserProfile() }),
          }),
          signal: controller.signal
        });
        clearTimeout(timeout);
        if (!genRes.ok) throw new Error(await readVideoError(genRes, "AI couldn't process this video"));
        data = await safeJson(genRes);
      } else {
        const imageBase64 = await resizeImage(image);
        const response = await fetch('https://blinkapost.onrender.com/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await getToken()}` },
          body: JSON.stringify({
            imageBase64, brandVoice, postContext,
            ...(userLanguage !== 'English' && { language: userLanguage }),
            ...(aiTone && { defaultTone: aiTone }),
            ...(buildUserProfile() && { userProfile: buildUserProfile() }),
          }),
          signal: controller.signal
        });
        clearTimeout(timeout);
        if (!response.ok) throw new Error(`Server error ${response.status}`);
        data = await safeJson(response);
      }
      clearTimeout(wakeTimer);
      setPosts(prev => ({ ...prev, [key]: data[key] }));
      showToast(`${platform} post regenerated!`);
    } catch (err) {
      clearTimeout(wakeTimer);
      if (err.message === 'beta_limit') showToast(`You've used all ${betaLimit} of your beta launches. Thanks for testing!`);
      else showToast(err.name === 'AbortError' ? 'Request timed out — please try again.' : (err.message || 'Something went wrong. Please try again!'));
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3 voice input ────────────────────────────
  const listenForContext = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast('Voice input is not supported in this browser — please type instead.'); return; }
    if (isListeningContext) {
      contextRecognitionRef.current?.stop();
      return;
    }
    contextTranscriptRef.current = '';
    const recognition = new SR();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = false;
    contextRecognitionRef.current = recognition;
    recognition.onstart = () => setIsListeningContext(true);
    recognition.onend = () => {
      setIsListeningContext(false);
      const transcript = contextTranscriptRef.current.trim();
      if (transcript) { setPostContext(transcript); generate({ context: transcript }); }
    };
    recognition.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) contextTranscriptRef.current += e.results[i][0].transcript + ' ';
      }
    };
    recognition.onerror = () => { setIsListeningContext(false); showToast('Could not hear you — try again.'); };
    recognition.start();
  };

  // ── Voice setup interview ─────────────────────────
  const VOICE_SETUP_QUESTIONS = [
    {
      label: 'Your Business',
      setter: setBizDescription,
      getter: () => bizDescription,
      question: "Okay, let's kick things off! Tell me about your business — what do you do, what are you selling, and honestly, what makes you different from everyone else out there?",
    },
    {
      label: 'Your Goals',
      setter: setBizGoal,
      getter: () => bizGoal,
      question: "Love it! So what are you actually trying to get out of social media? Like, is it sales, getting your name out there, building a community — or honestly, all of the above?",
    },
    {
      label: 'Your Audience',
      setter: setBizAudience,
      getter: () => bizAudience,
      question: "Okay, cool. Who are you talking to? Paint me a picture of the kind of person who'd absolutely love what you do.",
    },
    {
      label: 'Your Style',
      setter: setPostingStyle,
      getter: () => postingStyle,
      question: "Alright, this one's fun — how do you like to write? Are you straight to the point, do you love a good story, maybe you throw in a bit of humour? No wrong answers here.",
    },
    {
      label: 'Your Voice',
      setter: setPostingExamples,
      getter: () => postingExamples,
      question: "Last one, I promise! Give me an example of a caption you love — something you've written before, or something that made you think, I wish I'd written that. This one's the secret ingredient.",
    },
  ];

  const VS_ACKS = [
    null,
    "Yes! I love that — I'm already getting such a good feel for what you're about.",
    "Those are brilliant goals. Honestly, having that clarity makes such a difference to how I write for you.",
    "Oh I love that — knowing your audience that well is a seriously underrated superpower. Most people skip this.",
    "Ha, yes! That tells me everything about the vibe you're going for. I'm here for it.",
  ];

  const VS_DONE_LINE = "Okay, that is everything I needed! Honestly, I feel like I actually know you now. Your posts are gonna sound so much more like you, and way less like some robot wrote them. Saving your profile now...";

  const VS_SPEED = { slow: 0.78, normal: 0.9, fast: 1.1 };

  const vsPickVoice = (type, gender) => {
    const voices = window.speechSynthesis.getVoices();
    const isFemale = gender === 'female';
    const namePicks = {
      female: {
        warm:      ['Samantha', 'Karen', 'Moira', 'Victoria', 'Tessa'],
        clear:     ['Google UK English Female', 'Microsoft Aria', 'Google UK English Female'],
        energetic: ['Google US English Female', 'Microsoft Zira', 'Microsoft Aria Online'],
      },
      male: {
        warm:      ['Daniel', 'Alex', 'Tom', 'Gordon'],
        clear:     ['Google UK English Male', 'Microsoft David', 'Google UK English Male'],
        energetic: ['Google US English Male', 'Microsoft Mark', 'Fred'],
      },
    };
    const picks = namePicks[gender]?.[type] || [];
    for (const name of picks) {
      const v = voices.find(v => v.name === name || v.name.includes(name));
      if (v) return v;
    }
    // Fallback: gender heuristic
    const femaleHints = ['female', 'samantha', 'karen', 'aria', 'zira', 'victoria', 'tessa', 'moira'];
    const maleHints   = ['male', 'daniel', 'alex', 'david', 'mark', 'fred', 'tom', 'jorge'];
    const hints = isFemale ? femaleHints : maleHints;
    const genderMatch = voices.find(v => hints.some(h => v.name.toLowerCase().includes(h)) && v.lang.startsWith('en'));
    if (genderMatch) return genderMatch;
    return voices.find(v => v.lang.startsWith('en')) || null;
  };

  const voiceSetupSpeak = (text, onDone) => {
    window.speechSynthesis.cancel();
    setVoiceSetupSpeaking(true);
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate  = VS_SPEED[vsSpeed] || 0.9;
    utter.pitch = 1.05;
    const voice = vsPickVoice(vsVoiceType, vsGender);
    if (voice) utter.voice = voice;
    utter.onend  = () => { setVoiceSetupSpeaking(false); if (onDone) onDone(); };
    utter.onerror = () => { setVoiceSetupSpeaking(false); if (onDone) onDone(); };
    window.speechSynthesis.speak(utter);
  };

  const voiceSetupStartListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    voiceSetupAdvancing.current = false;
    const rec = new SR();
    rec.lang = 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    voiceSetupRecRef.current = rec;
    rec.onstart  = () => setVoiceSetupListening(true);
    rec.onresult = (e) => {
      let interim = '';
      let final = voiceSetupTransRef.current;
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) { final += e.results[i][0].transcript + ' '; voiceSetupTransRef.current = final; }
        else interim = e.results[i][0].transcript;
      }
      setVoiceSetupTranscript((final + interim).trim());
    };
    // Auto-restart if browser times out (no user-initiated stop)
    rec.onend = () => {
      if (!voiceSetupAdvancing.current) {
        setVoiceSetupListening(false);
        // Restart after a short pause so user can keep talking
        setTimeout(() => {
          if (!voiceSetupAdvancing.current) {
            try { rec.start(); } catch {}
          }
        }, 300);
      }
    };
    rec.onerror = (e) => { if (e.error !== 'aborted') setVoiceSetupListening(false); };
    rec.start();
  };

  const voiceSetupStopListening = () => {
    voiceSetupAdvancing.current = true;
    voiceSetupRecRef.current?.stop();
    setVoiceSetupListening(false);
  };

  const voiceSetupAskQuestion = (step, withAck = false) => {
    const existing = VOICE_SETUP_QUESTIONS[step].getter();
    setVoiceSetupTranscript(existing || '');
    voiceSetupTransRef.current = existing || '';
    const ack      = withAck ? VS_ACKS[step] : null;
    const question = VOICE_SETUP_QUESTIONS[step].question;
    if (ack) {
      voiceSetupSpeak(ack, () => setTimeout(() => voiceSetupSpeak(question), 350));
    } else {
      voiceSetupSpeak(question);
    }
  };

  const vsPreviewVoice = (type, gender) => {
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance("Hey! I'm Blink, your Blinkapost AI assistant. Great to meet you!");
    utter.rate  = VS_SPEED[vsSpeed] || 0.9;
    utter.pitch = 1.05;
    const voice = vsPickVoice(type, gender || vsGender);
    if (voice) utter.voice = voice;
    window.speechSynthesis.speak(utter);
  };

  const startVoiceSetup = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast('Voice input is not supported in this browser.'); return; }
    setVoiceSetupStep(0);
    setVoiceSetupTranscript('');
    voiceSetupTransRef.current = '';
    setVoiceSetupPhase('settings');
    setVoiceSetupActive(true);
  };

  const vsStartQuestions = () => {
    setVoiceSetupPhase('questions');
    setVoiceSetupStep(0);
    const intro = "Hey! I'm Blink, your Blinkapost AI assistant — so good to meet you! I'm going to ask you five quick questions so your posts actually sound like you, not like some robot wrote them. Ready? Let's go!";
    voiceSetupSpeak(intro, () => setTimeout(() => voiceSetupAskQuestion(0), 400));
  };

  // Called when user presses "Press to finish" — saves + auto-advances
  const voiceSetupFinish = () => {
    voiceSetupStopListening();
    window.speechSynthesis.cancel();
    const transcript = voiceSetupTransRef.current.trim();
    if (transcript) VOICE_SETUP_QUESTIONS[voiceSetupStep].setter(transcript);
    if (voiceSetupStep < VOICE_SETUP_QUESTIONS.length - 1) {
      const nextStep = voiceSetupStep + 1;
      setVoiceSetupStep(nextStep);
      setVoiceSetupTranscript('');
      voiceSetupTransRef.current = '';
      voiceSetupAskQuestion(nextStep, !!transcript);
    } else {
      voiceSetupSpeak(VS_DONE_LINE, () => {
        setVoiceSetupActive(false);
        setVoiceSetupListening(false);
        setVoiceSetupSpeaking(false);
        saveUserSettings();
        showToast('Profile saved — your posts will feel like you from now on!');
      });
    }
  };

  const voiceSetupSkip = () => {
    voiceSetupStopListening();
    window.speechSynthesis.cancel();
    if (voiceSetupStep < VOICE_SETUP_QUESTIONS.length - 1) {
      const nextStep = voiceSetupStep + 1;
      setVoiceSetupStep(nextStep);
      setVoiceSetupTranscript('');
      voiceSetupTransRef.current = '';
      voiceSetupAskQuestion(nextStep, false);
    } else {
      setVoiceSetupActive(false);
      setVoiceSetupListening(false);
      setVoiceSetupSpeaking(false);
      saveUserSettings();
      showToast('Profile saved!');
    }
  };

  const voiceSetupBack = () => {
    voiceSetupStopListening();
    window.speechSynthesis.cancel();
    const prevStep = voiceSetupStep - 1;
    setVoiceSetupStep(prevStep);
    voiceSetupAskQuestion(prevStep, false);
  };

  const closeVoiceSetup = () => {
    voiceSetupAdvancing.current = true;
    voiceSetupRecRef.current?.stop();
    window.speechSynthesis.cancel();
    setVoiceSetupActive(false);
    setVoiceSetupListening(false);
    setVoiceSetupSpeaking(false);
  };

  // ── Global voice commands (header mic) ────────────
  const readPostAloud = () => {
    if (!posts?.instagram) { showToast('No posts yet — generate some first!'); return; }
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(posts.instagram));
    showToast('Reading Instagram post aloud...');
  };

  const cleanForSpeech = (text) => text
    .replace(/\*+/g, '').replace(/_+/g, '').replace(/#+\s*/g, '')
    .replace(/`+/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/,(?=\s)/g, '').replace(/•/g, '').replace(/\s{2,}/g, ' ').trim();

  const speakReply = (text) => {
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.92; utter.pitch = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => v.name === 'Samantha')
      || voices.find(v => v.name === 'Karen')
      || voices.find(v => v.name === 'Moira')
      || voices.find(v => v.name.includes('Google UK English Female'))
      || voices.find(v => v.name.includes('Microsoft Zira'))
      || voices.find(v => v.name.includes('Microsoft Aria'))
      || voices.find(v => v.lang === 'en-AU')
      || voices.find(v => v.lang.startsWith('en'));
    if (preferred) utter.voice = preferred;
    window.speechSynthesis.speak(utter);
  };

  const executeAssistantAction = (action) => {
    if (!action || action === 'none') return;
    if (action === 'goto_step_1') { setActiveTab('create'); setStep(1); }
    else if (action === 'goto_step_2') { setActiveTab('create'); setStep(2); }
    else if (action === 'goto_step_3') { setActiveTab('create'); setStep(3); }
    else if (action === 'goto_step_4') { setActiveTab('create'); setStep(4); }
    else if (action === 'tab_create') setActiveTab('create');
    else if (action === 'tab_schedule') setActiveTab('schedule');
    else if (action === 'tab_history') setActiveTab('history');
    else if (action === 'tab_accounts') setActiveTab('accounts');
    else if (action === 'connect_linkedin') connectLinkedin('app');
    else if (action === 'connect_facebook') connectFacebook('app');
    else if (action === 'connect_instagram') connectInstagram('app');
    else if (action === 'connect_tiktok') connectTikTok('app');
    else if (action === 'open_brand_settings') { setActiveTab('create'); setShowBrandSettings(true); }
    else if (action === 'logout') handleLogout();
  };

  const sendChatMessage = async (text) => {
    if (!text.trim() || chatLoading) return;
    const connectedPlatforms = [...new Set(connectedAccounts.map(a => a.platform))];
    setChatHistory(h => [...h, { role: 'user', text }]);
    setChatInput('');
    setChatLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'https://blinkapost.onrender.com'}/assistant`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'Authorization': `Bearer ${await getToken()}` },
        body: JSON.stringify({
          message: text,
          userPlan: 'pro', // TODO: replace with user plan tier when Stripe is added
          context: { step, connectedPlatforms, hasImage: !!image, hasVideo: !!video, hasPosts: !!posts, activeTab },
          userProfile: buildUserProfile(),
        })
      });
      const data = await res.json();
      setChatHistory(h => [...h, { role: 'blinka', text: data.message }]);
      speakReply(cleanForSpeech(data.message));
      setTimeout(() => executeAssistantAction(data.action), 600);
    } catch {
      setChatHistory(h => [...h, { role: 'blinka', text: "Sorry, I couldn't reach mission control. Try again!" }]);
    }
    setChatLoading(false);
  };

  const toggleListening = () => {
    if (chatLoading) return;
    if (isListening) { recognitionRef.current?.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast('Voice not supported in this browser. Try Chrome.'); return; }
    const recognition = new SR();
    recognition.lang = 'en-US'; recognition.continuous = false; recognition.interimResults = false;
    recognitionRef.current = recognition;
    recognition.onstart = () => { setIsListening(true); setActiveTab('assistant'); };
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (e) => sendChatMessage(e.results[0][0].transcript);
    recognition.onerror = () => { setIsListening(false); showToast('Could not hear you — try again.'); };
    recognition.start();
  };

  // ── Beta ──────────────────────────────────────────
  const loadBetaUsage = async (user) => {
    const { data } = await supabase.from('beta_usage').select('generations_used, generation_limit').eq('user_id', user.id).single();
    if (data) {
      setBetaUsed(data.generations_used);
      setBetaLimit(data.generation_limit);
    } else {
      const storedLimit = localStorage.getItem('blinkapost_beta_limit');
      if (storedLimit) {
        const limit = parseInt(storedLimit, 10);
        localStorage.removeItem('blinkapost_beta_limit');
        setBetaUsed(0);
        setBetaLimit(limit);
        getToken().then(token => fetch('https://blinkapost.onrender.com/beta/init-user', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ limit })
        })).catch(() => {});
      }
    }
  };

  // ── Auth ──────────────────────────────────────────
  const handleLogin = async () => {
    setAuthLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    setAuthLoading(false);
    if (error) { setMessage(error.message); } else { setUser(data.user); }
  };

  const handleSignUp = async () => {
    if (!email || !password) { setMessage('Please enter your email and a password.'); return; }
    if (!betaCode) { setMessage('Please enter your beta access code.'); return; }
    setAuthLoading(true);
    const codeRes = await fetch('https://blinkapost.onrender.com/beta/validate-code', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: betaCode })
    });
    const codeData = await codeRes.json();
    setAuthLoading(false);
    if (!codeData.valid) { setMessage('Invalid beta access code.'); return; }
    setMessage('');
    setSignupStep('trivia');
  };

  const handleTriviaAnswer = async (skip = false) => {
    const isCorrect = !skip && triviaAnswer.replace(/\s/g, '').includes('140');
    const limit = isCorrect ? 280 : 140;
    localStorage.setItem('blinkapost_beta_limit', String(limit));
    setTriviaCorrect(isCorrect);
    setAuthLoading(true);
    const { error } = await supabase.auth.signUp({ email, password });
    setAuthLoading(false);
    if (error) { setMessage(error.message); setSignupStep('form'); return; }
    setSignupStep('done');
  };

  const handleForgotPassword = async () => {
    if (!email) { setMessage('Enter your email address above first.'); return; }
    await supabase.auth.resetPasswordForEmail(email, { redirectTo: 'https://www.blinkapost.com' });
    setMessage('Password reset link sent — check your email.');
  };

  const handleGoogleSignIn = async () => {
    await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: 'https://www.blinkapost.com' } });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null); setPosts(null); setImage(null); setImagePreview(null);
    setVideo(null); setVideoPreview(null); setMediaType('image'); setVideoRatio('original');
    setSelectedLogo(null); setSavedLogos([]); setHistory([]); setScheduledPosts([]);
    setStep(1); setPostContext('');
  };

  const readVideoAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleVideoSelect = (file) => {
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) { showToast('Video must be under 500MB.'); return; }
    setImage(null); setImagePreview(null); setOriginalImage(null); setOriginalImagePreview(null);
    setVideo(file);
    setVideoPreview(URL.createObjectURL(file));
    setMediaType('video');
    setVideoDuration(0); setVideoTrimStart(0); setVideoTrimEnd(0);
    setTimeout(() => setStep(2), 400);
  };

  const startOver = () => {
    setStep(1); setImage(null); setImagePreview(null); setVideo(null); setVideoPreview(null); setMediaType('image'); setVideoRatio('original'); setPostContext(''); setPosts(null);
    setPostGoal(null); setConvFramework(null); setConvOffer(''); setConvCta(''); setConvAudience('');
  };

  // ── Subtle background particles for hero ──────────────
  useEffect(() => {
    if (user) return;
    const canvas = neuralCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const nodes = Array.from({ length: 28 }, () => ({
      x: Math.random(), y: Math.random(),
      vx: (Math.random() - 0.5) * 0.00022, vy: (Math.random() - 0.5) * 0.00022,
      r: Math.random() * 1.5 + 0.5, pulse: Math.random() * Math.PI * 2,
    }));
    const draw = () => {
      const W = canvas.offsetWidth, H = canvas.offsetHeight;
      canvas.width = W; canvas.height = H;
      ctx.clearRect(0, 0, W, H);
      nodes.forEach(n => {
        n.x = (n.x + n.vx + 1) % 1; n.y = (n.y + n.vy + 1) % 1; n.pulse += 0.018;
        const g = 0.5 + 0.5 * Math.sin(n.pulse);
        ctx.beginPath(); ctx.arc(n.x * W, n.y * H, n.r * g, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(139,92,246,${0.1 * g})`; ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, [user]);

  const SLOGANS = [
    "Your feed won't fill itself.",
    "Your competitors are already posting. Are you?",
    "Stop staring at a blank caption box.",
    "847 photos on your phone. Zero captions. We can fix that.",
    "Let AI do the typing. You do the living.",
    "Stay relevant. Stay consistent. Stay sane.",
  ];

  useEffect(() => {
    if (user) return;
    const interval = setInterval(() => {
      setSloganFade(false);
      setTimeout(() => {
        setSloganIndex(i => (i + 1) % SLOGANS.length);
        setSloganFade(true);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (user) return;
    const canvas = stepsCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    const nodes = Array.from({ length: 45 }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.45, vy: (Math.random() - 0.5) * 0.45,
      r: Math.random() * 2.5 + 1.5, pulse: Math.random() * Math.PI * 2,
    }));
    const draw = () => {
      canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      nodes.forEach(n => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        n.pulse += 0.03;
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          if (dist < 160) {
            ctx.beginPath(); ctx.moveTo(nodes[i].x, nodes[i].y); ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(139,92,246,${(1 - dist/160) * 0.3})`; ctx.lineWidth = 1; ctx.stroke();
          }
        }
      }
      nodes.forEach(n => {
        const glow = 0.6 + 0.4 * Math.sin(n.pulse);
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r * glow, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(167,139,250,${0.5 + 0.5 * glow})`;
        ctx.shadowBlur = 8; ctx.shadowColor = 'rgba(139,92,246,0.8)'; ctx.fill(); ctx.shadowBlur = 0;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, [user]);

  // Cycle posting messages while any post is in progress
  useEffect(() => {
    const anyPosting = linkedinPosting || facebookPosting || instagramPosting || tiktokPosting || postingAll;
    if (!anyPosting) return;
    const interval = setInterval(() => setPostingMsgIdx(i => (i + 1) % POSTING_MSGS.length), 1800);
    return () => clearInterval(interval);
  }, [linkedinPosting, facebookPosting, instagramPosting, tiktokPosting, postingAll]);

  useEffect(() => {
    if (!stepsRef.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setStepsVisible(true); obs.disconnect(); }
    }, { threshold: 0.1 });
    obs.observe(stepsRef.current);
    return () => obs.disconnect();
  }, [user, splashVisible]);

  // Camera stream lifecycle
  useEffect(() => {
    if (!showCamera) return;
    let cancelled = false;
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(t => t.stop());
      cameraStreamRef.current = null;
    }
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: cameraFacing } },
      audio: true,
    }).then(stream => {
      if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) cameraVideoRef.current.srcObject = stream;
    }).catch(() => {
      if (!cancelled) {
        showToast('Camera access denied — please allow camera permission and try again.');
        setShowCamera(false);
      }
    });
    return () => { cancelled = true; };
  }, [showCamera, cameraFacing]);

  // Draw live camera feed into filter preview canvases at 10fps
  useEffect(() => {
    if (!showCamera) return;
    const id = setInterval(() => {
      const vid = cameraVideoRef.current;
      if (!vid || !vid.videoWidth) return;
      filterCanvasRefs.current.forEach((canvas, i) => {
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const f = CAMERA_FILTERS[i].value;
        ctx.filter = f === 'none' ? '' : f;
        ctx.drawImage(vid, 0, 0, canvas.width, canvas.height);
      });
    }, 100);
    return () => clearInterval(id);
  }, [showCamera]);

  // Scroll to top on every step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [step]);

  const openCamera = (mode = 'photo') => { setCameraMode(mode); setShowCamera(true); };

  const closeCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(t => t.stop());
      cameraStreamRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setShowCamera(false);
    setIsRecording(false);
    recordedChunksRef.current = [];
  };

  const trimVideoInBrowser = (videoEl, startTime, endTime) => new Promise((resolve, reject) => {
    videoEl.currentTime = startTime;
    videoEl.onseeked = () => {
      videoEl.onseeked = null;
      const captureStream = videoEl.captureStream || videoEl.mozCaptureStream;
      if (!captureStream) { resolve(null); return; } // not supported — use original
      const stream = captureStream.call(videoEl);
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
      let recorder;
      try { recorder = new MediaRecorder(stream, { mimeType }); }
      catch { resolve(null); return; }
      const chunks = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => resolve(new Blob(chunks, { type: mimeType }));
      recorder.onerror = () => reject(new Error('Trim failed'));
      recorder.start();
      videoEl.play();
      videoEl.ontimeupdate = () => {
        if (videoEl.currentTime >= endTime - 0.05) {
          videoEl.ontimeupdate = null;
          videoEl.pause();
          recorder.stop();
        }
      };
    };
  });

  const capturePhoto = () => {
    const vid = cameraVideoRef.current;
    if (!vid) return;
    const vw = vid.videoWidth;
    const vh = vid.videoHeight;
    let sx = 0, sy = 0, sw = vw, sh = vh;
    if (cameraRatio !== 'free') {
      const [rw, rh] = cameraRatio.split(':').map(Number);
      const target = rw / rh;
      const current = vw / vh;
      if (current > target) { sw = vh * target; sx = (vw - sw) / 2; }
      else { sh = vw / target; sy = (vh - sh) / 2; }
    }
    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    if (cameraFilter !== 'none') ctx.filter = cameraFilter;
    ctx.drawImage(vid, sx, sy, sw, sh, 0, 0, sw, sh);
    canvas.toBlob(blob => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      closeCamera();
      handleImageSelect(file);
    }, 'image/jpeg', 0.92);
  };

  const startRecording = () => {
    if (!cameraStreamRef.current) return;
    recordedChunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm')
      ? 'video/webm'
      : 'video/mp4';
    const recorder = new MediaRecorder(cameraStreamRef.current, { mimeType });
    recorder.ondataavailable = e => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: mimeType });
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `video_${Date.now()}.${ext}`, { type: mimeType });
      handleVideoSelect(file);
    };
    mediaRecorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    closeCamera();
  };

  // ── Voice Setup Interview overlay ─────────────────
  if (voiceSetupActive) {
    const totalSteps = VOICE_SETUP_QUESTIONS.length;
    const q = VOICE_SETUP_QUESTIONS[voiceSetupStep];
    const hasAnswer = !!(voiceSetupTranscript || voiceSetupTransRef.current.trim());

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(145deg, #0f0c29 0%, #302b63 60%, #24243e 100%)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '48px 24px 40px', overflow: 'hidden' }}>
        {/* Stars */}
        {SPLASH_STARS.slice(0, 40).map((s, i) => (
          <div key={i} style={{ position: 'absolute', borderRadius: '50%', background: 'white', opacity: 0.1, left: `${s.x}%`, top: `${s.y}%`, width: `${s.size}px`, height: `${s.size}px` }} />
        ))}

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '380px', position: 'relative', zIndex: 1 }}>
          {voiceSetupPhase === 'questions' && voiceSetupStep > 0
            ? <button onClick={voiceSetupBack} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.7)', borderRadius: '10px', padding: '7px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>← Back</button>
            : <img src="/Blinkapost logo transparent.png" alt="Blinkapost" style={{ height: '32px', objectFit: 'contain' }} />
          }
          <button onClick={closeVoiceSetup} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.5)', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px' }}>✕</button>
        </div>

        {/* ── PHASE: Voice settings ── */}
        {voiceSetupPhase === 'settings' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '380px', gap: '24px', position: 'relative', zIndex: 1 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>👋</div>
              <h2 style={{ color: 'white', fontSize: '22px', fontWeight: '800', margin: '0 0 8px' }}>Meet Blink</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: 0, lineHeight: 1.6 }}>Your Blinkapost AI assistant. We're going to have a quick chat so your posts sound exactly like <em>you</em>.</p>
            </div>

            {/* Gender */}
            <div style={{ width: '100%' }}>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px', textAlign: 'center' }}>Blink's gender</p>
              <div style={{ display: 'flex', gap: '10px' }}>
                {[{ key: 'female', icon: '👩', label: 'Female' }, { key: 'male', icon: '👨', label: 'Male' }].map(g => (
                  <button key={g.key} onClick={() => { setVsGender(g.key); vsPreviewVoice(vsVoiceType, g.key); }}
                    style={{ flex: 1, padding: '13px', borderRadius: '12px', border: vsGender === g.key ? '2px solid #818cf8' : '2px solid rgba(255,255,255,0.1)', background: vsGender === g.key ? 'rgba(129,140,248,0.2)' : 'rgba(255,255,255,0.04)', cursor: 'pointer', color: 'white', fontSize: '14px', fontWeight: vsGender === g.key ? '700' : '400' }}>
                    {g.icon} {g.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Voice style */}
            <div style={{ width: '100%' }}>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px', textAlign: 'center' }}>Voice style — tap to preview</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                {[
                  { key: 'warm',      icon: '🤍', label: 'Warm',      sub: 'Friendly & relaxed' },
                  { key: 'clear',     icon: '💼', label: 'Clear',     sub: 'Crisp & polished' },
                  { key: 'energetic', icon: '⚡', label: 'Energetic', sub: 'Upbeat & bright' },
                ].map(v => (
                  <button key={v.key} onClick={() => { setVsVoiceType(v.key); vsPreviewVoice(v.key, vsGender); }}
                    style={{ padding: '12px 8px', borderRadius: '12px', border: vsVoiceType === v.key ? '2px solid #818cf8' : '2px solid rgba(255,255,255,0.1)', background: vsVoiceType === v.key ? 'rgba(129,140,248,0.18)' : 'rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>{v.icon}</div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: 'white' }}>{v.label}</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '2px', lineHeight: 1.3 }}>{v.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Speed */}
            <div style={{ width: '100%' }}>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px', textAlign: 'center' }}>Speech speed</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[{ key: 'slow', label: '🐢 Slow' }, { key: 'normal', label: '👌 Normal' }, { key: 'fast', label: '⚡ Fast' }].map(s => (
                  <button key={s.key} onClick={() => setVsSpeed(s.key)}
                    style={{ flex: 1, padding: '11px', borderRadius: '10px', border: vsSpeed === s.key ? '2px solid #818cf8' : '2px solid rgba(255,255,255,0.1)', background: vsSpeed === s.key ? 'rgba(129,140,248,0.18)' : 'rgba(255,255,255,0.04)', color: 'white', fontSize: '12px', fontWeight: vsSpeed === s.key ? '700' : '400', cursor: 'pointer' }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── PHASE: Questions ── */}
        {voiceSetupPhase === 'questions' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '380px', gap: '18px', position: 'relative', zIndex: 1 }}>
            {/* Progress dots */}
            <div style={{ display: 'flex', gap: '6px' }}>
              {VOICE_SETUP_QUESTIONS.map((_, i) => (
                <div key={i} style={{ width: i === voiceSetupStep ? '24px' : '8px', height: '8px', borderRadius: '4px', background: i < voiceSetupStep ? '#10b981' : i === voiceSetupStep ? '#818cf8' : 'rgba(255,255,255,0.18)', transition: 'all 0.3s ease' }} />
              ))}
            </div>

            <p style={{ color: 'rgba(255,255,255,0.38)', fontSize: '11px', margin: 0, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {voiceSetupStep + 1} of {totalSteps} — {q.label}
            </p>

            {/* Question text */}
            <p style={{ color: 'white', fontSize: '17px', fontWeight: '600', margin: 0, textAlign: 'center', lineHeight: 1.65 }}>
              {voiceSetupSpeaking ? <span style={{ color: 'rgba(255,255,255,0.55)', fontStyle: 'italic', fontWeight: '400' }}>Blink is speaking...</span> : q.question}
            </p>

            {/* Mic */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {voiceSetupListening && <>
                  <div className="mic-ring" style={{ borderColor: '#818cf8' }} />
                  <div className="mic-ring" style={{ borderColor: '#818cf8', animationDelay: '0.3s' }} />
                  <div className="mic-ring" style={{ borderColor: '#818cf8', animationDelay: '0.6s' }} />
                </>}
                <button
                  onClick={() => {
                    if (voiceSetupSpeaking) return;
                    if (voiceSetupListening) { voiceSetupFinish(); }
                    else { voiceSetupStartListening(); }
                  }}
                  disabled={voiceSetupSpeaking}
                  style={{ width: '82px', height: '82px', borderRadius: '50%', border: 'none', background: voiceSetupListening ? 'linear-gradient(135deg, #4F46E5, #7c3aed)' : voiceSetupSpeaking ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)', cursor: voiceSetupSpeaking ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.25s', boxShadow: voiceSetupListening ? '0 0 30px rgba(129,140,248,0.5)' : 'none' }}>
                  <img src="/Blinkapost bulb.png" alt="Mic" style={{ width: '44px', height: '44px', objectFit: 'contain', opacity: voiceSetupSpeaking ? 0.25 : 1 }} />
                </button>
              </div>
              {/* Press to talk / Press to finish label */}
              <p style={{ color: voiceSetupListening ? '#a5b4fc' : 'rgba(255,255,255,0.35)', fontSize: '12px', fontWeight: voiceSetupListening ? '700' : '400', margin: 0, textAlign: 'center', letterSpacing: '0.03em', transition: 'all 0.2s' }}>
                {voiceSetupSpeaking ? '' : voiceSetupListening ? 'Press to finish' : 'Press to talk'}
              </p>
            </div>

            {/* Transcript box */}
            <div style={{ width: '100%', minHeight: '64px', background: 'rgba(255,255,255,0.05)', borderRadius: '14px', padding: '13px 16px', border: voiceSetupListening ? '1px solid rgba(129,140,248,0.38)' : '1px solid rgba(255,255,255,0.06)', transition: 'border 0.2s' }}>
              {!voiceSetupSpeaking && !voiceSetupTranscript && !voiceSetupListening && <p style={{ color: 'rgba(255,255,255,0.22)', fontSize: '13px', margin: 0, textAlign: 'center' }}>Your answer will appear here as you speak</p>}
              {!voiceSetupSpeaking && !voiceSetupTranscript && voiceSetupListening  && <p style={{ color: 'rgba(129,140,248,0.8)', fontSize: '13px', margin: 0, textAlign: 'center' }}>Listening — take your time...</p>}
              {voiceSetupTranscript && <p style={{ color: 'white', fontSize: '14px', margin: 0, lineHeight: 1.65 }}>{voiceSetupTranscript}</p>}
            </div>

            {/* Skip link */}
            <button onClick={voiceSetupSkip} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.28)', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>
              Skip this question
            </button>
          </div>
        )}

        {/* Bottom CTA */}
        <div style={{ width: '100%', maxWidth: '380px', position: 'relative', zIndex: 1 }}>
          {voiceSetupPhase === 'settings' && (
            <button onClick={vsStartQuestions}
              style={{ width: '100%', padding: '15px', background: 'linear-gradient(135deg, #4F46E5, #7c3aed)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '700', cursor: 'pointer' }}>
              Let's go →
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Splash screen ─────────────────────────────────
  if (splashVisible) {
    const SPLASH_PLANETS = [
      { key: 'instagram', size: 34, orbitD: 112, dur: 7,  startDeg: 0,
        bg: 'radial-gradient(circle at 38% 35%, #f77737, #c13584 55%, #6c27ab)',
        glow: 'rgba(193,53,132,0.55)',
        d: 'M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z' },
      { key: 'tiktok',    size: 30, orbitD: 156, dur: 10, startDeg: 72,
        bg: 'radial-gradient(circle at 38% 35%, #25F4EE, #3b3b5c 45%, #010101)',
        glow: 'rgba(37,244,238,0.45)',
        d: 'M19.59,6.69A4.83,4.83 0 0,1 15.82,2.5H12.37V15.67A2.89,2.89 0 0,1 9.49,18.17C7.9,18.17 6.62,16.92 6.62,15.33C6.62,13.47 8.32,12.05 10.18,12.49V8.97A6.33,6.33 0 0,0 3.17,15.33C3.17,18.9 6.09,21.83 9.66,21.83C13.22,21.83 16.14,18.9 16.14,15.33V8.85A8.17,8.17 0 0,0 20.83,10.34V6.88A4.85,4.85 0 0,1 19.59,6.69Z' },
      { key: 'facebook',  size: 30, orbitD: 196, dur: 14, startDeg: 144,
        bg: 'radial-gradient(circle at 38% 35%, #6aaef7, #1877F2 50%, #0d50b3)',
        glow: 'rgba(24,119,242,0.5)',
        d: 'M17,2V2H17V6H15C14.31,6 14,6.81 14,7.5V10H14L17,10V14H14V22H10V14H7V10H10V6A4,4 0 0,1 14,2H17Z' },
      { key: 'linkedin',  size: 28, orbitD: 232, dur: 18, startDeg: 216,
        bg: 'radial-gradient(circle at 38% 35%, #4ea8e8, #0A66C2 50%, #064a8c)',
        glow: 'rgba(10,102,194,0.5)',
        d: 'M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.46 12.92,11.24V10.13H10.13V18.5H12.92V13.57C12.92,12.8 13.54,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,5.95 7.81,5.19 6.88,5.19A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.95,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z' },
      { key: 'gmb',       size: 26, orbitD: 264, dur: 23, startDeg: 288,
        bg: 'radial-gradient(circle at 38% 35%, #7de878, #34A853 50%, #1b6e30)',
        glow: 'rgba(52,168,83,0.5)',
        d: 'M12,2C8.13,2 5,5.13 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9C19,5.13 15.87,2 12,2M12,11.5C10.62,11.5 9.5,10.38 9.5,9C9.5,7.62 10.62,6.5 12,6.5C13.38,6.5 14.5,7.62 14.5,9C14.5,10.38 13.38,11.5 12,11.5Z' },
    ];

    // Reusable shuttle SVG — logo lives on the body, viewBox stays constant, size changes
    const shuttleSVG = (w, h) => (
      <svg viewBox="0 0 80 148" width={w} height={h} xmlns="http://www.w3.org/2000/svg">
        <path d="M40,3 Q29,10 27,30 L53,30 Q51,10 40,3Z" fill="#94a3b8"/>
        <path d="M40,3 Q35,9 33,20 L47,20 Q45,9 40,3Z" fill="#1f2937" opacity="0.5"/>
        <rect x="25" y="28" width="30" height="80" rx="4" fill="#e2e8f0"/>
        <line x1="40" y1="28" x2="40" y2="108" stroke="#cbd5e1" strokeWidth="1"/>
        <image href="/Blinkapost logo transparent.png" x="25" y="29" width="30" height="30" />
        <path d="M25,70 L3,118 L25,106Z" fill="#cbd5e1"/>
        <path d="M25,70 L3,118 L25,106Z" fill="none" stroke="#94a3b8" strokeWidth="0.8"/>
        <path d="M55,70 L77,118 L55,106Z" fill="#cbd5e1"/>
        <path d="M55,70 L77,118 L55,106Z" fill="none" stroke="#94a3b8" strokeWidth="0.8"/>
        <path d="M36,106 L32,134 L48,134 L44,106Z" fill="#94a3b8"/>
        <ellipse cx="31" cy="110" rx="6" ry="4.5" fill="#374151"/>
        <ellipse cx="40" cy="113" rx="6" ry="4.5" fill="#374151"/>
        <ellipse cx="49" cy="110" rx="6" ry="4.5" fill="#374151"/>
        <ellipse cx="31" cy="110" rx="3.5" ry="2.5" fill="#fb923c"/>
        <ellipse cx="40" cy="113" rx="3.5" ry="2.5" fill="#fde68a"/>
        <ellipse cx="49" cy="110" rx="3.5" ry="2.5" fill="#fb923c"/>
      </svg>
    );

    const flameSVG = (w, h, cls = 'splash-flame') => (
      <svg className={cls} viewBox="0 0 80 85" width={w} height={h}
        xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '-8px' }}>
        <defs>
          <radialGradient id="sfgC" cx="50%" cy="0%" r="85%" gradientUnits="objectBoundingBox">
            <stop offset="0%"   stopColor="white"   stopOpacity="1"/>
            <stop offset="18%"  stopColor="#fde68a" stopOpacity="0.98"/>
            <stop offset="55%"  stopColor="#f97316" stopOpacity="0.7"/>
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0"/>
          </radialGradient>
          <radialGradient id="sfgS" cx="50%" cy="0%" r="85%" gradientUnits="objectBoundingBox">
            <stop offset="0%"   stopColor="#fde68a" stopOpacity="0.9"/>
            <stop offset="45%"  stopColor="#f97316" stopOpacity="0.65"/>
            <stop offset="100%" stopColor="#dc2626" stopOpacity="0"/>
          </radialGradient>
        </defs>
        <ellipse cx="40" cy="4" rx="26" ry="8" fill="#fde68a" opacity="0.45"/>
        <path d="M27,2 C23,20 19,42 22,72 C26,52 33,36 31,2Z" fill="url(#sfgS)"/>
        <path d="M36,0 C31,24 29,52 33,84 C37,65 43,65 47,84 C51,52 49,24 44,0Z" fill="url(#sfgC)"/>
        <path d="M49,2 C57,20 61,42 58,72 C54,52 47,36 49,2Z" fill="url(#sfgS)"/>
      </svg>
    );

    // Planets render helper — used in both orbit and approach phases
    const renderPlanets = (stagger = false) => SPLASH_PLANETS.map((p, i) => {
      const delay = -((p.startDeg / 360) * p.dur);
      return (
        <div key={p.key} style={{
          position: 'absolute',
          top: `calc(50% - ${p.orbitD / 2}px)`,
          left: `calc(50% - ${p.orbitD / 2}px)`,
          width: p.orbitD, height: p.orbitD,
          opacity: stagger ? 0 : 1,
          animation: stagger
            ? `splashPlanetReveal 0.45s ease-out ${i * 0.18}s both`
            : undefined,
        }}>
          <div style={{
            width: '100%', height: '100%',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.09)',
            animation: `splashOrbit ${p.dur}s linear ${delay}s infinite`,
          }}>
            <div style={{
              position: 'absolute',
              top: `-${p.size / 2}px`,
              left: `calc(50% - ${p.size / 2}px)`,
              width: p.size, height: p.size,
              borderRadius: '50%',
              background: p.bg,
              boxShadow: `0 0 10px 3px ${p.glow}, inset -3px -3px 6px rgba(0,0,0,0.35)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: `splashCounterOrbit ${p.dur}s linear ${delay}s infinite`,
            }}>
              <svg viewBox="0 0 24 24" width={p.size * 0.52} height={p.size * 0.52}>
                <path d={p.d} fill="white" />
              </svg>
            </div>
          </div>
        </div>
      );
    });

    return (
      <div className={`splash-screen${splashFading ? ' splash-fading' : ''}`}>
        {/* Stars */}
        {SPLASH_STARS.map((s, i) => (
          <div key={i} className="splash-star" style={{
            left: `${s.x}%`, top: `${s.y}%`,
            width: `${s.size}px`, height: `${s.size}px`,
            animationDelay: `${s.delay}s`, animationDuration: `${s.dur}s`,
          }} />
        ))}

        {/* ── Phase 1: Launch — shuttle rockets up with logo ── */}
        {splashPhase === 'launch' && (
          <>
            <div className="splash-launch-glow" />
            <div style={{ position: 'absolute', bottom: 0, left: '50%' }}>
              <div className="splash-shuttle-mover">
                <div style={{ transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {shuttleSVG(80, 148)}
                  {flameSVG(72, 77)}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Phase 2: Orbit — planets appear, shuttle laps them ── */}
        {splashPhase === 'orbit' && (
          <div className="splash-solar">
            {/* Planets with staggered fade-in */}
            {renderPlanets(true)}

            {/* Shuttle on fast inner orbit (between Facebook 196 and LinkedIn 232) */}
            <div style={{
              position: 'absolute',
              top: 'calc(50% - 110px)',
              left: 'calc(50% - 110px)',
              width: 220, height: 220,
              borderRadius: '50%',
              animation: 'splashOrbit 2s linear 0s infinite',
            }}>
              {/*
                Shuttle sits at 12-o'clock of the ring.
                rotate(90deg) = nose points RIGHT = correct for CW orbit travel direction.
                top: -10px  = half of 20px (shuttle's rotated height at this scale)
                left: calc(50% - 18px) = half of 36px (shuttle's rotated width at this scale)
              */}
              <div style={{
                position: 'absolute',
                top: '-10px',
                left: 'calc(50% - 18px)',
                transform: 'rotate(90deg)',
                filter: 'drop-shadow(0 0 5px rgba(251,146,60,0.95)) drop-shadow(0 0 14px rgba(239,68,68,0.55))',
              }}>
                {shuttleSVG(20, 37)}
              </div>
            </div>

            {/* Center glow */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%,-50%)',
              width: 80, height: 80, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)',
              pointerEvents: 'none',
            }} />
          </div>
        )}

        {/* ── Phase 3: Approach — solar system fades, shuttle zooms at camera ── */}
        {splashPhase === 'approach' && (
          <>
            {/* Planets fade out */}
            <div className="splash-solar splash-solar-fadeout">
              {renderPlanets(false)}
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                width: 80, height: 80, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />
            </div>

            {/* Shuttle zooms toward camera from center */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
            }}>
              <div className="splash-approach-shuttle">
                {shuttleSVG(80, 148)}
                {flameSVG(72, 77, 'splash-flame splash-approach-flame')}
              </div>
            </div>

            {/* White flash overlay */}
            <div className="splash-approach-flash" />
          </>
        )}
      </div>
    );
  }

  // ── Landing page (logged out) ─────────────────────
  if (!user) {
    return (
      <div style={{ minHeight: '100vh', background: 'white', fontFamily: 'Arial, sans-serif' }}>

        {/* Navbar */}
        <nav style={{ position: 'sticky', top: 0, background: 'rgba(15,12,41,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 50 }}>
          <img src="/Blinkapost logo transparent.png" alt="Blinkapost" style={{ height: '36px', objectFit: 'contain' }} />
          <button onClick={() => { setIsSignUp(false); setMessage(''); setShowLogin(true); }} className="landing-nav-btn">Sign in</button>
        </nav>

        {/* Hero */}
        <section className="landing-hero" style={{ background: 'linear-gradient(145deg, #0f0c29 0%, #302b63 45%, #24243e 100%)', padding: '56px 24px 72px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <canvas ref={neuralCanvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
          <div style={{ maxWidth: '640px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            {/* Badge */}
            <span style={{ background: 'rgba(139,92,246,0.3)', color: '#c4b5fd', fontSize: '13px', fontWeight: '600', padding: '5px 16px', borderRadius: '20px', display: 'inline-block', marginBottom: '20px', border: '1px solid rgba(139,92,246,0.4)' }}>AI-Powered Social Media</span>

            {/* Hub SVG */}
            {(() => {
              const HUB = [
                { key: 'instagram', label: 'Instagram',       color: '#E1306C', x: 0,    y: -148,
                  d: 'M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z' },
                { key: 'linkedin',  label: 'LinkedIn',         color: '#0A66C2', x: 141,  y: -46,
                  d: 'M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.46 12.92,11.24V10.13H10.13V18.5H12.92V13.57C12.92,12.8 13.54,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,5.95 7.81,5.19 6.88,5.19A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.95,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z' },
                { key: 'facebook',  label: 'Facebook',         color: '#1877F2', x: 87,   y: 120,
                  d: 'M17,2V2H17V6H15C14.31,6 14,6.81 14,7.5V10H14L17,10V14H14V22H10V14H7V10H10V6A4,4 0 0,1 14,2H17Z' },
                { key: 'tiktok',    label: 'TikTok',           color: '#69C9D0', x: -87,  y: 120,
                  d: 'M19.59,6.69A4.83,4.83 0 0,1 15.82,2.5H12.37V15.67A2.89,2.89 0 0,1 9.49,18.17C7.9,18.17 6.62,16.92 6.62,15.33C6.62,13.47 8.32,12.05 10.18,12.49V8.97A6.33,6.33 0 0,0 3.17,15.33C3.17,18.9 6.09,21.83 9.66,21.83C13.22,21.83 16.14,18.9 16.14,15.33V8.85A8.17,8.17 0 0,0 20.83,10.34V6.88A4.85,4.85 0 0,1 19.59,6.69Z' },
                { key: 'gmb',       label: 'Google Business',  color: '#34A853', x: -141, y: -46,
                  d: 'M12,2C8.13,2 5,5.13 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9C19,5.13 15.87,2 12,2M12,11.5C10.62,11.5 9.5,10.38 9.5,9C9.5,7.62 10.62,6.5 12,6.5C13.38,6.5 14.5,7.62 14.5,9C14.5,10.38 13.38,11.5 12,11.5Z' },
              ];
              return (
                <div style={{ position: 'relative', width: '100%', maxWidth: '460px', margin: '0 auto 24px' }}>
                  <svg viewBox="-205 -200 410 390" style={{ width: '100%', height: 'auto', display: 'block' }}>
                    <defs>
                      <radialGradient id="cg" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.55" />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                      </radialGradient>
                    </defs>
                    <circle cx="0" cy="0" r="68" fill="url(#cg)" />
                    {HUB.map(p => (
                      <line key={p.key} x1="0" y1="0" x2={p.x} y2={p.y}
                        stroke={p.color} strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="5 4" />
                    ))}
                    {HUB.map((p, i) => [0, 0.48].map(off => (
                      <circle key={`${p.key}-${off}`} r="4" fill={p.color} opacity="0.9">
                        <animateMotion dur={`${1.5 + i * 0.11}s`} begin={`${off * (1.5 + i * 0.11)}s`} repeatCount="indefinite" path={`M0,0 L${p.x},${p.y}`} />
                      </circle>
                    )))}
                    {HUB.map((p, i) => (
                      <g key={p.key} transform={`translate(${p.x},${p.y})`}>
                        <circle r="28" fill={p.color} opacity="0.18">
                          <animate attributeName="r" values="26;36;26" dur={`${2.6 + i * 0.35}s`} repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.18;0.06;0.18" dur={`${2.6 + i * 0.35}s`} repeatCount="indefinite" />
                        </circle>
                        <circle r="26" fill={p.color} />
                        <g transform="translate(-9,-9) scale(0.75)">
                          <path fill="white" d={p.d} />
                        </g>
                        <text y="43" textAnchor="middle" fill="rgba(255,255,255,0.65)" fontSize="10" fontWeight="600" fontFamily="-apple-system,BlinkMacSystemFont,sans-serif">{p.label}</text>
                      </g>
                    ))}
                  </svg>
                  {/* Center logo overlaid with CSS glow — same as CTA banner */}
                  <img
                    src="/Blinkapost logo transparent.png"
                    alt="Blinkapost"
                    className="logo-pulse"
                    style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -56%)', width: '19%', height: 'auto', pointerEvents: 'none' }}
                  />
                </div>
              );
            })()}

            <h1 style={{ color: 'white', margin: '0 0 16px', textShadow: '0 2px 20px rgba(79,70,229,0.4)' }}>Stop writing.<br />Start posting.</h1>
            <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.7)', margin: '0 0 36px', lineHeight: '1.7' }}>
              Snap, upload, done — Blinkapost's AI turns your photos and videos into ready-to-post captions for Instagram, LinkedIn, Facebook, TikTok, Google Business and more in seconds.
            </p>
            <button onClick={() => { setIsSignUp(true); setMessage(''); setShowLogin(true); }} className="landing-cta-btn">
              Get started free →
            </button>
            <p style={{ margin: '14px 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>No credit card required</p>
          </div>
        </section>

        {/* How it works */}
        <section ref={stepsRef} style={{ padding: '90px 24px', background: 'linear-gradient(145deg, #0f0c29 0%, #1a1545 50%, #0f0c29 100%)', position: 'relative', overflow: 'hidden' }}>
          <canvas ref={stepsCanvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '700px', height: '700px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <p style={{ textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 12px' }}>How it works</p>
            <h2 style={{ textAlign: 'center', fontSize: '34px', fontWeight: '800', color: 'white', margin: '0 0 64px', lineHeight: 1.2 }}>Four steps to your next post</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
              {[
                { num: '01', icon: '📸', title: 'Add your media', desc: 'Upload a photo or video, or take one right from the app using your camera.' },
                { num: '02', icon: '🎨', title: 'Edit & add your logo', desc: 'Adjust brightness, crop, apply filters, and stamp your logo — just tell us what you want to change.' },
                { num: '03', icon: '✨', title: 'AI writes your posts', desc: 'Our AI analyses your image or video and writes platform-perfect captions for Instagram, LinkedIn, Facebook and TikTok.' },
                { num: '04', icon: '🚀', title: 'Preview & post', desc: 'Publish directly to your connected accounts in one click, or schedule for the best engagement time.' },
              ].map((s, i) => (
                <div key={s.num} className={`step-reveal-card${stepsVisible ? ' step-visible' : ''}`} style={{ animationDelay: `${i * 0.35}s`, width: '100%', maxWidth: '620px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '20px', padding: '32px', display: 'flex', alignItems: 'center', gap: '28px', backdropFilter: 'blur(8px)' }}>
                  <div style={{ flexShrink: 0, width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(124,58,237,0.3))', border: '1px solid rgba(139,92,246,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', boxShadow: '0 0 24px rgba(124,58,237,0.3)' }}>{s.icon}</div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <span style={{ fontSize: '11px', fontWeight: '800', color: '#8b5cf6', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Step {s.num}</span>
                    <h3 style={{ margin: '4px 0 8px', fontSize: '20px', fontWeight: '800', color: 'white' }}>{s.title}</h3>
                    <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.6)', lineHeight: '1.7' }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Post directly to */}
        <section style={{ padding: '56px 24px', background: '#f9fafb' }}>
          <div style={{ maxWidth: '640px', margin: '0 auto', textAlign: 'center' }}>
            <p style={{ margin: '0 0 28px', fontSize: '13px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Post directly to</p>
            <div className="landing-platforms">
              {[
                { name: 'Instagram', color: '#E1306C', d: 'M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z' },
                { name: 'LinkedIn',  color: '#0A66C2', d: 'M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.46 12.92,11.24V10.13H10.13V18.5H12.92V13.57C12.92,12.8 13.54,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,5.95 7.81,5.19 6.88,5.19A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.95,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z' },
                { name: 'Facebook',  color: '#1877F2', d: 'M17,2V2H17V6H15C14.31,6 14,6.81 14,7.5V10H14L17,10V14H14V22H10V14H7V10H10V6A4,4 0 0,1 14,2H17Z' },
                { name: 'TikTok',    color: '#010101', d: 'M19.59,6.69A4.83,4.83 0 0,1 15.82,2.5H12.37V15.67A2.89,2.89 0 0,1 9.49,18.17C7.9,18.17 6.62,16.92 6.62,15.33C6.62,13.47 8.32,12.05 10.18,12.49V8.97A6.33,6.33 0 0,0 3.17,15.33C3.17,18.9 6.09,21.83 9.66,21.83C13.22,21.83 16.14,18.9 16.14,15.33V8.85A8.17,8.17 0 0,0 20.83,10.34V6.88A4.85,4.85 0 0,1 19.59,6.69Z' },
                { name: 'Google Business', color: '#34A853', d: 'M12,2C8.13,2 5,5.13 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9C19,5.13 15.87,2 12,2M12,11.5C10.62,11.5 9.5,10.38 9.5,9C9.5,7.62 10.62,6.5 12,6.5C13.38,6.5 14.5,7.62 14.5,9C14.5,10.38 13.38,11.5 12,11.5Z' },
              ].map(({ name, color, d }) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'white', borderRadius: '12px', padding: '12px 18px', border: '1px solid #e5e7eb', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                    <path fill={color} d={d} />
                  </svg>
                  {name}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section style={{ padding: '72px 24px', background: 'white' }}>
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>
            <h2 style={{ textAlign: 'center', fontSize: '30px', fontWeight: '800', color: '#1e1b4b', margin: '0 0 48px' }}>Everything you need</h2>
            <div className="landing-features-grid">
              {[
                { icon: '🎤', title: 'Voice commands', desc: 'Speak to edit images, set your tone, or generate posts completely hands-free.' },
                { icon: '🎨', title: 'Image editing', desc: 'Crop, rotate, add filters, and overlay your logo — all in one place.' },
                { icon: '🗣️', title: 'Brand voice', desc: 'Save your unique tone and it\'s automatically applied to every post you generate.' },
                { icon: '📅', title: 'Post scheduling', desc: 'Queue posts for the best times and manage your whole calendar in one tab.' },
                { icon: '🔄', title: 'Multi-account', desc: 'Connect multiple pages per platform and switch between them with one tap.' },
                { icon: '📋', title: 'Post history', desc: 'Every post you generate is saved so you can revisit, copy, and reuse anytime.' },
              ].map(f => (
                <div key={f.title} style={{ background: '#f9fafb', borderRadius: '16px', padding: '24px', border: '1px solid #e5e7eb' }}>
                  <span style={{ fontSize: '28px', display: 'block', marginBottom: '12px' }}>{f.icon}</span>
                  <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '700', color: '#1e1b4b' }}>{f.title}</h3>
                  <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section style={{ padding: '90px 24px', background: 'linear-gradient(145deg, #0f0c29 0%, #302b63 45%, #24243e 100%)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '600px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <img src="/Blinkapost logo transparent.png" alt="Blinkapost" className="logo-pulse" style={{ height: '160px', objectFit: 'contain', marginBottom: '32px' }} />
            <h2 style={{ margin: '0 0 20px', fontSize: '36px', fontWeight: '800', color: 'white', lineHeight: 1.2 }}>Ready to post smarter?</h2>
            <p style={{ margin: '0 0 36px', fontSize: '20px', color: '#a78bfa', fontWeight: '600', minHeight: '30px', transition: 'opacity 0.4s ease', opacity: sloganFade ? 1 : 0 }}>
              {SLOGANS[sloganIndex]}
            </p>
            <button onClick={() => { setIsSignUp(true); setMessage(''); setShowLogin(true); }} className="landing-cta-btn">Get started free →</button>
            <p style={{ margin: '16px 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>No credit card required</p>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ background: '#1e1b4b', padding: '36px 24px', textAlign: 'center' }}>
          <img src="/Blinkapost logo.png" alt="Blinkapost" style={{ height: '30px', objectFit: 'contain', marginBottom: '18px', filter: 'brightness(0) invert(1)', opacity: 0.75 }} />
          <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <a href="/privacy" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', textDecoration: 'none' }}>Privacy Policy</a>
            <a href="/terms" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', textDecoration: 'none' }}>Terms of Service</a>
            <a href="/delete-data" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', textDecoration: 'none' }}>Delete Data</a>
          </div>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>© 2025 Blinkapost. All rights reserved.</p>
        </footer>

        {showLogin && (
          <div onClick={e => { if (e.target === e.currentTarget && signupStep === 'form') { setShowLogin(false); setSignupStep('form'); } }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 100 }}>
            <div style={{ background: 'white', borderRadius: '20px', padding: '36px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
              <img src="/Blinkapost logo.png" alt="Blinkapost" style={{ display: 'block', margin: '0 auto 20px', height: '44px', objectFit: 'contain' }} />

              {/* ── Sign in / Sign up form ── */}
              {signupStep === 'form' && (<>
                <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '10px', padding: '4px', marginBottom: '24px' }}>
                  <button onClick={() => { setIsSignUp(false); setMessage(''); }} style={{ flex: 1, padding: '9px', border: 'none', borderRadius: '7px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', background: !isSignUp ? 'white' : 'transparent', color: !isSignUp ? '#4F46E5' : '#6b7280', boxShadow: !isSignUp ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>Sign in</button>
                  <button onClick={() => { setIsSignUp(true); setMessage(''); }} style={{ flex: 1, padding: '9px', border: 'none', borderRadius: '7px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', background: isSignUp ? 'white' : 'transparent', color: isSignUp ? '#4F46E5' : '#6b7280', boxShadow: isSignUp ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>Create account</button>
                </div>
                <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '10px', fontSize: '15px', boxSizing: 'border-box' }} />
                <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (isSignUp ? handleSignUp() : handleLogin())}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '10px', fontSize: '15px', boxSizing: 'border-box' }} />
                {isSignUp && (
                  <input type="text" placeholder="Beta access code" value={betaCode} onChange={e => setBetaCode(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '16px', fontSize: '15px', boxSizing: 'border-box', letterSpacing: '0.05em' }} />
                )}
                {!isSignUp && <div style={{ marginBottom: '16px' }} />}
                <button onClick={isSignUp ? handleSignUp : handleLogin} disabled={authLoading}
                  style={{ width: '100%', padding: '13px', background: '#4F46E5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: authLoading ? 'not-allowed' : 'pointer', opacity: authLoading ? 0.7 : 1 }}>
                  {authLoading ? 'Please wait…' : isSignUp ? 'Next →' : 'Sign in'}
                </button>
                {!isSignUp && (
                  <button onClick={handleForgotPassword} style={{ width: '100%', marginTop: '10px', background: 'none', border: 'none', color: '#6b7280', fontSize: '13px', cursor: 'pointer' }}>Forgot password?</button>
                )}
                {message && <p style={{ textAlign: 'center', color: 'red', fontSize: '13px', margin: '12px 0 0' }}>{message}</p>}
                <button onClick={() => { setShowLogin(false); setSignupStep('form'); }} style={{ width: '100%', marginTop: '10px', background: 'none', border: 'none', color: '#bbb', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
              </>)}

              {/* ── Trivia question ── */}
              {signupStep === 'trivia' && (<>
                <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '28px' }}>🚀</div>
                <h2 style={{ textAlign: 'center', margin: '0 0 8px', fontSize: '20px', fontWeight: '700', color: '#111' }}>One quick question</h2>
                <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px', margin: '0 0 20px', lineHeight: '1.5' }}>Answer correctly and we'll double your post quota.</p>
                <div style={{ background: '#f8f7ff', border: '1px solid #e0ddff', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#4F46E5', textAlign: 'center', lineHeight: '1.6' }}>
                    "The post that started it all had a limit.<br />How many characters was it?"
                  </p>
                </div>
                <input type="text" placeholder="Your answer..." value={triviaAnswer} onChange={e => setTriviaAnswer(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleTriviaAnswer(false)}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '12px', fontSize: '15px', boxSizing: 'border-box', textAlign: 'center' }} />
                <button onClick={() => handleTriviaAnswer(false)} disabled={authLoading}
                  style={{ width: '100%', padding: '13px', background: '#4F46E5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: authLoading ? 'not-allowed' : 'pointer', opacity: authLoading ? 0.7 : 1, marginBottom: '8px' }}>
                  {authLoading ? 'Creating account…' : 'Submit answer'}
                </button>
                <button onClick={() => handleTriviaAnswer(true)} disabled={authLoading}
                  style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: '#9ca3af', fontSize: '13px', cursor: 'pointer' }}>
                  I don't know — just get started
                </button>
              </>)}

              {/* ── Done — check email ── */}
              {signupStep === 'done' && (<>
                <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '36px' }}>{triviaCorrect ? '🎉' : '🚀'}</div>
                <h2 style={{ textAlign: 'center', margin: '0 0 10px', fontSize: '20px', fontWeight: '700', color: '#111' }}>
                  {triviaCorrect ? 'You got it!' : 'Almost there!'}
                </h2>
                {triviaCorrect ? (
                  <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px', margin: '0 0 16px', lineHeight: '1.6' }}>
                    Twitter's original limit was <strong>140</strong> characters. We've doubled your quota to <strong>280 launches</strong>.
                  </p>
                ) : (
                  <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px', margin: '0 0 16px', lineHeight: '1.6' }}>
                    The answer was <strong>140</strong> — Twitter's original character limit. You're loaded up with <strong>140 launches</strong>.
                  </p>
                )}
                <div style={{ background: triviaCorrect ? '#f0fdf4' : '#f8f7ff', border: `1px solid ${triviaCorrect ? '#bbf7d0' : '#e0ddff'}`, borderRadius: '12px', padding: '14px', marginBottom: '20px', textAlign: 'center' }}>
                  <span style={{ fontSize: '17px', fontWeight: '700', color: triviaCorrect ? '#16a34a' : '#4F46E5' }}>
                    {triviaCorrect ? '280 launches unlocked' : '140 launches ready'}
                  </span>
                </div>
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#92400e', lineHeight: '1.6' }}>
                    <strong>📬 Next step:</strong> Check your inbox and confirm your email to activate your account.
                  </p>
                </div>
                <button onClick={() => { setShowLogin(false); setSignupStep('form'); setTriviaAnswer(''); }} style={{ width: '100%', marginTop: '16px', background: 'none', border: 'none', color: '#bbb', fontSize: '13px', cursor: 'pointer' }}>Close</button>
              </>)}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Main app ──────────────────────────────────────
  return (
    <div className="app-wrapper">
      <div className="app-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/Blinkapost logo.png" alt="Blinkapost" style={{ height: '32px', objectFit: 'contain' }} />
          </div>
          <button onClick={() => setShowMenu(true)} style={{ background: 'linear-gradient(135deg, #4F46E5, #7c3aed)', border: 'none', padding: '8px 14px', borderRadius: '10px', cursor: 'pointer', color: 'white', fontSize: '13px', fontWeight: '700', lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', flexShrink: 0, boxShadow: '0 2px 8px rgba(79,70,229,0.3)' }}>
            ☰ Logistics Menu
          </button>
        </div>
      </div>

      {voiceToast && <div className="voice-toast">{voiceToast}</div>}
      {(linkedinPosting || facebookPosting || instagramPosting || tiktokPosting || postingAll) && (
        <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #4F46E5, #7c3aed)', color: 'white', borderRadius: '16px', padding: '16px 28px', boxShadow: '0 8px 32px rgba(79,70,229,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', gap: '14px', minWidth: '280px', maxWidth: '90vw' }}>
          <div style={{ width: '20px', height: '20px', border: '3px solid rgba(255,255,255,0.3)', borderTop: '3px solid white', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          <span style={{ fontSize: '15px', fontWeight: '600', lineHeight: '1.4' }}>{POSTING_MSGS[postingMsgIdx]}</span>
        </div>
      )}

      <div className="tab-bar">
        <button className={`tab-btn tab-btn-create${activeTab === 'create' ? ' active' : ''}`} onClick={() => setActiveTab('create')}>✨ Create</button>
        <button className={`tab-btn tab-btn-schedule${activeTab === 'schedule' ? ' active' : ''}`} onClick={() => setActiveTab('schedule')}>
          📅 Schedule {scheduledPosts.length > 0 && <span className="tab-badge">{scheduledPosts.length}</span>}
        </button>
        <button className={`tab-btn tab-btn-mission${activeTab === 'mission' ? ' active' : ''}`} onClick={() => {
          setActiveTab('mission'); loadMissions();
          const seen = localStorage.getItem(`mission_walkthrough_${user?.id}`);
          if (!seen) { setShowMissionWalkthrough(true); setMissionWalkthroughSlide(0); }
        }} style={{ background: activeTab === 'mission' ? 'linear-gradient(135deg, #0f0c29, #302b63)' : '', color: activeTab === 'mission' ? 'white' : '' }}>
          🚀 Mission Control {missions.filter(m => m.status === 'in_orbit').length > 0 && <span className="tab-badge" style={{ background: '#f97316' }}>{missions.filter(m => m.status === 'in_orbit').length}</span>}
        </button>
      </div>

      <div className="app-content">

        {/* ── ONBOARDING ── */}
        {showOnboarding && (() => {
          const OB_PLATFORMS = [
            { key: 'linkedin',  label: 'LinkedIn',  color: '#0A66C2', connectFn: () => connectLinkedin('onboarding'),
              tagline: 'Reach professionals & grow your network',
              hint: 'Log in to LinkedIn → tap Allow → you\'ll be brought straight back',
              d: 'M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.46 12.92,11.24V10.13H10.13V18.5H12.92V13.57C12.92,12.8 13.54,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,5.95 7.81,5.19 6.88,5.19A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.95,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z' },
            { key: 'facebook',  label: 'Facebook',  color: '#1877F2', connectFn: () => connectFacebook('onboarding'),
              tagline: 'Post to your pages & business accounts',
              hint: 'Log in to Facebook → choose your pages → you\'ll be brought straight back',
              d: 'M17,2V2H17V6H15C14.31,6 14,6.81 14,7.5V10H14L17,10V14H14V22H10V14H7V10H10V6A4,4 0 0,1 14,2H17Z' },
            { key: 'instagram', label: 'Instagram', color: '#E1306C', connectFn: () => connectInstagram('onboarding'),
              tagline: 'Share photos, reels & stories',
              hint: 'Log in to Instagram → tap Allow → you\'ll be brought straight back',
              d: 'M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z' },
            { key: 'tiktok',    label: 'TikTok',    color: '#010101', connectFn: () => connectTikTok('onboarding'),
              tagline: 'Publish videos & photos to TikTok',
              hint: 'Log in to TikTok → tap Authorise → you\'ll be brought straight back',
              d: 'M19.59,6.69A4.83,4.83 0 0,1 15.82,2.5H12.37V15.67A2.89,2.89 0 0,1 9.49,18.17C7.9,18.17 6.62,16.92 6.62,15.33C6.62,13.47 8.32,12.05 10.18,12.49V8.97A6.33,6.33 0 0,0 3.17,15.33C3.17,18.9 6.09,21.83 9.66,21.83C13.22,21.83 16.14,18.9 16.14,15.33V8.85A8.17,8.17 0 0,0 20.83,10.34V6.88A4.85,4.85 0 0,1 19.59,6.69Z' },
          ];

          const finishOnboarding = () => {
            localStorage.setItem(`blinkapost_onboarded_${user.id}`, 'true');
            localStorage.removeItem('onboardingStep');
            setShowOnboarding(false);
            setOnboardingStep(0);
            setTutorialSlide(0);
            setShowTermsConsent(false);
            setStep(1);
          };

          const advanceOnboarding = () => {
            setOnboardingJustConnected(false);
            if (onboardingStep >= OB_PLATFORMS.length) { finishOnboarding(); return; }
            setOnboardingStep(s => s + 1);
          };

          // Language selection — very first onboarding step
          if (showObLanguage) {
            const filteredLangs = obLangSearch
              ? LANGUAGES.filter(l => l.name.toLowerCase().includes(obLangSearch.toLowerCase()))
              : LANGUAGES;
            const priorityLangs = filteredLangs.filter(l => l.priority);
            const otherLangs    = filteredLangs.filter(l => !l.priority);
            return (
              <div style={{ minHeight: '100vh', background: 'linear-gradient(145deg, #0f0c29 0%, #302b63 60%, #24243e 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '48px 24px 36px' }}>
                <img src="/Blinkapost logo transparent.png" alt="Blinkapost" style={{ height: '52px', objectFit: 'contain' }} />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '360px', paddingTop: '24px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>🌍</div>
                  <h2 style={{ color: 'white', fontSize: '22px', fontWeight: '800', margin: '0 0 8px', textAlign: 'center' }}>What language do you post in?</h2>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: '0 0 20px', textAlign: 'center', lineHeight: 1.6 }}>Your AI posts will be written in this language. Change it any time in Settings.</p>

                  <input
                    type="text"
                    placeholder="Search languages..."
                    value={obLangSearch}
                    onChange={e => setObLangSearch(e.target.value)}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.1)', color: 'white', fontSize: '14px', marginBottom: '10px', outline: 'none', boxSizing: 'border-box' }}
                  />

                  <div style={{ width: '100%', overflowY: 'auto', maxHeight: '42vh', borderRadius: '12px', background: 'rgba(255,255,255,0.06)' }}>
                    {priorityLangs.map(lang => (
                      <button key={lang.name} onClick={() => setObLanguage(lang.name)}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 14px', background: obLanguage === lang.name ? 'rgba(79,70,229,0.35)' : 'transparent', border: 'none', borderLeft: obLanguage === lang.name ? '3px solid #818cf8' : '3px solid transparent', cursor: 'pointer', textAlign: 'left' }}>
                        <span style={{ fontSize: '20px' }}>{lang.flag}</span>
                        <span style={{ flex: 1, fontSize: '14px', fontWeight: obLanguage === lang.name ? '700' : '400', color: 'white' }}>{lang.name}</span>
                        {obLanguage === lang.name && <span style={{ color: '#818cf8', fontSize: '16px' }}>✓</span>}
                      </button>
                    ))}
                    {!obLangSearch && priorityLangs.length > 0 && otherLangs.length > 0 && (
                      <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '4px 14px' }} />
                    )}
                    {otherLangs.map(lang => (
                      <button key={lang.name} onClick={() => setObLanguage(lang.name)}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 14px', background: obLanguage === lang.name ? 'rgba(79,70,229,0.35)' : 'transparent', border: 'none', borderLeft: obLanguage === lang.name ? '3px solid #818cf8' : '3px solid transparent', cursor: 'pointer', textAlign: 'left' }}>
                        <span style={{ fontSize: '20px' }}>{lang.flag}</span>
                        <span style={{ flex: 1, fontSize: '14px', fontWeight: obLanguage === lang.name ? '700' : '400', color: 'white' }}>{lang.name}</span>
                        {obLanguage === lang.name && <span style={{ color: '#818cf8', fontSize: '16px' }}>✓</span>}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ width: '100%', maxWidth: '360px' }}>
                  <button onClick={async () => {
                    setUserLanguage(obLanguage);
                    localStorage.setItem(`blinkapost_lang_${user.id}`, obLanguage);
                    await saveUserSettings({ language: obLanguage });
                    setShowObLanguage(false);
                  }} style={{ width: '100%', padding: '15px', background: 'linear-gradient(135deg, #4F46E5, #7c3aed)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', marginBottom: '12px' }}>
                    Continue →
                  </button>
                  <button onClick={() => {
                    localStorage.setItem(`blinkapost_lang_${user.id}`, 'English');
                    setShowObLanguage(false);
                  }} style={{ width: '100%', padding: '10px', background: 'none', color: 'rgba(255,255,255,0.35)', border: 'none', fontSize: '13px', cursor: 'pointer' }}>
                    Skip for now
                  </button>
                </div>
              </div>
            );
          }

          // Terms consent screen (must be before tutorial check)
          if (showTermsConsent) return (
            <div style={{ minHeight: '100vh', background: 'linear-gradient(145deg, #0f0c29 0%, #302b63 60%, #24243e 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '48px 24px 36px' }}>
              <img src="/Blinkapost logo transparent.png" alt="Blinkapost" style={{ height: '52px', objectFit: 'contain' }} />

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '360px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                <h2 style={{ color: 'white', fontSize: '22px', fontWeight: '800', margin: '0 0 8px', textAlign: 'center' }}>Before you continue</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: '0 0 24px', textAlign: 'center', lineHeight: 1.6 }}>Blinkapost is used to publish content to real audiences. Please agree to post responsibly.</p>

                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '14px', padding: '16px 18px', width: '100%', marginBottom: '20px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 12px' }}>You agree NOT to post</p>
                  {[
                    ['🚫', 'Hate speech or discrimination'],
                    ['🚫', 'Harassment or threats'],
                    ['🔞', 'Sexually explicit or adult content'],
                    ['⚠️', 'Violent or graphic content'],
                    ['🚨', 'Terrorist or extremist content'],
                    ['❌', 'Spam or misleading information'],
                    ['⚖️', 'Illegal content of any kind'],
                  ].map(([icon, text]) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', width: '20px', textAlign: 'center' }}>{icon}</span>
                      <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>{text}</span>
                    </div>
                  ))}
                </div>

                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', textAlign: 'center', lineHeight: 1.6, margin: 0 }}>
                  By tapping agree you accept our{' '}
                  <a href="/terms" target="_blank" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'underline' }}>Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'underline' }}>Privacy Policy</a>
                </p>
              </div>

              <div style={{ width: '100%', maxWidth: '360px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={() => { localStorage.setItem(`blinkapost_terms_${user.id}`, Date.now()); setShowTermsConsent(false); setOnboardingStep(1); }}
                  style={{ width: '100%', padding: '17px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: '800', cursor: 'pointer' }}
                >
                  I agree — let's go →
                </button>
                <button onClick={() => setShowTermsConsent(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '13px', cursor: 'pointer' }}>
                  ← Back
                </button>
              </div>
            </div>
          );

          // Welcome / Tutorial carousel
          if (onboardingStep === 0) {
            const tutorialSlides = [
              {
                icon: '📸',
                color: '#6366f1',
                glow: '#6366f155',
                step: '01',
                title: 'Upload Your Media',
                desc: 'Drop in a photo or video from your phone or computer. Blinkapost supports images, MP4s, MOVs and more.',
                detail: 'Your logo is optional — add it to brand every post instantly.',
              },
              {
                icon: '✏️',
                color: '#8b5cf6',
                glow: '#8b5cf655',
                step: '02',
                title: 'Edit & Describe',
                desc: 'Crop, rotate, and filter your image — or speak your context using the mic so AI knows what the post is about.',
                detail: 'The more context you give, the smarter the captions.',
              },
              {
                icon: '🤖',
                color: '#a855f7',
                glow: '#a855f755',
                step: '03',
                title: 'AI Generates Your Posts',
                desc: 'Hit Generate and Claude AI writes tailored captions for Instagram, LinkedIn, Facebook, TikTok and Google.',
                detail: 'Each platform gets a unique caption styled for its audience.',
              },
              {
                icon: '🚀',
                color: '#ec4899',
                glow: '#ec489955',
                step: '04',
                title: 'Post Everywhere',
                desc: 'Edit any caption inline, then tap Post on each platform — or Post All to share everywhere at once.',
                detail: 'Connect your accounts once and post directly from the app.',
              },
            ];
            const slide = tutorialSlides[tutorialSlide];
            return (
              <div style={{ minHeight: '100vh', background: 'linear-gradient(145deg, #0f0c29 0%, #302b63 60%, #24243e 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '48px 24px 36px', overflow: 'hidden' }}>
                {/* Logo */}
                <img src="/Blinkapost logo transparent.png" alt="Blinkapost" style={{ height: '52px', objectFit: 'contain' }} />

                {/* Slide area */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '360px', textAlign: 'center' }}>
                  {/* Step badge */}
                  <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.15em', color: slide.color, textTransform: 'uppercase', marginBottom: '20px', transition: 'all 0.35s' }}>
                    STEP {slide.step}
                  </div>

                  {/* Icon circle */}
                  <div key={tutorialSlide} style={{ width: '110px', height: '110px', borderRadius: '32px', background: `linear-gradient(135deg, ${slide.color}33, ${slide.color}11)`, border: `2px solid ${slide.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '52px', marginBottom: '28px', boxShadow: `0 0 60px ${slide.glow}`, animation: 'tutSlideIn 0.35s cubic-bezier(0.34,1.56,0.64,1)' }}>
                    {slide.icon}
                  </div>

                  {/* Title */}
                  <h2 style={{ color: 'white', fontSize: '26px', fontWeight: '800', margin: '0 0 14px', lineHeight: 1.2, transition: 'opacity 0.3s' }}>
                    {slide.title}
                  </h2>

                  {/* Description */}
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '15px', lineHeight: 1.7, margin: '0 0 10px', maxWidth: '300px' }}>
                    {slide.desc}
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px', lineHeight: 1.6, margin: 0, maxWidth: '280px' }}>
                    {slide.detail}
                  </p>
                </div>

                {/* Bottom controls */}
                <div style={{ width: '100%', maxWidth: '360px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                  {/* Nav arrows + dots */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                      onClick={() => setTutorialSlide(s => Math.max(0, s - 1))}
                      disabled={tutorialSlide === 0}
                      style={{ width: '40px', height: '40px', borderRadius: '50%', background: tutorialSlide === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.12)', border: 'none', color: tutorialSlide === 0 ? 'rgba(255,255,255,0.2)' : 'white', fontSize: '18px', cursor: tutorialSlide === 0 ? 'default' : 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >‹</button>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {tutorialSlides.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setTutorialSlide(i)}
                          style={{ width: i === tutorialSlide ? '24px' : '8px', height: '8px', borderRadius: '999px', background: i === tutorialSlide ? 'white' : 'rgba(255,255,255,0.2)', border: 'none', padding: 0, cursor: 'pointer', transition: 'all 0.3s' }}
                        />
                      ))}
                    </div>

                    <button
                      onClick={() => {
                        if (tutorialSlide < tutorialSlides.length - 1) setTutorialSlide(s => s + 1);
                        else setShowTermsConsent(true);
                      }}
                      style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white', fontSize: '18px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >›</button>
                  </div>

                  {/* Primary CTA */}
                  {tutorialSlide === tutorialSlides.length - 1 ? (
                    <button
                      onClick={() => setShowTermsConsent(true)}
                      style={{ width: '100%', padding: '17px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: '800', cursor: 'pointer' }}
                    >
                      Let's connect your accounts →
                    </button>
                  ) : (
                    <button
                      onClick={() => setTutorialSlide(s => s + 1)}
                      style={{ width: '100%', padding: '17px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: '800', cursor: 'pointer' }}
                    >
                      Next →
                    </button>
                  )}

                  <button onClick={finishOnboarding} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '13px', cursor: 'pointer' }}>
                    Skip, I'll set up later
                  </button>
                </div>
              </div>
            );
          }

          // Terms consent screen
          if (showTermsConsent) return (
            <div style={{ minHeight: '100vh', background: 'linear-gradient(145deg, #0f0c29 0%, #302b63 60%, #24243e 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '48px 24px 36px' }}>
              <img src="/Blinkapost logo transparent.png" alt="Blinkapost" style={{ height: '52px', objectFit: 'contain' }} />

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '360px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                <h2 style={{ color: 'white', fontSize: '22px', fontWeight: '800', margin: '0 0 8px', textAlign: 'center' }}>Before you continue</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: '0 0 24px', textAlign: 'center', lineHeight: 1.6 }}>Blinkapost is used to publish content to real audiences. Please agree to post responsibly.</p>

                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '14px', padding: '16px 18px', width: '100%', marginBottom: '20px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 12px' }}>You agree NOT to post</p>
                  {[
                    ['🚫', 'Hate speech or discrimination'],
                    ['🚫', 'Harassment or threats'],
                    ['🔞', 'Sexually explicit or adult content'],
                    ['⚠️', 'Violent or graphic content'],
                    ['🚨', 'Terrorist or extremist content'],
                    ['❌', 'Spam or misleading information'],
                    ['⚖️', 'Illegal content of any kind'],
                  ].map(([icon, text]) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', width: '20px', textAlign: 'center' }}>{icon}</span>
                      <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>{text}</span>
                    </div>
                  ))}
                </div>

                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', textAlign: 'center', lineHeight: 1.6, margin: 0 }}>
                  By tapping agree you accept our{' '}
                  <a href="/terms" target="_blank" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'underline' }}>Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'underline' }}>Privacy Policy</a>
                </p>
              </div>

              <div style={{ width: '100%', maxWidth: '360px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={() => { localStorage.setItem(`blinkapost_terms_${user.id}`, Date.now()); setShowTermsConsent(false); setOnboardingStep(1); }}
                  style={{ width: '100%', padding: '17px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: '800', cursor: 'pointer' }}
                >
                  I agree — let's go →
                </button>
                <button onClick={() => setShowTermsConsent(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '13px', cursor: 'pointer' }}>
                  ← Back
                </button>
              </div>
            </div>
          );

          // Done screen
          if (onboardingStep > OB_PLATFORMS.length) return (
            <div style={{ minHeight: '100vh', background: 'linear-gradient(145deg, #0f0c29 0%, #302b63 60%, #24243e 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎉</div>
              <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '800', margin: '0 0 12px' }}>You're all set!</h1>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', margin: '0 0 32px', maxWidth: '300px', lineHeight: 1.7 }}>Your accounts are connected. Time to create your first post!</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '32px' }}>
                {OB_PLATFORMS.map(p => {
                  const connected = getAccountsForPlatform(p.key).length > 0;
                  if (!connected) return null;
                  const acc = getSelectedAccount(p.key);
                  return (
                    <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.07)', borderRadius: '10px', padding: '10px 16px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24"><path fill={p.color} d={p.d} /></svg>
                      <span style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>{acc?.page_name || p.label}</span>
                      <span style={{ marginLeft: 'auto', color: '#86efac', fontSize: '13px' }}>✓</span>
                    </div>
                  );
                })}
              </div>
              <button onClick={finishOnboarding} style={{ width: '100%', maxWidth: '360px', padding: '17px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: '800', cursor: 'pointer' }}>
                Create my first post →
              </button>
            </div>
          );

          // Single connect-all screen — all platforms visible at once
          const anyConnected = connectedAccounts.length > 0;
          return (
            <div style={{ minHeight: '100vh', background: 'linear-gradient(145deg, #0f0c29 0%, #302b63 60%, #24243e 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '28px 20px 40px', overflowY: 'auto' }}>
              <img src="/Blinkapost logo transparent.png" alt="Blinkapost" style={{ height: '40px', objectFit: 'contain', marginBottom: '20px', flexShrink: 0 }} />

              <div style={{ width: '100%', maxWidth: '400px' }}>
                <h2 style={{ color: 'white', fontSize: '21px', fontWeight: '800', margin: '0 0 6px', textAlign: 'center' }}>Connect your accounts</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', margin: '0 0 18px', textAlign: 'center', lineHeight: 1.5 }}>
                  Connect at least one to start posting. Add more any time.
                </p>

                {onboardingJustConnected && (
                  <div style={{ background: 'rgba(134,239,172,0.1)', border: '1px solid rgba(134,239,172,0.3)', borderRadius: '10px', padding: '8px 14px', marginBottom: '12px', textAlign: 'center' }}>
                    <span style={{ color: '#86efac', fontSize: '13px', fontWeight: '700' }}>Connected! Keep going or tap Continue below.</span>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {OB_PLATFORMS.map(p => {
                    const accs = getAccountsForPlatform(p.key);
                    const connected = accs.length > 0;
                    const acc = getSelectedAccount(p.key);
                    return (
                      <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: connected ? 'rgba(134,239,172,0.07)' : 'rgba(255,255,255,0.05)', border: `1px solid ${connected ? 'rgba(134,239,172,0.25)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '14px', padding: '12px 14px', transition: 'all 0.3s' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '11px', background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="21" height="21" viewBox="0 0 24 24"><path fill="white" d={p.d} /></svg>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ color: 'white', fontWeight: '700', fontSize: '14px', margin: 0 }}>{p.label}</p>
                          {connected
                            ? <p style={{ color: '#86efac', fontSize: '11px', margin: '1px 0 0', fontWeight: '600' }}>✓ {p.key === 'instagram' ? `@${acc?.page_name}` : acc?.page_name || `${accs.length} connected`}</p>
                            : <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', margin: '1px 0 0' }}>{p.tagline}</p>
                          }
                        </div>
                        {connected
                          ? <button onClick={p.connectFn} style={{ padding: '6px 11px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: 'rgba(255,255,255,0.45)', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>+ Add</button>
                          : <button onClick={p.connectFn} style={{ padding: '8px 14px', background: p.color, border: 'none', borderRadius: '9px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>Connect</button>
                        }
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button
                    onClick={() => setOnboardingStep(OB_PLATFORMS.length + 1)}
                    style={{ width: '100%', padding: '15px', background: anyConnected ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255,255,255,0.08)', color: 'white', border: 'none', borderRadius: '13px', fontSize: '15px', fontWeight: '800', cursor: 'pointer' }}
                  >
                    {anyConnected ? 'Continue →' : 'Skip for now →'}
                  </button>
                  <button onClick={finishOnboarding} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.22)', fontSize: '12px', cursor: 'pointer', padding: '4px' }}>
                    I'll connect accounts later
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── CREATE TAB ── */}
        {!showOnboarding && activeTab === 'create' && (
          <>
            {/* Low-launches warning */}
            {betaLimit !== null && (betaLimit - betaUsed) <= 5 && (betaLimit - betaUsed) > 0 && (
              <div style={{ margin: '12px 16px 0', background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', border: '1px solid #fbbf24', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '22px', flexShrink: 0 }}>🚀</span>
                <div>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#92400e' }}>
                    Only {betaLimit - betaUsed} launch{(betaLimit - betaUsed) === 1 ? '' : 'es'} left!
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#b45309' }}>
                    Head to <strong>Logistics Menu → Subscription</strong> to top up and keep posting.
                  </p>
                </div>
              </div>
            )}
            {betaLimit !== null && betaUsed >= betaLimit && (
              <div style={{ margin: '12px 16px 0', background: 'linear-gradient(135deg, #fef2f2, #fee2e2)', border: '1px solid #fca5a5', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '22px', flexShrink: 0 }}>⛽</span>
                <div>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#991b1b' }}>You're out of launches</p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#b91c1c' }}>
                    Go to <strong>Logistics Menu → Subscription</strong> to refuel and start posting again.
                  </p>
                </div>
              </div>
            )}

            {/* Step indicator */}
            <div className="step-indicator">
              {[1,2,3,4].map((s, i) => {
                const completed = (s === 1 && (!!image || !!video)) || (s === 2 && step > 2 && (!!image || !!video)) || (s === 3 && !!postContext) || (s === 4 && !!posts);
                const active = step === s;
                const clickable = s < step;

                const dotContent = () => {
                  if (completed && s === 1 && imagePreview) return <img src={imagePreview} alt="img" className="step-preview-img" />;
                  if (completed && s === 1 && videoPreview) return <span style={{ fontSize: '18px' }}>🎬</span>;
                  if (completed) return '✓';
                  if (s === 3) return <span style={{ fontSize: '16px' }}>✨</span>;
                  return s;
                };

                const subLabel = () => {
                  if (!completed) return null;
                  if (s === 1 && (image || video)) return <span className="step-sublabel">ready</span>;
                  if (s === 2 && step > 2) return <span className="step-sublabel">done</span>;
                  if (s === 3 && postContext) return <span className="step-sublabel">{postContext.slice(0, 12)}…</span>;
                  return null;
                };

                return (
                  <React.Fragment key={s}>
                    <div className="step-item" onClick={() => clickable && setStep(s)} style={{ cursor: clickable ? 'pointer' : 'default' }}>
                      <div className={`step-dot ${completed ? 'completed' : active ? 'active' : ''} ${completed && s === 1 ? 'has-preview' : ''}`}>
                        {dotContent()}
                      </div>
                      <span className={`step-label ${active ? 'active' : ''}`}>{STEP_LABELS[i]}</span>
                      {subLabel()}
                    </div>
                    {s < 4 && <div className={`step-line ${completed ? 'completed' : ''}`} />}
                  </React.Fragment>
                );
              })}
            </div>

            {/* ── STEP 1: Media ── */}
            {step === 1 && (
              <div className="wizard-card">
                <div className="wizard-card-header">
                  <span className="wizard-step-num">1</span>
                  <div>
                    <h2 className="wizard-title">Add your media</h2>
                    <p className="wizard-sub">Upload a photo or video, or take one now</p>
                  </div>
                </div>

                <div className="image-upload-area"
                  onClick={() => !imagePreview && !videoPreview && document.getElementById('mediaUploadWizard').click()}
                  style={{ cursor: imagePreview || videoPreview ? 'default' : 'pointer' }}>
                  {imagePreview ? (
                    <img src={imagePreview} alt="preview" className="image-preview" />
                  ) : videoPreview ? (() => {
                    const ratioMap = { '16:9': '16/9', '9:16': '9/16', '4:3': '4/3' };
                    const cssRatio = ratioMap[videoRatio];
                    return cssRatio ? (
                      <div style={{ width: '100%', aspectRatio: cssRatio, overflow: 'hidden', borderRadius: '8px', background: '#000' }}>
                        <video src={videoPreview} controls style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ) : (
                      <video src={videoPreview} controls style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', display: 'block' }} />
                    );
                  })() : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
                      <p style={{ fontSize: '48px', margin: 0 }}>📸</p>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                          onClick={e => { e.stopPropagation(); document.getElementById('mediaUploadWizard').click(); }}
                          style={{ padding: '10px 20px', background: '#4F46E5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                          Upload file
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); openCamera('photo'); }}
                          style={{ padding: '10px 20px', background: 'white', color: '#4F46E5', border: '2px solid #4F46E5', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                          📷 Use camera
                        </button>
                      </div>
                      <p style={{ color: '#aaa', fontSize: '13px', margin: 0 }}>MP4, MOV, JPG, PNG</p>
                    </div>
                  )}
                  <input id="mediaUploadWizard" type="file" accept="image/*,video/mp4,video/quicktime,video/webm,video/*" style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.type.startsWith('video/')) handleVideoSelect(file);
                      else handleImageSelect(file);
                    }} />
                </div>

                {videoPreview && (
                  <>
                    <p style={{ fontSize: '12px', color: '#888', margin: '14px 0 8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aspect ratio</p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {[
                        { key: 'original', label: 'Original', hint: 'As uploaded' },
                        { key: '16:9',     label: '16:9',     hint: 'YouTube · LinkedIn' },
                        { key: '9:16',     label: '9:16',     hint: 'TikTok · Reels' },
                        { key: '4:3',      label: '4:3',      hint: 'Facebook · General' },
                      ].map(({ key, label, hint }) => (
                        <button key={key} onClick={() => setVideoRatio(key)}
                          style={{ padding: '8px 12px', borderRadius: '8px', border: `2px solid ${videoRatio === key ? '#4F46E5' : '#ddd'}`, background: videoRatio === key ? '#EEF2FF' : 'white', cursor: 'pointer', textAlign: 'left', minWidth: '90px' }}>
                          <div style={{ fontWeight: '600', fontSize: '13px', color: videoRatio === key ? '#4F46E5' : '#333' }}>{label}</div>
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>{hint}</div>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {(imagePreview || videoPreview) && (
                  <button onClick={() => document.getElementById('mediaUploadWizard').click()}
                    style={{ marginTop: '12px', background: 'none', border: 'none', color: '#888', fontSize: '13px', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                    Change media
                  </button>
                )}

                {(imagePreview || videoPreview) && (
                  <button onClick={() => setStep(2)}
                    style={{ width: '100%', marginTop: '16px', padding: '14px', background: '#4F46E5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
                    Next — Edit &amp; add logo →
                  </button>
                )}
              </div>
            )}

            {/* ── STEP 2: Edit & Logo ── */}
            {step === 2 && (
              <div className="wizard-card">
                <button className="back-btn" onClick={() => setStep(1)}>← Back</button>
                <div className="wizard-card-header">
                  <span className="wizard-step-num">2</span>
                  <div>
                    <h2 className="wizard-title">Edit &amp; add logo</h2>
                    <p className="wizard-sub">Adjust your photo, or skip straight to generating</p>
                  </div>
                </div>

                {/* Media preview */}
                {imagePreview && (
                  <img src={imagePreview} alt="preview" className="image-preview" style={{ marginBottom: '8px' }} />
                )}
                {videoPreview && (
                  <video ref={trimVideoRef} src={videoPreview} controls
                    style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: '8px', display: 'block', marginBottom: '8px' }}
                    onLoadedMetadata={e => {
                      const d = e.target.duration;
                      setVideoDuration(d);
                      setVideoTrimStart(0);
                      setVideoTrimEnd(d);
                    }} />
                )}

                {/* Retake / change media row */}
                {(imagePreview || videoPreview) && (
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    <button onClick={() => openCamera(mediaType === 'video' ? 'video' : 'photo')}
                      style={{ background: 'none', border: '1.5px solid #4F46E5', color: '#4F46E5', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                      📷 Retake
                    </button>
                    <button onClick={() => document.getElementById('mediaUploadWizard2').click()}
                      style={{ background: 'none', border: '1.5px solid #F97316', color: '#F97316', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                      Upload different file
                    </button>
                    {imagePreview && (
                      <button onClick={() => {
                        const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
                        if (isIOS) {
                          window.open(imagePreview, '_blank');
                          showToast('Long-press the image and tap "Save to Photos"');
                        } else {
                          const a = document.createElement('a');
                          a.href = imagePreview;
                          a.download = 'blinkapost-image.jpg';
                          a.click();
                        }
                      }} style={{ background: 'none', border: '1.5px solid #10b981', color: '#10b981', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                        Save to gallery
                      </button>
                    )}
                    <input id="mediaUploadWizard2" type="file" accept="image/*,video/mp4,video/quicktime,video/webm,video/*" style={{ display: 'none' }}
                      onChange={e => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (file.type.startsWith('video/')) handleVideoSelect(file);
                        else handleImageSelect(file);
                        e.target.value = '';
                      }} />
                  </div>
                )}

                {/* Video trim */}
                {videoPreview && videoDuration > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#888', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trim</p>
                      <span style={{ fontSize: '12px', color: '#4F46E5', fontWeight: '600' }}>
                        {formatTime(videoTrimStart)} → {formatTime(videoTrimEnd)} &nbsp;·&nbsp; {formatTime(videoTrimEnd - videoTrimStart)}
                      </span>
                    </div>

                    {/* Visual range bar */}
                    <div style={{ position: 'relative', height: '6px', background: '#e5e7eb', borderRadius: '3px', margin: '0 0 12px' }}>
                      <div style={{
                        position: 'absolute',
                        left: `${(videoTrimStart / videoDuration) * 100}%`,
                        width: `${((videoTrimEnd - videoTrimStart) / videoDuration) * 100}%`,
                        height: '100%', background: '#4F46E5', borderRadius: '3px'
                      }} />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { label: 'Start', value: videoTrimStart, min: 0, max: videoTrimEnd - 0.5, set: v => setVideoTrimStart(parseFloat(v)) },
                        { label: 'End',   value: videoTrimEnd,   min: videoTrimStart + 0.5, max: videoDuration, set: v => setVideoTrimEnd(parseFloat(v)) },
                      ].map(({ label, value, min, max, set }) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '12px', color: '#888', width: '32px', flexShrink: 0 }}>{label}</span>
                          <input type="range" min={min} max={max} step={0.1} value={value}
                            onChange={e => set(e.target.value)}
                            style={{ flex: 1, accentColor: '#4F46E5', cursor: 'pointer' }} />
                          <span style={{ fontSize: '12px', color: '#4F46E5', fontWeight: '600', width: '34px', textAlign: 'right', flexShrink: 0 }}>{formatTime(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video crop — aspect ratio selector */}
                {videoPreview && (
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#888', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Crop ratio</p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {[
                        { key: 'original', label: 'Original', hint: 'As recorded' },
                        { key: '1:1',  label: '1:1',  hint: 'Square' },
                        { key: '4:3',  label: '4:3',  hint: 'Facebook · General' },
                        { key: '9:16', label: '9:16', hint: 'TikTok · Reels' },
                        { key: '16:9', label: '16:9', hint: 'YouTube · LinkedIn' },
                      ].map(({ key, label, hint }) => (
                        <button key={key} onClick={() => setVideoRatio(key)}
                          style={{ padding: '7px 12px', borderRadius: '8px', border: `2px solid ${videoRatio === key ? '#4F46E5' : '#ddd'}`, background: videoRatio === key ? '#EEF2FF' : 'white', cursor: 'pointer', textAlign: 'left' }}>
                          <div style={{ fontWeight: '600', fontSize: '13px', color: videoRatio === key ? '#4F46E5' : '#333' }}>{label}</div>
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>{hint}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Edit section — images only */}
                {imagePreview && (
                  <>
                    <p style={{ fontSize: '13px', color: '#555', margin: '0 0 10px', lineHeight: '1.6' }}>
                      Tell us what you'd like to change — e.g. <em>"brighter"</em>, <em>"crop square"</em>, <em>"add logo"</em>.
                      You can also add your logo in the section below.
                    </p>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      <input
                        value={editTextInput}
                        onChange={e => setEditTextInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && editTextInput.trim()) { handleImageVoiceCommand(editTextInput); setEditTextInput(''); } }}
                        placeholder="e.g. make it brighter, crop square..."
                        style={{ flex: 1, padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
                      />
                      <button
                        onClick={e => { e.stopPropagation(); listenForImageEdit(); }}
                        className={`mic-btn${isListeningImageEdit ? ' listening' : ''}`}
                        title="Voice edit"
                        style={{ width: '44px', height: '44px', flexShrink: 0 }}
                      ><img src="/Blinkapost bulb.png" alt="Voice" style={{ width: '22px', height: '22px', objectFit: 'contain' }} /></button>
                    </div>
                    {isListeningImageEdit && (
                      <p style={{ fontSize: '13px', color: '#4F46E5', fontWeight: '600', margin: '0 0 10px' }}>Listening...</p>
                    )}
                    {imageEditOptions.length > 0 && (
                      <div className="command-chips" style={{ justifyContent: 'flex-start', marginBottom: '12px' }}>
                        {imageEditOptions.map((opt, i) => (
                          <button key={i} className="command-chip" onClick={opt.action}>{opt.label}</button>
                        ))}
                        <button className="command-chip" style={{ color: '#999', borderColor: '#ddd' }} onClick={() => setImageEditOptions([])}>✕ Cancel</button>
                      </div>
                    )}
                    <button onClick={() => setShowTemplateEditor(true)}
                      style={{ width: '100%', marginBottom: '14px', padding: '12px', background: 'linear-gradient(135deg, #4F46E5, #7c3aed)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      🎨 Frame &amp; text editor
                    </button>
                  </>
                )}

                {/* Video — description + mic */}
                {videoPreview && (
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ fontSize: '13px', color: '#555', margin: '0 0 8px', lineHeight: '1.6' }}>
                      Add context about your video to help the AI write better posts.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        value={postContext}
                        onChange={e => setPostContext(e.target.value)}
                        placeholder="e.g. gym promo, targeting young professionals..."
                        style={{ flex: 1, padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
                      />
                      <button
                        onClick={e => { e.stopPropagation(); listenForContext(); }}
                        className={`mic-btn${isListeningContext ? ' listening' : ''}`}
                        title="Voice input"
                        style={{ width: '44px', height: '44px', flexShrink: 0 }}
                      ><img src="/Blinkapost bulb.png" alt="Voice" style={{ width: '22px', height: '22px', objectFit: 'contain' }} /></button>
                    </div>
                    {isListeningContext && <p style={{ fontSize: '13px', color: '#4F46E5', fontWeight: '600', margin: '8px 0 0' }}>Listening...</p>}
                  </div>
                )}

                {/* Logo section — shown for both image and video */}
                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px', marginTop: '8px' }}>
                  <p style={{ fontSize: '12px', fontWeight: '600', color: '#888', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your Logo</p>
                  {logosLoading ? (
                    <p style={{ color: '#bbb', fontSize: '13px' }}>Loading...</p>
                  ) : savedLogos.length > 0 ? (
                    <div className="logo-grid" style={{ marginBottom: '10px' }}>
                      {savedLogos.map(logo => (
                        <div key={logo.name} className={`logo-thumb${selectedLogo?.name === logo.name ? ' selected' : ''}`}
                          onClick={() => selectLogo(logo)}>
                          <img src={logo.url} alt={logo.name} />
                          <button className="logo-delete-btn" onClick={e => handleDeleteLogo(e, logo)}>×</button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#bbb', fontSize: '13px', marginBottom: '10px' }}>No logos yet — upload one below.</p>
                  )}
                  {/* Logo placement buttons — images only */}
                  {selectedLogo && imagePreview && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {[
                        { pos: 'top-right', label: '↗ Top Right' },
                        { pos: 'top-left', label: '↖ Top Left' },
                        { pos: 'bottom-right', label: '↘ Bottom Right' },
                        { pos: 'bottom-left', label: '↙ Bottom Left' },
                        { pos: 'center', label: '⊙ Center' },
                      ].map(({ pos, label }) => (
                        <button key={pos}
                          onClick={() => imagePreview ? applyLogo(pos) : (setVideoLogoPosition(pos), showToast(`Logo set to ${label}`))}
                          style={{ padding: '7px 12px', background: (videoPreview && videoLogoPosition === pos) ? '#EEF2FF' : (imagePreview ? '#EEF2FF' : 'white'), color: '#4F46E5', border: `1.5px solid ${videoPreview && videoLogoPosition === pos ? '#4F46E5' : '#c7d2fe'}`, borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                  <button onClick={() => document.getElementById('logoUploadWizard').click()} disabled={logoUploading}
                    style={{ width: '100%', padding: '11px', background: 'transparent', color: logoUploading ? '#a5b4fc' : '#4F46E5', border: `1.5px solid ${logoUploading ? '#a5b4fc' : '#4F46E5'}`, borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: logoUploading ? 'not-allowed' : 'pointer' }}>
                    {logoUploading ? 'Uploading...' : '+ Upload logo'}
                  </button>
                  <input id="logoUploadWizard" type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => { if (e.target.files[0]) handleNewLogoUpload(e.target.files[0]); e.target.value = ''; }} />
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  {posts && (
                    <button onClick={() => setStep(4)}
                      style={{ flex: 1, padding: '14px', background: 'white', color: '#4F46E5', border: '2px solid #4F46E5', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
                      View posts →
                    </button>
                  )}
                  <button onClick={() => setStep(3)}
                    style={{ flex: 1, padding: '14px', background: '#4F46E5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
                    {posts ? '↻ Regenerate' : 'Next — Generate posts →'}
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 3: Describe ── */}
            {step === 3 && (
              <div className="wizard-card">
                <button className="back-btn" onClick={() => setStep(2)}>← Back</button>
                <div className="wizard-card-header">
                  <span className="wizard-step-num">3</span>
                  <div>
                    <h2 className="wizard-title">Tell me about this post</h2>
                    <p className="wizard-sub">Tap mic to start · tap again to stop &amp; generate</p>
                  </div>
                </div>

                {/* ── Post goal ── */}
                <div style={{ marginBottom: postGoal === 'conversion' ? '14px' : '20px' }}>
                  <p style={{ fontSize: '12px', fontWeight: '600', color: '#888', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>What's the goal of this post?</p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {POST_GOALS.map(g => {
                      const active = postGoal === g.key;
                      return (
                        <button key={g.key} onClick={() => setPostGoal(active ? null : g.key)}
                          style={{ flex: '1 1 140px', minWidth: '128px', textAlign: 'left', padding: '12px', borderRadius: '12px', border: `2px solid ${active ? '#4F46E5' : '#e5e7eb'}`, background: active ? '#EEF2FF' : 'white', cursor: 'pointer' }}>
                          <div style={{ fontSize: '20px', marginBottom: '4px' }}>{g.emoji}</div>
                          <div style={{ fontWeight: '700', fontSize: '14px', color: active ? '#4F46E5' : '#333' }}>{g.label}</div>
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '2px', lineHeight: 1.3 }}>{g.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* ── Conversion: offer / CTA / audience + framework ── */}
                {postGoal === 'conversion' && (
                  <div style={{ border: '1px solid #e0e7ff', background: '#fafaff', borderRadius: '12px', padding: '14px', marginBottom: '20px' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#4F46E5', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>🎯 Conversion details</p>
                    {[
                      { label: 'Offer', value: convOffer, set: setConvOffer, ph: 'e.g. Free first class, no experience needed' },
                      { label: 'Call to action', value: convCta, set: setConvCta, ph: 'e.g. Book via link in bio' },
                      { label: 'Target audience', value: convAudience, set: setConvAudience, ph: 'e.g. Complete beginners' },
                    ].map(f => (
                      <div key={f.label} style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                        <input value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.ph}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '14px', outline: 'none' }} />
                      </div>
                    ))}
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#555', margin: '14px 0 8px' }}>Framework</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {CONVERSION_FRAMEWORKS.map(fw => {
                        const active = convFramework === fw.key;
                        return (
                          <button key={fw.key} onClick={() => setConvFramework(active ? null : fw.key)}
                            style={{ textAlign: 'left', padding: '10px 12px', borderRadius: '10px', border: `2px solid ${active ? '#4F46E5' : '#e5e7eb'}`, background: active ? '#EEF2FF' : 'white', cursor: 'pointer' }}>
                            <div style={{ fontWeight: '700', fontSize: '13px', color: active ? '#4F46E5' : '#333' }}>{fw.label}</div>
                            <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>{fw.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Big mic */}
                <div style={{ textAlign: 'center', margin: '8px 0 24px' }}>
                  <div className="mic-hero-wrap" style={{ margin: '0 auto 12px' }} onClick={listenForContext}>
                    {isListeningContext && <><div className="mic-ring"/><div className="mic-ring"/><div className="mic-ring"/></>}
                    <button className={`mic-hero-btn${isListeningContext ? ' listening' : ''}`}><img src="/Blinkapost bulb.png" alt="Voice" style={{ width: '48px', height: '48px', objectFit: 'contain' }} /></button>
                  </div>
                  <p className={`mic-status-text${isListeningContext ? ' active' : ''}`}>
                    {isListeningContext ? 'Tap to stop & generate' : 'Tap to speak'}
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#bbb' }}>
                    🎤 Works on Chrome &amp; Safari ·{' '}
                    <button onClick={() => { navigator.clipboard.writeText(window.location.href); showToast('Link copied — paste in Chrome!'); }}
                      style={{ background: 'none', border: 'none', color: '#4F46E5', fontSize: '11px', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                      Open in Chrome
                    </button>
                    {' '}· or type below
                  </p>
                </div>

                {/* Text fallback */}
                <textarea
                  value={postContext}
                  onChange={e => setPostContext(e.target.value)}
                  placeholder="e.g. Grand opening of our new gym location, targeting young professionals, promote our 50% off membership deal this week"
                  rows={3}
                  style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'inherit', lineHeight: '1.6', resize: 'vertical', marginBottom: '16px' }}
                />

                {/* Brand voice (collapsed by default) */}
                <details style={{ marginBottom: '16px' }}>
                  <summary style={{ fontSize: '13px', color: '#888', cursor: 'pointer', marginBottom: '8px' }}>⚙️ Brand voice settings</summary>
                  <div style={{ paddingTop: '10px' }}>
                    {/* Settings pills */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: '99px', background: '#ede9fe', color: '#4F46E5', fontSize: '12px', fontWeight: '600' }}>🌐 {userLanguage || 'English'}</span>
                      {aiTone && <span style={{ padding: '4px 10px', borderRadius: '99px', background: '#fef3c7', color: '#d97706', fontSize: '12px', fontWeight: '600' }}>🎨 {aiTone}</span>}
                      {savedHashtags && <span style={{ padding: '4px 10px', borderRadius: '99px', background: '#dcfce7', color: '#16a34a', fontSize: '12px', fontWeight: '600' }}>#{savedHashtags.trim().split(/\s+/).length} hashtags saved</span>}
                      <button onClick={() => setMenuSection('settings')} style={{ padding: '4px 10px', borderRadius: '99px', background: 'none', border: '1px dashed #ddd', color: '#aaa', fontSize: '11px', cursor: 'pointer' }}>Edit in Settings ›</button>
                    </div>
                    <textarea value={brandVoice} onChange={e => setBrandVoice(e.target.value)}
                      onBlur={saveBrandVoice}
                      placeholder="e.g. Energetic and motivating, always end with a call to action"
                      rows={2} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', fontFamily: 'inherit', lineHeight: '1.5', marginBottom: '8px' }} />
                    <button onClick={saveBrandVoice} style={{ background: brandVoiceSaved ? '#10b981' : '#4F46E5', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', transition: 'background 0.2s' }}>
                      {brandVoiceSaved ? '✓ Saved' : 'Save voice'}
                    </button>
                  </div>
                </details>

                <button onClick={() => generate()} disabled={loading}
                  style={{ width: '100%', padding: '15px', background: '#4F46E5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 14px rgba(79,70,229,0.35)' }}>
                  ✨ Generate posts
                </button>
                <button onClick={() => generate({ context: '' })} style={{ width: '100%', marginTop: '10px', padding: '10px', background: 'none', border: 'none', color: '#aaa', fontSize: '13px', cursor: 'pointer' }}>
                  Skip — just use the image
                </button>
              </div>
            )}

            {/* ── STEP 4: Results ── */}
            {step === 4 && (
              <div>
                <button className="back-btn" onClick={() => setStep(2)}>← Back</button>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <div className="spinner" />
                    <p style={{ color: '#666', marginTop: '20px', fontSize: '15px' }}>Writing your posts...</p>
                  </div>
                ) : posts ? (
                  <>
                    {[['Instagram', 'instagram'], ['LinkedIn', 'linkedin'], ['Facebook', 'facebook'], ['TikTok', 'tiktok']].map(([platform, key]) => {
                      const post = posts[key];
                      const isEditing = editingPlatform === platform;
                      return (
                      <div key={platform} style={{ background: postedPlatforms[platform] ? '#f0fdf4' : failedPlatforms[platform] ? '#fff5f5' : 'white', borderRadius: '14px', marginBottom: '14px', border: `1px solid ${postedPlatforms[platform] ? '#bbf7d0' : failedPlatforms[platform] ? '#fecaca' : '#e0e7ff'}`, boxShadow: '0 2px 8px rgba(79,70,229,0.06)', overflow: 'hidden' }}>
                        {postedPlatforms[platform] && (
                          <div style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)', color: 'white', textAlign: 'center', padding: '14px 16px', fontSize: '15px', fontWeight: '700', letterSpacing: '0.01em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '22px' }}>✅</span>
                            <span>Live on {platform}!</span>
                            <span style={{ fontSize: '13px', fontWeight: '400', opacity: 0.85 }}>Your post is out there 🎉</span>
                          </div>
                        )}
                        {failedPlatforms[platform] && (
                          <div style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white', textAlign: 'center', padding: '12px 16px', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '20px' }}>❌</span>
                            <span>Failed to post to {platform}</span>
                            <span style={{ fontSize: '12px', fontWeight: '400', opacity: 0.85 }}>Tap the button below to retry</span>
                          </div>
                        )}
                        <div style={{ padding: '20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <h3 style={{ margin: 0, color: '#333', fontSize: '15px', fontWeight: '600' }}>{platform}</h3>
                            </div>
                            {!isEditing && (
                              <button onClick={() => { setEditingPlatform(platform); setEditDraft(post); }} className="action-btn action-btn-ghost" style={{ padding: '4px 10px', fontSize: '12px' }}>
                                ✏️ Edit
                              </button>
                            )}
                          </div>
                          {BEST_TIMES[platform] && (
                            <p style={{ margin: '0 0 10px', fontSize: '11px', color: '#a5b4fc' }}>
                              📅 Best time: {BEST_TIMES[platform].days} · {BEST_TIMES[platform].times}
                            </p>
                          )}

                          {isEditing ? (
                            <>
                              <textarea
                                value={editDraft}
                                onChange={e => setEditDraft(e.target.value)}
                                rows={5}
                                autoFocus
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #4F46E5', fontSize: '14px', fontFamily: 'inherit', lineHeight: '1.7', resize: 'vertical', marginBottom: '10px' }}
                              />
                              {/* Quick CTAs — tap to append to the caption */}
                              <div style={{ marginBottom: '10px' }}>
                                <p style={{ fontSize: '11px', color: '#888', fontWeight: '600', margin: '0 0 6px' }}>⚡ Quick CTAs</p>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {QUICK_CTAS.map(cta => (
                                    <button key={cta} onClick={() => setEditDraft(d => (d.trim() ? d.trim() + '\n\n' : '') + cta)}
                                      style={{ padding: '6px 10px', borderRadius: '14px', border: '1px solid #c7d2fe', background: '#EEF2FF', color: '#4F46E5', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                                      + {cta}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <button onClick={() => { setPosts(p => ({ ...p, [key]: editDraft })); setEditingPlatform(null); }} className="action-btn action-btn-primary">
                                  ✓ Save
                                </button>
                                <button onClick={() => setEditingPlatform(null)} className="action-btn action-btn-ghost">
                                  Cancel
                                </button>
                              </div>
                            </>
                          ) : (
                            <p style={{ margin: '0 0 16px', color: '#555', fontSize: '14px', lineHeight: '1.7' }}>{post}</p>
                          )}

                          {!isEditing && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {platform === 'LinkedIn' && linkedinConnected ? (
                                <button onClick={() => postToLinkedin(post)} disabled={linkedinPosting} className="action-btn action-btn-primary" style={{ background: linkedinPosting ? '#a5b4fc' : '#0A66C2', borderColor: linkedinPosting ? '#a5b4fc' : '#0A66C2' }}>
                                  {linkedinPosting ? POSTING_MSGS[postingMsgIdx] : '🔗 Post to LinkedIn'}
                                </button>

                              ) : platform === 'Facebook' && facebookConnected ? (
                                <>
                                  <div style={{ display: 'flex', gap: '6px', marginBottom: '4px', width: '100%' }}>
                                    {['post', 'story'].map(type => (
                                      <button key={type} onClick={() => setFbPostType(type)} style={{ padding: '5px 12px', borderRadius: '20px', border: `1.5px solid ${fbPostType === type ? '#1877F2' : '#ddd'}`, background: fbPostType === type ? '#EBF5FF' : 'white', color: fbPostType === type ? '#1877F2' : '#888', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                                        {type === 'post' ? '📄 Post' : '⭕ Story'}
                                      </button>
                                    ))}
                                  </div>
                                  <button onClick={() => postToFacebook(post)} disabled={facebookPosting} className="action-btn action-btn-primary" style={{ background: facebookPosting ? '#a5b4fc' : '#1877F2', borderColor: facebookPosting ? '#a5b4fc' : '#1877F2' }}>
                                    {facebookPosting ? POSTING_MSGS[postingMsgIdx] : `📘 ${fbPostType === 'story' ? 'Post Story' : 'Post to Facebook'}`}
                                  </button>
                                </>

                              ) : platform === 'Instagram' && igConnected ? (
                                <>
                                  <div style={{ display: 'flex', gap: '6px', marginBottom: '4px', width: '100%' }}>
                                    {['post', 'story', ...(mediaType === 'video' ? ['video', 'reel'] : [])].map(type => (
                                      <button key={type} onClick={() => setIgPostType(type)} style={{ padding: '5px 12px', borderRadius: '20px', border: `1.5px solid ${igPostType === type ? '#E1306C' : '#ddd'}`, background: igPostType === type ? '#FFF0F5' : 'white', color: igPostType === type ? '#E1306C' : '#888', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                                        {type === 'post' ? '📸 Post' : type === 'story' ? '⭕ Story' : type === 'video' ? '🎥 Video' : '🎬 Reel'}
                                      </button>
                                    ))}
                                  </div>
                                  <button onClick={() => postToInstagram(post)} disabled={instagramPosting} className="action-btn action-btn-primary" style={{ background: instagramPosting ? '#a5b4fc' : '#E1306C', borderColor: instagramPosting ? '#a5b4fc' : '#E1306C' }}>
                                    {instagramPosting ? POSTING_MSGS[postingMsgIdx] : `📷 ${igPostType === 'story' ? 'Post Story' : igPostType === 'reel' ? 'Post Reel' : 'Post to Instagram'}`}
                                  </button>
                                </>

                              ) : platform === 'TikTok' && tiktokConnected ? (
                                <button onClick={() => postToTikTok(post)} disabled={tiktokPosting} className="action-btn action-btn-primary" style={{ background: tiktokPosting ? '#a5b4fc' : '#000', borderColor: tiktokPosting ? '#a5b4fc' : '#000' }}>
                                  {tiktokPosting ? POSTING_MSGS[postingMsgIdx] : '🎵 Post to TikTok'}
                                </button>

                              ) : (
                                <button onClick={() => handlePostNow(platform, post)} className="action-btn action-btn-primary">
                                  🚀 Post now
                                </button>
                              )}
                              <button onClick={() => setSchedulingPlatform(schedulingPlatform === platform ? null : platform)} className="action-btn action-btn-secondary">
                                📅 Schedule
                              </button>
                              <button onClick={() => navigator.clipboard.writeText(post)} className="action-btn action-btn-ghost">
                                Copy
                              </button>
                            </div>
                          )}
                        </div>

                        {schedulingPlatform === platform && !isEditing && (
                          <div className="schedule-picker">
                            <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#555', fontWeight: '500' }}>Pick a date and time for {platform}</p>
                            <input
                              type="date"
                              value={scheduleTime.split('T')[0] || ''}
                              min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 10)}
                              onChange={e => setScheduleTime(`${e.target.value}T${scheduleTime.split('T')[1] || '09:00'}`)}
                              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', marginBottom: '8px', boxSizing: 'border-box' }}
                            />
                            <input
                              type="time"
                              value={scheduleTime.split('T')[1] || '09:00'}
                              onChange={e => setScheduleTime(`${scheduleTime.split('T')[0] || new Date().toISOString().slice(0, 10)}T${e.target.value}`)}
                              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', marginBottom: '10px', boxSizing: 'border-box' }}
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button onClick={() => handleSchedule(platform, post)} className="action-btn action-btn-primary" style={{ flex: 1 }}>
                                Confirm schedule
                              </button>
                              <button onClick={() => setSchedulingPlatform(null)} className="action-btn action-btn-ghost">
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );})}
                    {/* ── Google Business post card ── */}
                    {gbConnected && (posts?.google_business || posts?.facebook) && (() => {
                      const post = posts.google_business || posts.facebook;
                      const isEditing = editingPlatform === 'Google Business';
                      return (
                        <div style={{ background: postedPlatforms['Google Business'] ? '#f0fdf4' : failedPlatforms['Google Business'] ? '#fff5f5' : 'white', borderRadius: '14px', marginBottom: '14px', border: `1px solid ${postedPlatforms['Google Business'] ? '#bbf7d0' : failedPlatforms['Google Business'] ? '#fecaca' : '#e0e7ff'}`, boxShadow: '0 2px 8px rgba(79,70,229,0.06)', overflow: 'hidden' }}>
                          {postedPlatforms['Google Business'] && (
                            <div style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)', color: 'white', textAlign: 'center', padding: '14px 16px', fontSize: '15px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '22px' }}>✅</span><span>Live on Google Business!</span>
                            </div>
                          )}
                          {failedPlatforms['Google Business'] && (
                            <div style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white', textAlign: 'center', padding: '12px 16px', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '20px' }}>❌</span><span>Failed to post to Google Business</span>
                            </div>
                          )}
                          <div style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <h3 style={{ margin: 0, color: '#333', fontSize: '15px', fontWeight: '600' }}>📍 Google Business</h3>
                              {!isEditing && <button onClick={() => { setEditingPlatform('Google Business'); setEditDraft(post); }} className="action-btn action-btn-ghost" style={{ padding: '4px 10px', fontSize: '12px' }}>✏️ Edit</button>}
                            </div>
                            {isEditing ? (
                              <>
                                <textarea value={editDraft} onChange={e => setEditDraft(e.target.value)} rows={4} autoFocus style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #4F46E5', fontSize: '14px', fontFamily: 'inherit', lineHeight: '1.7', resize: 'vertical', marginBottom: '10px' }} />
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                  <button onClick={() => { setPosts(p => ({ ...p, google_business: editDraft })); setEditingPlatform(null); }} className="action-btn action-btn-primary">✓ Save</button>
                                  <button onClick={() => setEditingPlatform(null)} className="action-btn action-btn-ghost">Cancel</button>
                                </div>
                              </>
                            ) : (
                              <p style={{ margin: '0 0 16px', color: '#555', fontSize: '14px', lineHeight: '1.7' }}>{post}</p>
                            )}
                            {!isEditing && (
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {gbSelectedLocation ? (
                                  <button onClick={() => postToGoogleBusiness(post)} disabled={gbPosting} className="action-btn action-btn-primary" style={{ background: gbPosting ? '#a5b4fc' : '#34A853', borderColor: gbPosting ? '#a5b4fc' : '#34A853' }}>
                                    {gbPosting ? POSTING_MSGS[postingMsgIdx] : '📍 Post to Google Business'}
                                  </button>
                                ) : (
                                  <button onClick={loadGBLocations} style={{ background: 'none', border: '1px solid #34A853', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', color: '#34A853', cursor: 'pointer' }}>🔄 Load locations</button>
                                )}
                                <button onClick={() => navigator.clipboard.writeText(post)} className="action-btn action-btn-ghost">Copy</button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    {/* Post to All */}
                    {(linkedinConnected || facebookConnected || igConnected || tiktokConnected || gbConnected) && (
                      <div style={{ marginBottom: '24px' }}>
                        <button onClick={() => { setPostAllResults(null); postToAll(); }} disabled={postingAll}
                          style={{ width: '100%', padding: '15px', background: postingAll ? '#a5b4fc' : '#4F46E5', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: postingAll ? 'not-allowed' : 'pointer' }}>
                          {postingAll ? 'Posting to all platforms...' : '🚀 Post to All Connected Platforms'}
                        </button>
                        {postAllResults && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px', justifyContent: 'center' }}>
                            {Object.entries(postAllResults).map(([platform, status]) => (
                              <span key={platform} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '99px', fontSize: '13px', fontWeight: '700', background: status === 'success' ? '#dcfce7' : '#fee2e2', color: status === 'success' ? '#15803d' : '#dc2626', border: `1px solid ${status === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
                                {status === 'success' ? '✓' : '✗'} {platform}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Connection banners */}
                    {/* ── LinkedIn banner ── */}
                    {(() => {
                      const accs = getAccountsForPlatform('linkedin');
                      const sel = getSelectedAccount('linkedin');
                      const connected = accs.length > 0;
                      return (
                        <div style={{ background: postedPlatforms['LinkedIn'] ? '#f0fdf4' : connected ? '#EFF8FF' : 'white', borderRadius: '14px', marginBottom: '10px', border: `1px solid ${postedPlatforms['LinkedIn'] ? '#bbf7d0' : connected ? '#BFDBFE' : '#e0e7ff'}`, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '22px' }}>🔗</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {connected ? (
                              <>
                                <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#1d4ed8', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>LinkedIn {postedPlatforms['LinkedIn'] && <span style={{ background: '#16a34a', color: 'white', fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '99px' }}>✅ Posted!</span>}</p>
                                {accs.length === 1
                                  ? <p style={{ margin: 0, fontSize: '12px', color: '#3b82f6' }}>{sel?.page_name}</p>
                                  : <select value={sel?.id || ''} onChange={e => setSelectedAccountIds(prev => ({ ...prev, linkedin: e.target.value }))} style={{ fontSize: '12px', color: '#3b82f6', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '3px 6px', background: '#EFF8FF', cursor: 'pointer', maxWidth: '160px' }}>
                                      {accs.map(a => <option key={a.id} value={a.id}>{a.page_name}</option>)}
                                    </select>}
                              </>
                            ) : <p style={{ margin: 0, fontSize: '14px', color: '#555', fontWeight: '500' }}>Connect LinkedIn to post directly</p>}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            {connected && <button onClick={() => disconnectAccount(sel?.id, 'linkedin')} style={{ background: 'none', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '6px 10px', fontSize: '12px', color: '#1d4ed8', cursor: 'pointer' }}>Disconnect</button>}
                            <button onClick={connectLinkedin} style={{ background: '#0A66C2', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', color: 'white', fontWeight: '600', cursor: 'pointer' }}>{connected ? '+ Add' : 'Connect'}</button>
                          </div>
                        </div>
                      );
                    })()}
                    {/* ── Facebook banner ── */}
                    {(() => {
                      const accs = getAccountsForPlatform('facebook');
                      const sel = getSelectedAccount('facebook');
                      const connected = accs.length > 0;
                      return (
                        <div style={{ background: connected ? '#F0FFF4' : 'white', borderRadius: '14px', marginBottom: '10px', border: `1px solid ${connected ? '#BBF7D0' : '#e0e7ff'}`, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '22px', flexShrink: 0 }}>📘</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {connected ? (
                              <>
                                <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#166534', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>Facebook {postedPlatforms['Facebook'] && <span style={{ background: '#16a34a', color: 'white', fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '99px' }}>✅ Posted!</span>}</p>
                                {accs.length === 1
                                  ? <p style={{ margin: 0, fontSize: '12px', color: '#16a34a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sel?.page_name}</p>
                                  : <select value={sel?.id || ''} onChange={e => setSelectedAccountIds(prev => ({ ...prev, facebook: e.target.value }))} style={{ fontSize: '12px', color: '#16a34a', border: '1px solid #BBF7D0', borderRadius: '6px', padding: '3px 6px', background: '#F0FFF4', cursor: 'pointer', maxWidth: '160px' }}>
                                      {accs.map(a => <option key={a.id} value={a.id}>{a.page_name}</option>)}
                                    </select>}
                              </>
                            ) : <p style={{ margin: 0, fontSize: '14px', color: '#555', fontWeight: '500' }}>Connect Facebook to post directly</p>}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            {connected && <button onClick={() => disconnectAccount(sel?.id, 'facebook')} style={{ background: 'none', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '6px 10px', fontSize: '12px', color: '#166534', cursor: 'pointer' }}>Disconnect</button>}
                            <button onClick={connectFacebook} style={{ background: '#1877F2', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', color: 'white', fontWeight: '600', cursor: 'pointer' }}>{connected ? '+ Add' : 'Connect'}</button>
                          </div>
                        </div>
                      );
                    })()}
                    {/* ── Instagram banner ── */}
                    {(() => {
                      const accs = getAccountsForPlatform('instagram');
                      const sel = getSelectedAccount('instagram');
                      const connected = accs.length > 0;
                      return (
                        <div style={{ background: connected ? '#F0FFF4' : 'white', borderRadius: '14px', marginBottom: '10px', border: `1px solid ${connected ? '#BBF7D0' : '#e0e7ff'}`, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '22px' }}>📸</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {connected ? (
                              <>
                                <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#166534', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>Instagram {postedPlatforms['Instagram'] && <span style={{ background: '#16a34a', color: 'white', fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '99px' }}>✅ Posted!</span>}</p>
                                {accs.length === 1
                                  ? <p style={{ margin: 0, fontSize: '12px', color: '#16a34a' }}>@{sel?.page_name}</p>
                                  : <select value={sel?.id || ''} onChange={e => setSelectedAccountIds(prev => ({ ...prev, instagram: e.target.value }))} style={{ fontSize: '12px', color: '#16a34a', border: '1px solid #BBF7D0', borderRadius: '6px', padding: '3px 6px', background: '#F0FFF4', cursor: 'pointer', maxWidth: '160px' }}>
                                      {accs.map(a => <option key={a.id} value={a.id}>@{a.page_name}</option>)}
                                    </select>}
                              </>
                            ) : <p style={{ margin: 0, fontSize: '14px', color: '#555', fontWeight: '500' }}>Connect Instagram to post directly</p>}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            {connected && <button onClick={() => disconnectAccount(sel?.id, 'instagram')} style={{ background: 'none', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '6px 10px', fontSize: '12px', color: '#166534', cursor: 'pointer' }}>Disconnect</button>}
                            <button onClick={connectInstagram} style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', color: 'white', fontWeight: '600', cursor: 'pointer' }}>{connected ? '+ Add' : 'Connect'}</button>
                          </div>
                        </div>
                      );
                    })()}
                    {/* ── TikTok banner ── */}
                    {(() => {
                      const accs = getAccountsForPlatform('tiktok');
                      const sel = getSelectedAccount('tiktok');
                      const connected = accs.length > 0;
                      return (
                        <div style={{ background: connected ? '#F0FFF4' : 'white', borderRadius: '14px', marginBottom: '14px', border: `1px solid ${connected ? '#BBF7D0' : '#e0e7ff'}`, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '22px' }}>🎵</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {connected ? (
                              <>
                                <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#166534', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>TikTok {postedPlatforms['TikTok'] && <span style={{ background: '#16a34a', color: 'white', fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '99px' }}>✅ Posted!</span>}</p>
                                {accs.length === 1
                                  ? <p style={{ margin: 0, fontSize: '12px', color: '#16a34a' }}>{sel?.page_name}</p>
                                  : <select value={sel?.id || ''} onChange={e => setSelectedAccountIds(prev => ({ ...prev, tiktok: e.target.value }))} style={{ fontSize: '12px', color: '#16a34a', border: '1px solid #BBF7D0', borderRadius: '6px', padding: '3px 6px', background: '#F0FFF4', cursor: 'pointer', maxWidth: '160px' }}>
                                      {accs.map(a => <option key={a.id} value={a.id}>{a.page_name}</option>)}
                                    </select>}
                              </>
                            ) : <p style={{ margin: 0, fontSize: '14px', color: '#555', fontWeight: '500' }}>Connect TikTok to post directly</p>}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            {connected && <button onClick={() => disconnectAccount(sel?.id, 'tiktok')} style={{ background: 'none', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '6px 10px', fontSize: '12px', color: '#166534', cursor: 'pointer' }}>Disconnect</button>}
                            <button onClick={connectTikTok} style={{ background: '#000', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', color: 'white', fontWeight: '600', cursor: 'pointer' }}>{connected ? '+ Add' : 'Connect'}</button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* ── Google Business banner ── */}
                    {(() => {
                      const post = posts?.google_business || posts?.facebook || '';
                      return (
                        <div style={{ background: gbConnected ? '#F0FFF4' : 'white', borderRadius: '14px', marginBottom: '14px', border: `1px solid ${gbConnected ? '#BBF7D0' : '#e0e7ff'}`, overflow: 'hidden' }}>
                          {postedPlatforms['Google Business'] && (
                            <div style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)', color: 'white', textAlign: 'center', padding: '10px 16px', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                              <span>✅</span><span>Posted to Google Business!</span>
                            </div>
                          )}
                          <div style={{ padding: '16px 18px' }}>
                            {/* Header row */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', gap: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: '20px', flexShrink: 0 }}>📍</span>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <p style={{ margin: 0, fontSize: '13px', color: gbConnected ? '#166534' : '#555', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    Google Business {postedPlatforms['Google Business'] && <span style={{ background: '#16a34a', color: 'white', fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '99px' }}>✅ Posted!</span>}
                                  </p>
                                  {gbConnected && gbSelectedLocation && <p style={{ margin: 0, fontSize: '12px', color: '#16a34a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gbSelectedLocation.page_name}</p>}
                                  {gbConnected && !gbSelectedLocation && <button onClick={loadGBLocations} style={{ background: 'none', border: 'none', padding: 0, fontSize: '12px', color: '#34A853', cursor: 'pointer', textDecoration: 'underline' }}>Load location</button>}
                                  {gbConnected && gbLocations.length > 1 && (
                                    <select value={gbSelectedLocation?.page_id || ''} onChange={e => setGbSelectedLocation(gbLocations.find(l => l.page_id === e.target.value))} style={{ fontSize: '12px', color: '#16a34a', border: '1px solid #BBF7D0', borderRadius: '6px', padding: '3px 6px', background: '#F0FFF4', cursor: 'pointer', maxWidth: '160px', marginTop: '2px' }}>
                                      {gbLocations.map(l => <option key={l.page_id} value={l.page_id}>{l.page_name}</option>)}
                                    </select>
                                  )}
                                  {!gbConnected && <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Not connected</p>}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                {gbConnected && <button onClick={disconnectGB} style={{ background: 'none', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '6px 10px', fontSize: '12px', color: '#166534', cursor: 'pointer' }}>Disconnect</button>}
                                <button onClick={connectGB} style={{ background: '#34A853', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', color: 'white', fontWeight: '600', cursor: 'pointer' }}>{gbConnected ? '+ Add' : 'Connect'}</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Download image */}
                    {imagePreview && (
                      <div style={{ background: 'white', borderRadius: '14px', marginBottom: '14px', border: '1px solid #e0e7ff', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <img src={imagePreview} alt="post" style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 2px', fontSize: '14px', fontWeight: '600', color: '#333' }}>Save your image</p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Download it to post alongside your caption</p>
                        </div>
                        <a href={imagePreview} download="blinkapost-image.jpg" className="action-btn action-btn-primary" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
                          ⬇️ Download
                        </a>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px', marginTop: '8px', marginBottom: '32px' }}>
                      <button onClick={startOver} style={{ flex: 1, padding: '13px', background: 'white', border: '1.5px solid #e0e7ff', borderRadius: '10px', fontSize: '14px', fontWeight: '500', color: '#4F46E5', cursor: 'pointer' }}>
                        ← Start over
                      </button>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <button onClick={() => setShowRegenerateMenu(m => !m)} style={{ width: '100%', padding: '13px', background: '#4F46E5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                          ↻ Regenerate
                        </button>
                        {showRegenerateMenu && (
                          <div style={{ position: 'absolute', bottom: '110%', left: 0, right: 0, background: 'white', border: '1px solid #e0e7ff', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', overflow: 'hidden', zIndex: 10 }}>
                            {['Instagram', 'LinkedIn', 'Facebook', 'TikTok'].map(p => (
                              <button key={p} onClick={() => regenerateSingle(p)}
                                style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', borderBottom: '1px solid #f0f0f0', fontSize: '14px', color: '#333', cursor: 'pointer', textAlign: 'left' }}>
                                ↻ {p} only
                              </button>
                            ))}
                            <button onClick={() => { setShowRegenerateMenu(false); generate(); }}
                              style={{ width: '100%', padding: '12px 16px', background: '#F5F3FF', border: 'none', fontSize: '14px', color: '#4F46E5', fontWeight: '600', cursor: 'pointer', textAlign: 'left' }}>
                              ↻ All platforms
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            )}
            {/* ── TikTok pre-authorisation consent (required by TikTok UX Guidelines Points 1–5) ── */}
            {showTikTokConsent && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
                <div style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '360px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)' }}>
                  {/* Header — TikTok branding (Point 1) */}
                  <div style={{ background: '#010101', padding: '28px 24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <svg viewBox="0 0 48 48" width="52" height="52" fill="none">
                      <rect width="48" height="48" rx="12" fill="#010101"/>
                      <path d="M34.5,14.1a9.8,9.8,0,0,1-2.4-1.3,9.6,9.6,0,0,1-3.4-6.3H24v22.6a4.6,4.6,0,1,1-4.6-4.6,4.7,4.7,0,0,1,1.5.3V20.5a9.2,9.2,0,0,0-1.5-.1,9,9,0,1,0,9,9V17.2a15.3,15.3,0,0,0,8.9,2.8V15.6A9.7,9.7,0,0,1,34.5,14.1Z" fill="white"/>
                      <path d="M34.5,14.1a9.8,9.8,0.01-2.4-1.3A9.6,9.6,0,0,1,28.7,6.5H24v22.6A4.6,4.6,0,1,1,19.4,24.5a4.7,4.7,0,0,1,1.5.3V20.5a9.2,9.2,0,0,0-1.5-.1,9,9,0,1,0,9,9V17.2a15.3,15.3,0,0,0,8.9,2.8V15.6A9.7,9.7,0,0,1,34.5,14.1Z" fill="#EE1D52" opacity="0.5"/>
                    </svg>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Connecting to</p>
                      <p style={{ color: 'white', fontSize: '22px', fontWeight: '800', margin: 0 }}>TikTok</p>
                    </div>
                  </div>

                  {/* Body — permission disclosure (Point 2) */}
                  <div style={{ padding: '20px 24px' }}>
                    <p style={{ fontSize: '14px', color: '#374151', fontWeight: '600', margin: '0 0 4px' }}>Blinkapost will request access to:</p>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 16px' }}>You'll review and approve these on TikTok's own screen.</p>

                    {[
                      { icon: '👤', title: 'Basic profile info', desc: 'Your username and display name' },
                      { icon: '🎬', title: 'Upload videos', desc: 'Post videos on your behalf' },
                      { icon: '📤', title: 'Publish content', desc: 'Publish uploaded videos to your profile' },
                    ].map(({ icon, title, desc }) => (
                      <div key={title} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                        <span style={{ fontSize: '20px', lineHeight: 1 }}>{icon}</span>
                        <div>
                          <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#111827' }}>{title}</p>
                          <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>{desc}</p>
                        </div>
                      </div>
                    ))}

                    <p style={{ fontSize: '11px', color: '#9ca3af', margin: '16px 0 0', lineHeight: 1.6, textAlign: 'center' }}>
                      By continuing you agree to Blinkapost's{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'underline' }}>Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'underline' }}>Privacy Policy</a>.
                      {' '}Blinkapost is not affiliated with TikTok.
                    </p>
                  </div>

                  {/* Actions (Point 3 — leads into TikTok's own auth screen) */}
                  <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <button
                      onClick={doConnectTikTok}
                      style={{ width: '100%', padding: '14px', background: '#010101', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="white"><path d="M19.59,6.69A4.83,4.83 0 0,1 15.82,2.5H12.37V15.67A2.89,2.89 0 0,1 9.49,18.17C7.9,18.17 6.62,16.92 6.62,15.33C6.62,13.47 8.32,12.05 10.18,12.49V8.97A6.33,6.33 0 0,0 3.17,15.33C3.17,18.9 6.09,21.83 9.66,21.83C13.22,21.83 16.14,18.9 16.14,15.33V8.85A8.17,8.17 0 0,0 20.83,10.34V6.88A4.85,4.85 0 0,1 19.59,6.69Z"/></svg>
                      Continue with TikTok
                    </button>
                    <button
                      onClick={() => setShowTikTokConsent(false)}
                      style={{ width: '100%', padding: '12px', background: 'none', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: '14px', color: '#6b7280', cursor: 'pointer', fontWeight: '500' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Camera modal ── */}
            {showCamera && (
              <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 9999, display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>

                {/* Top bar */}
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(0,0,0,0.75)' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['photo', 'video'].map(m => (
                      <button key={m} onClick={() => setCameraMode(m)}
                        style={{ padding: '6px 14px', borderRadius: '20px', border: 'none', background: cameraMode === m ? 'white' : 'rgba(255,255,255,0.2)', color: cameraMode === m ? '#111' : 'white', fontWeight: '600', fontSize: '13px', cursor: 'pointer', textTransform: 'capitalize' }}>
                        {m}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setCameraFacing(f => f === 'environment' ? 'user' : 'environment')}
                      style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '38px', height: '38px', color: 'white', fontSize: '18px', cursor: 'pointer' }}>🔄</button>
                    <button onClick={closeCamera}
                      style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '38px', height: '38px', color: 'white', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                  </div>
                </div>

                {/* Live preview */}
                <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden', background: '#000' }}>
                  <video ref={cameraVideoRef} autoPlay playsInline muted
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: cameraFilter === 'none' ? 'none' : cameraFilter }} />
                  {/* Crop guide overlay */}
                  {cameraRatio !== 'free' && (() => {
                    const [rw, rh] = cameraRatio.split(':').map(Number);
                    const portrait = rh >= rw;
                    return (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                        <div style={{
                          aspectRatio: `${rw} / ${rh}`,
                          ...(portrait ? { height: '100%', maxWidth: '100%' } : { width: '100%', maxHeight: '100%' }),
                          boxShadow: '0 0 0 2000px rgba(0,0,0,0.52)',
                          border: '1px solid rgba(255,255,255,0.7)',
                        }} />
                      </div>
                    );
                  })()}
                  {isRecording && (
                    <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', background: '#ef4444', color: 'white', borderRadius: '20px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'white', display: 'inline-block' }} />
                      REC
                    </div>
                  )}
                </div>

                {/* Aspect ratio strip */}
                <div style={{ flexShrink: 0, display: 'flex', gap: '8px', padding: '8px 16px', overflowX: 'auto', background: 'rgba(0,0,0,0.75)', scrollbarWidth: 'none' }}>
                  {CAMERA_RATIOS.map(r => (
                    <button key={r.value} onClick={() => setCameraRatio(r.value)}
                      style={{ flexShrink: 0, padding: '5px 14px', borderRadius: '20px', border: `1.5px solid ${cameraRatio === r.value ? 'white' : 'rgba(255,255,255,0.3)'}`, background: cameraRatio === r.value ? 'white' : 'transparent', color: cameraRatio === r.value ? '#111' : 'white', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {r.label}
                    </button>
                  ))}
                </div>

                {/* Filter strip — live canvas previews */}
                <div style={{ flexShrink: 0, display: 'flex', gap: '12px', padding: '10px 16px', overflowX: 'auto', background: 'rgba(0,0,0,0.75)', scrollbarWidth: 'none' }}>
                  {CAMERA_FILTERS.map((f, i) => (
                    <button key={f.value} onClick={() => setCameraFilter(f.value)}
                      style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px' }}>
                      <canvas
                        ref={el => filterCanvasRefs.current[i] = el}
                        width={50} height={50}
                        style={{ borderRadius: '8px', display: 'block', border: `2px solid ${cameraFilter === f.value ? 'white' : 'transparent'}` }}
                      />
                      <span style={{ fontSize: '10px', color: cameraFilter === f.value ? 'white' : 'rgba(255,255,255,0.6)', fontWeight: cameraFilter === f.value ? '700' : '400' }}>{f.label}</span>
                    </button>
                  ))}
                </div>

                {/* Shutter */}
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 20px 24px', background: 'rgba(0,0,0,0.75)' }}>
                  {cameraMode === 'photo' ? (
                    <button onClick={capturePhoto}
                      style={{ width: '68px', height: '68px', borderRadius: '50%', border: '4px solid white', background: 'white', cursor: 'pointer', boxShadow: '0 0 0 4px rgba(255,255,255,0.3)', flexShrink: 0 }} />
                  ) : isRecording ? (
                    <button onClick={stopRecording}
                      style={{ width: '68px', height: '68px', borderRadius: '50%', border: '4px solid white', background: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 4px rgba(239,68,68,0.4)', flexShrink: 0 }}>
                      <div style={{ width: '22px', height: '22px', background: 'white', borderRadius: '4px' }} />
                    </button>
                  ) : (
                    <button onClick={startRecording}
                      style={{ width: '68px', height: '68px', borderRadius: '50%', border: '4px solid white', background: '#ef4444', cursor: 'pointer', boxShadow: '0 0 0 4px rgba(239,68,68,0.3)', flexShrink: 0 }} />
                  )}
                </div>

              </div>
            )}

            {/* ── Themed frame & text editor ── */}
            {showTemplateEditor && imagePreview && (
              <ImageTemplateEditor
                imageSrc={imagePreview}
                onApply={applyTemplateResult}
                onClose={() => setShowTemplateEditor(false)} />
            )}
          </>
        )}

        {/* ── SCHEDULE TAB ── */}
        {!showOnboarding && activeTab === 'schedule' && (() => {
          const PLATFORM_COLORS = { instagram: '#E1306C', linkedin: '#0A66C2', facebook: '#1877F2', tiktok: '#010101', google_business: '#34A853' };
          const today = new Date(); today.setHours(0,0,0,0);
          const year = calendarMonth.getFullYear();
          const month = calendarMonth.getMonth();
          const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const monthName = calendarMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

          // Map scheduledPosts by day key "YYYY-MM-DD"
          const postsByDay = {};
          scheduledPosts.forEach(p => {
            const d = new Date(p.scheduled_at);
            const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            if (!postsByDay[key]) postsByDay[key] = [];
            postsByDay[key].push(p);
          });

          const selectedKey = calendarSelectedDay
            ? `${calendarSelectedDay.getFullYear()}-${String(calendarSelectedDay.getMonth()+1).padStart(2,'0')}-${String(calendarSelectedDay.getDate()).padStart(2,'0')}`
            : null;
          const selectedPosts = selectedKey ? (postsByDay[selectedKey] || []) : [];

          const failedPosts = scheduledPosts.filter(p => p.status === 'failed');

          return (
            <>
              <h2 style={{ color: '#333', marginBottom: '4px', marginTop: 0 }}>Content Calendar</h2>
              <p style={{ color: '#888', marginBottom: '20px', fontSize: '14px', marginTop: 0 }}>Tap a day to see scheduled posts</p>

              {/* Failed posts */}
              {failedPosts.length > 0 && (
                <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderRadius: '14px', padding: '16px 18px', marginBottom: '20px' }}>
                  <p style={{ margin: '0 0 12px', fontWeight: '700', fontSize: '14px', color: '#dc2626' }}>Posts that failed to send</p>
                  {failedPosts.map(p => (
                    <div key={p.id} style={{ background: 'white', borderRadius: '10px', padding: '12px 14px', marginBottom: '8px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '700', color: 'white', background: '#dc2626', borderRadius: '6px', padding: '2px 8px' }}>{p.platform}</span>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(p.scheduled_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: '13px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.content}</p>
                        {p.error_message && <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#dc2626' }}>{p.error_message}</p>}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button
                          onClick={() => retryPost(p)}
                          disabled={retryingPostId === p.id}
                          style={{ background: retryingPostId === p.id ? '#a5b4fc' : '#4F46E5', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: '600', cursor: retryingPostId === p.id ? 'not-allowed' : 'pointer' }}
                        >
                          {retryingPostId === p.id ? 'Posting...' : 'Post Now'}
                        </button>
                        <button onClick={() => deleteScheduledPost(p.id)} style={{ background: 'none', border: '1px solid #fecaca', borderRadius: '8px', padding: '7px 10px', fontSize: '13px', color: '#dc2626', cursor: 'pointer' }}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Calendar card */}
              <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', marginBottom: '16px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                {/* Month nav */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #f0f0f0' }}>
                  <button onClick={() => { const d = new Date(calendarMonth); d.setMonth(d.getMonth()-1); setCalendarMonth(d); setCalendarSelectedDay(null); }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6366f1', padding: '4px 8px' }}>‹</button>
                  <span style={{ fontWeight: '700', fontSize: '15px', color: '#1f2937' }}>{monthName}</span>
                  <button onClick={() => { const d = new Date(calendarMonth); d.setMonth(d.getMonth()+1); setCalendarMonth(d); setCalendarSelectedDay(null); }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6366f1', padding: '4px 8px' }}>›</button>
                </div>

                {/* Day headers */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '8px 8px 0' }}>
                  {['S','M','T','W','T','F','S'].map((d, i) => (
                    <div key={i} style={{ textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#9ca3af', paddingBottom: '6px' }}>{d}</div>
                  ))}
                </div>

                {/* Days grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '0 8px 8px', gap: '2px' }}>
                  {/* Empty cells before first day */}
                  {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
                  {/* Day cells */}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const dayNum = i + 1;
                    const cellDate = new Date(year, month, dayNum);
                    const key = `${year}-${String(month+1).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`;
                    const posts = postsByDay[key] || [];
                    const isToday = cellDate.getTime() === today.getTime();
                    const isSelected = selectedKey === key;
                    const hasPosts = posts.length > 0;
                    return (
                      <div
                        key={dayNum}
                        onClick={() => setCalendarSelectedDay(isSelected ? null : cellDate)}
                        style={{ borderRadius: '8px', padding: '5px 2px', textAlign: 'center', cursor: hasPosts ? 'pointer' : 'default', background: isSelected ? '#6366f1' : isToday ? '#f5f3ff' : 'transparent', border: isToday && !isSelected ? '1px solid #c4b5fd' : '1px solid transparent', transition: 'all 0.15s' }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: isToday ? '700' : '400', color: isSelected ? 'white' : isToday ? '#6366f1' : '#374151', marginBottom: '3px' }}>{dayNum}</div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', flexWrap: 'wrap', minHeight: '6px' }}>
                          {posts.slice(0, 3).map((p, pi) => (
                            <div key={pi} style={{ width: '5px', height: '5px', borderRadius: '50%', background: isSelected ? 'rgba(255,255,255,0.8)' : p.status === 'posted' ? '#16a34a' : p.status === 'failed' ? '#dc2626' : (PLATFORM_COLORS[p.platform?.toLowerCase()] || '#6366f1') }} />
                          ))}
                          {posts.length > 3 && <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: isSelected ? 'rgba(255,255,255,0.6)' : '#d1d5db' }} />}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', padding: '10px 16px', borderTop: '1px solid #f0f0f0' }}>
                  {Object.entries(PLATFORM_COLORS).map(([p, c]) => (
                    <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: c }} />
                      <span style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'capitalize' }}>{p.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected day posts */}
              {calendarSelectedDay && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontWeight: '700', fontSize: '14px', color: '#374151', margin: '0 0 10px' }}>
                    {calendarSelectedDay.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
                    {selectedPosts.length > 0 ? ` · ${selectedPosts.length} post${selectedPosts.length > 1 ? 's' : ''}` : ' · No posts'}
                  </p>
                  {selectedPosts.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '24px', background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', color: '#9ca3af', fontSize: '13px' }}>
                      Nothing scheduled for this day.
                    </div>
                  )}
                  {selectedPosts.map(item => {
                    const due = new Date(item.scheduled_at);
                    const isPast = due < new Date();
                    return (
                      <div key={item.id} style={{ background: item.status === 'posted' ? '#f0fdf4' : item.status === 'failed' ? '#fff5f5' : 'white', borderRadius: '12px', marginBottom: '8px', border: `1px solid ${item.status === 'posted' ? '#bbf7d0' : item.status === 'failed' ? '#fecaca' : isPast ? '#fde68a' : '#e0e7ff'}`, padding: '12px 14px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                          {/* Thumbnail */}
                          {(item.image_url || item.video_url) && (
                            <div style={{ width: '80px', minHeight: '80px', alignSelf: 'stretch', flexShrink: 0, borderRadius: '8px', overflow: 'hidden', background: '#f3f4f6', position: 'relative' }}>
                              {item.video_url ? (
                                <>
                                  <video src={item.video_url} muted style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                                    <span style={{ fontSize: '22px', color: 'white' }}>▶</span>
                                  </div>
                                </>
                              ) : (
                                <img src={item.image_url} alt="Post media" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                              )}
                            </div>
                          )}
                          {/* Content */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.status === 'posted' ? '#16a34a' : item.status === 'failed' ? '#dc2626' : (PLATFORM_COLORS[item.platform?.toLowerCase()] || '#6366f1'), flexShrink: 0 }} />
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontWeight: '700', fontSize: '13px', color: '#1f2937', textTransform: 'capitalize' }}>{item.platform.replace('_',' ')}</span>
                                    {item.status === 'posted' && <span style={{ fontSize: '11px', fontWeight: '700', color: '#15803d', background: '#dcfce7', borderRadius: '99px', padding: '1px 7px' }}>✓ Posted</span>}
                                    {item.status === 'failed' && <span style={{ fontSize: '11px', fontWeight: '700', color: '#dc2626', background: '#fee2e2', borderRadius: '99px', padding: '1px 7px' }}>✗ Failed</span>}
                                  </div>
                                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: isPast && item.status === 'pending' ? '#d97706' : '#6366f1' }}>
                                    {isPast && item.status === 'pending' ? '⚠️ Overdue · ' : ''}{due.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                {item.status === 'pending' && editingScheduledId !== item.id && (
                                  <button onClick={() => { setEditingScheduledId(item.id); setEditingScheduledText(item.content); }} style={{ background: 'none', border: '1px solid #eee', padding: '3px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#6366f1' }}>✏️ Edit</button>
                                )}
                                <button onClick={() => deleteScheduledPost(item.id)} style={{ background: 'none', border: '1px solid #eee', padding: '3px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#999' }}>Delete</button>
                              </div>
                            </div>
                            {editingScheduledId === item.id ? (
                              <>
                                <textarea value={editingScheduledText} onChange={e => setEditingScheduledText(e.target.value)} rows={4} style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #c7d2fe', fontSize: '13px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', marginBottom: '8px' }} />
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                                  <button onClick={() => saveScheduledPostEdit(item.id)} className="action-btn action-btn-primary" style={{ fontSize: '12px', padding: '7px 14px' }}>Save</button>
                                  <button onClick={() => { setEditingScheduledId(null); setEditingScheduledText(''); }} className="action-btn action-btn-ghost" style={{ fontSize: '12px', padding: '7px 14px' }}>Cancel</button>
                                </div>
                              </>
                            ) : (
                              <p style={{ margin: '0 0 8px', color: '#555', fontSize: '13px', lineHeight: '1.5' }}>{item.content}</p>
                            )}
                            <button onClick={() => handlePostNow(item.platform, item.content)} className="action-btn action-btn-primary" style={{ fontSize: '12px', padding: '7px 14px' }}>
                              🚀 Copy & post now
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Best times to post */}
              <div style={{ background: '#F5F3FF', borderRadius: '14px', padding: '16px', marginBottom: '16px' }}>
                <p style={{ margin: '0 0 12px', fontWeight: '600', fontSize: '13px', color: '#4F46E5' }}>⏰ Best times to post</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {Object.entries(BEST_TIMES).map(([platform, info]) => (
                    <div key={platform} style={{ background: 'white', borderRadius: '10px', padding: '12px', border: '1px solid #e0e7ff' }}>
                      <p style={{ margin: '0 0 4px', fontWeight: '600', fontSize: '13px', color: '#333' }}>{info.emoji} {platform}</p>
                      <p style={{ margin: '0 0 2px', fontSize: '11px', color: '#666' }}>{info.days}</p>
                      <p style={{ margin: 0, fontSize: '11px', color: '#4F46E5', fontWeight: '500' }}>{info.times}</p>
                    </div>
                  ))}
                </div>
              </div>

              {scheduleLoading && <p style={{ textAlign: 'center', color: '#888' }}>Loading...</p>}
              {!scheduleLoading && scheduledPosts.length === 0 && !calendarSelectedDay && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa' }}>
                  <p style={{ fontSize: '13px', margin: 0 }}>No scheduled posts yet — generate some and hit Schedule!</p>
                </div>
              )}
            </>
          );
        })()}

        {/* ── ASSISTANT TAB ── */}
        {!showOnboarding && activeTab === 'assistant' && (
          <div style={{ position: 'fixed', top: '112px', left: 0, right: 0, bottom: 0, background: 'white', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
            {/* Assistant header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', flexShrink: 0 }}>
              <img src="/Blinkapost bulb.png" alt="Blinka" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
              <div>
                <p style={{ margin: 0, fontWeight: '700', fontSize: '15px', color: '#111' }}>Blinka AI</p>
                <p style={{ margin: 0, fontSize: '11px', color: '#7c3aed' }}>Ask me anything about Blinkapost or social media</p>
              </div>
              {chatHistory.length > 0 && (
                <button onClick={() => setChatHistory([])} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #ddd', borderRadius: '8px', padding: '4px 10px', fontSize: '11px', color: '#888', cursor: 'pointer' }}>Clear</button>
              )}
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {chatHistory.length === 0 && (
                <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', padding: '40px 20px' }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>👋</div>
                  <p style={{ margin: '0 0 8px', fontWeight: '600', color: '#6b7280' }}>Hi, I'm Blinka!</p>
                  <p style={{ margin: 0, lineHeight: '1.6' }}>Ask me anything about Blinkapost, social media strategy, or how to get the most out of your posts.</p>
                </div>
              )}
              {chatHistory.map((msg, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: '8px', alignItems: 'flex-end' }}>
                  {msg.role === 'blinka' && <img src="/Blinkapost bulb.png" alt="" style={{ width: '22px', height: '22px', objectFit: 'contain', flexShrink: 0, marginBottom: '2px' }} />}
                  <div style={{ maxWidth: '78%', padding: '10px 14px', borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px', background: msg.role === 'user' ? 'linear-gradient(135deg, #4F46E5, #7c3aed)' : '#f3f4f6', color: msg.role === 'user' ? 'white' : '#1f2937', fontSize: '14px', lineHeight: '1.55' }}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                  <img src="/Blinkapost bulb.png" alt="" style={{ width: '22px', height: '22px', objectFit: 'contain', marginBottom: '2px' }} />
                  <div style={{ padding: '10px 14px', borderRadius: '18px 18px 18px 4px', background: '#f3f4f6', fontSize: '14px', color: '#9ca3af', display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <span style={{ animation: 'blink 1s step-start infinite' }}>●</span>
                    <span style={{ animation: 'blink 1s step-start 0.33s infinite' }}>●</span>
                    <span style={{ animation: 'blink 1s step-start 0.66s infinite' }}>●</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input area */}
            <div style={{ borderTop: '1px solid #f0f0f0', background: 'white', flexShrink: 0, padding: '10px 16px 16px' }}>
              {/* Text + send row */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                <input
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(chatInput); } }}
                  placeholder="Ask Blinka anything…"
                  style={{ flex: 1, minWidth: 0, padding: '11px 14px', borderRadius: '22px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#fafafa' }}
                />
                <button onClick={() => sendChatMessage(chatInput)} disabled={!chatInput.trim() || chatLoading}
                  style={{ background: chatInput.trim() && !chatLoading ? 'linear-gradient(135deg, #4F46E5, #7c3aed)' : '#e5e7eb', color: chatInput.trim() && !chatLoading ? 'white' : '#aaa', border: 'none', borderRadius: '50%', width: '44px', height: '44px', cursor: chatInput.trim() && !chatLoading ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '18px' }}>→</button>
              </div>
              {/* Mic row — full width, always visible */}
              <button onClick={toggleListening} title="Voice input"
                className={`mic-btn${isListening ? ' listening' : ''}`}
                style={{ width: '100%', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', background: isListening ? '#fee2e2' : '#4F46E5', color: isListening ? '#ef4444' : 'white', border: isListening ? '1.5px solid #ef4444' : '1.5px solid #4F46E5' }}>
                <img src="/Blinkapost bulb.png" alt="mic" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                {isListening ? 'Listening…' : 'Tap to speak'}
              </button>
            </div>
          </div>
        )}

        {/* ── HISTORY TAB ── */}
        {!showOnboarding && activeTab === 'accounts' && (
          <>
            <h2 style={{ color: '#333', marginBottom: '4px', marginTop: 0 }}>Connected Accounts</h2>
            <p style={{ color: '#888', marginBottom: '28px', fontSize: '14px', marginTop: 0 }}>Manage which platforms you post to</p>
            {[
              { key: 'linkedin',  label: 'LinkedIn',  color: '#0A66C2', bg: '#EFF6FF', connectFn: connectLinkedin,
                icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#0A66C2"><path d="M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.46 12.92,11.24V10.13H10.13V18.5H12.92V13.57C12.92,12.8 13.54,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,5.95 7.81,5.19 6.88,5.19A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.95,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z"/></svg> },
              { key: 'facebook',  label: 'Facebook',  color: '#1877F2', bg: '#EFF6FF', connectFn: connectFacebook,
                icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#1877F2"><path d="M17,2V2H17V6H15C14.31,6 14,6.81 14,7.5V10H14L17,10V14H14V22H10V14H7V10H10V6A4,4 0 0,1 14,2H17Z"/></svg> },
              { key: 'instagram', label: 'Instagram', color: '#E1306C', bg: '#FFF0F5', connectFn: connectInstagram,
                icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#E1306C"><path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z"/></svg> },
              { key: 'tiktok',    label: 'TikTok',    color: '#010101', bg: '#F0FFFE', connectFn: connectTikTok,
                icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#010101"><path d="M19.59,6.69A4.83,4.83 0 0,1 15.82,2.5H12.37V15.67A2.89,2.89 0 0,1 9.49,18.17C7.9,18.17 6.62,16.92 6.62,15.33C6.62,13.47 8.32,12.05 10.18,12.49V8.97A6.33,6.33 0 0,0 3.17,15.33C3.17,18.9 6.09,21.83 9.66,21.83C13.22,21.83 16.14,18.9 16.14,15.33V8.85A8.17,8.17 0 0,0 20.83,10.34V6.88A4.85,4.85 0 0,1 19.59,6.69Z"/></svg> },
            ].map(({ key, label, color, bg, connectFn, icon }) => {
              const accounts = getAccountsForPlatform(key);
              const connected = accounts.length > 0;
              return (
                <div key={key} className="acct-card">
                  <div className="acct-card-header">
                    <div className="acct-card-info">
                      <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {icon}
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '15px', color: '#1f2937' }}>{label}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: connected ? '#16a34a' : '#6b7280', background: connected ? '#dcfce7' : '#f3f4f6', borderRadius: '999px', padding: '2px 8px' }}>
                            {connected ? 'Connected' : 'Disconnected'}
                          </span>
                          {connected && <span style={{ fontSize: '11px', color: '#9ca3af' }}>{accounts.length} account{accounts.length > 1 ? 's' : ''}</span>}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => connectFn('app')} className="acct-card-connect-btn" style={{ background: connected ? '#16a34a' : color, color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                      {connected ? '+ Add account' : 'Connect'}
                    </button>
                  </div>
                  {connected && (
                    <div className="acct-rows">
                      {accounts.map(acc => (
                        <div key={acc.id} className="acct-row">
                          <div className="acct-row-left">
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#16a34a', display: 'inline-block', flexShrink: 0 }} />
                            {renamingAccountId === acc.id ? (
                              <input
                                className="acct-rename-input"
                                value={renameValue}
                                onChange={e => setRenameValue(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') renameAccount(acc.id, renameValue); if (e.key === 'Escape') { setRenamingAccountId(null); setRenameValue(''); } }}
                                autoFocus
                              />
                            ) : (
                              <span className="acct-row-name">{acc.page_name || 'Connected'}</span>
                            )}
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#16a34a', background: '#dcfce7', borderRadius: '999px', padding: '2px 8px', flexShrink: 0 }}>Connected</span>
                          </div>
                          <div className="acct-row-actions">
                            {renamingAccountId === acc.id ? (
                              <>
                                <button onClick={() => renameAccount(acc.id, renameValue)} style={{ background: '#4F46E5', color: 'white', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Save</button>
                                <button onClick={() => { setRenamingAccountId(null); setRenameValue(''); }} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#6b7280', cursor: 'pointer' }}>Cancel</button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => { setRenamingAccountId(acc.id); setRenameValue(acc.page_name || ''); }} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#6b7280', cursor: 'pointer' }}>✏️</button>
                                <button onClick={() => disconnectAccount(acc.id, key)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#6b7280', cursor: 'pointer' }}>Disconnect</button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Google Business — separate because it uses its own state, not connectedAccounts */}
            <div className="acct-card">
              <div className="acct-card-header">
                <div className="acct-card-info">
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#F0FFF4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="#34A853"><path d="M12,2C8.13,2 5,5.13 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9C19,5.13 15.87,2 12,2M12,11.5C10.62,11.5 9.5,10.38 9.5,9C9.5,7.62 10.62,6.5 12,6.5C13.38,6.5 14.5,7.62 14.5,9C14.5,10.38 13.38,11.5 12,11.5Z"/></svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '15px', color: '#1f2937' }}>Google Business</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: gbConnected ? '#16a34a' : '#6b7280', background: gbConnected ? '#dcfce7' : '#f3f4f6', borderRadius: '999px', padding: '2px 8px' }}>
                        {gbConnected ? 'Connected' : 'Disconnected'}
                      </span>
                      {gbConnected && gbLocations.length > 0 && <span style={{ fontSize: '11px', color: '#9ca3af' }}>{gbLocations.length} location{gbLocations.length > 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                </div>
                <div className="acct-card-connect-btn" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {gbConnected && gbLocations.length === 0 && <button onClick={loadGBLocations} style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Load location</button>}
                  {gbConnected && <button onClick={disconnectGB} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Disconnect</button>}
                  <button onClick={connectGB} style={{ background: gbConnected ? '#16a34a' : '#34A853', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>{gbConnected ? '+ Add' : 'Connect'}</button>
                </div>
              </div>
              {gbConnected && gbLocations.length > 0 && (
                <div className="acct-rows">
                  {gbLocations.map(loc => (
                    <div key={loc.page_id} className="acct-row" style={{ background: gbSelectedLocation?.page_id === loc.page_id ? '#f0fdf4' : '#f9fafb', border: `1px solid ${gbSelectedLocation?.page_id === loc.page_id ? '#BBF7D0' : 'transparent'}` }}>
                      <div className="acct-row-left">
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#16a34a', display: 'inline-block', flexShrink: 0 }} />
                        <span className="acct-row-name">{loc.page_name}</span>
                        {gbSelectedLocation?.page_id === loc.page_id && <span style={{ fontSize: '11px', fontWeight: '700', color: '#16a34a', background: '#dcfce7', borderRadius: '999px', padding: '2px 8px', flexShrink: 0 }}>Active</span>}
                      </div>
                      {gbSelectedLocation?.page_id !== loc.page_id && (
                        <div className="acct-row-actions">
                          <button onClick={() => setGbSelectedLocation(loc)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#6b7280', cursor: 'pointer' }}>Select</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── MISSION CONTROL TAB ── */}
        {!showOnboarding && activeTab === 'mission' && (() => {
          const MISSION_PLATFORMS = ['Instagram', 'LinkedIn', 'Facebook', 'TikTok', 'Google Business'];

          const missionSlides = [
            {
              icon: '🚀',
              color: '#f97316',
              glow: '#f9731655',
              step: '01',
              title: 'Welcome to Mission Control',
              desc: 'Your autonomous content engine. Drop in your photos, set your orbit, and Blinkapost launches your posts automatically — no manual work.',
              detail: 'Sit back. We handle the rest.',
            },
            {
              icon: '📦',
              color: '#8b5cf6',
              glow: '#8b5cf655',
              step: '02',
              title: 'Load the Payload Bay',
              desc: 'Drop up to 20 images into the Payload Bay. Each one becomes a fully written, platform-ready post — automatically.',
              detail: 'Batch your whole week of content in seconds.',
            },
            {
              icon: '🤖',
              color: '#6366f1',
              glow: '#6366f155',
              step: '03',
              title: 'AI Writes Everything',
              desc: 'Our AI reads every image and writes tailored captions for Instagram, LinkedIn, Facebook, TikTok and Google Business.',
              detail: 'One image. Five platforms. Zero writing.',
            },
            {
              icon: '📡',
              color: '#ec4899',
              glow: '#ec489955',
              step: '04',
              title: 'Set Your Orbit',
              desc: 'Pick your platforms, choose a launch date, and set how many posts fire per day. We space them perfectly.',
              detail: 'Post daily without lifting a finger.',
            },
            {
              icon: '🛸',
              color: '#10b981',
              glow: '#10b98155',
              step: '05',
              title: 'Mission in Orbit',
              desc: 'Hit Launch and your mission goes live. Track every post in real time — what\'s launched, what\'s queued, mission complete.',
              detail: 'You\'re in control. The autopilot does the flying.',
            },
          ];

          if (showMissionWalkthrough) {
            const slide = missionSlides[missionWalkthroughSlide];
            const isLast = missionWalkthroughSlide === missionSlides.length - 1;
            const dismiss = () => {
              localStorage.setItem(`mission_walkthrough_${user?.id}`, '1');
              setShowMissionWalkthrough(false);
            };
            return (
              <div style={{ minHeight: '100vh', background: 'linear-gradient(145deg, #0f0c29 0%, #302b63 60%, #24243e 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '48px 24px 36px', overflow: 'hidden', margin: '-16px -16px 0' }}>
                {/* Logo */}
                <img src="/Blinkapost logo transparent.png" alt="Blinkapost" style={{ height: '52px', objectFit: 'contain' }} />

                {/* Slide */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '360px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.15em', color: slide.color, textTransform: 'uppercase', marginBottom: '20px' }}>
                    STEP {slide.step}
                  </div>
                  <div key={missionWalkthroughSlide} style={{ width: '110px', height: '110px', borderRadius: '32px', background: `linear-gradient(135deg, ${slide.color}33, ${slide.color}11)`, border: `2px solid ${slide.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '52px', marginBottom: '28px', boxShadow: `0 0 60px ${slide.glow}`, animation: 'tutSlideIn 0.35s cubic-bezier(0.34,1.56,0.64,1)' }}>
                    {slide.icon}
                  </div>
                  <h2 style={{ color: 'white', fontSize: '26px', fontWeight: '800', margin: '0 0 14px', lineHeight: 1.2 }}>
                    {slide.title}
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '15px', lineHeight: 1.7, margin: '0 0 10px', maxWidth: '300px' }}>
                    {slide.desc}
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px', lineHeight: 1.6, margin: 0, maxWidth: '280px' }}>
                    {slide.detail}
                  </p>
                </div>

                {/* Controls */}
                <div style={{ width: '100%', maxWidth: '360px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={() => setMissionWalkthroughSlide(s => Math.max(0, s - 1))} disabled={missionWalkthroughSlide === 0}
                      style={{ width: '40px', height: '40px', borderRadius: '50%', background: missionWalkthroughSlide === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.12)', border: 'none', color: missionWalkthroughSlide === 0 ? 'rgba(255,255,255,0.2)' : 'white', fontSize: '18px', cursor: missionWalkthroughSlide === 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {missionSlides.map((_, i) => (
                        <button key={i} onClick={() => setMissionWalkthroughSlide(i)}
                          style={{ width: i === missionWalkthroughSlide ? '24px' : '8px', height: '8px', borderRadius: '999px', background: i === missionWalkthroughSlide ? 'white' : 'rgba(255,255,255,0.2)', border: 'none', padding: 0, cursor: 'pointer', transition: 'all 0.3s' }} />
                      ))}
                    </div>

                    <button onClick={() => isLast ? dismiss() : setMissionWalkthroughSlide(s => s + 1)}
                      style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                  </div>

                  <button onClick={isLast ? dismiss : () => setMissionWalkthroughSlide(s => s + 1)}
                    style={{ width: '100%', padding: '17px', background: isLast ? 'linear-gradient(135deg, #f97316, #ef4444)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: '800', cursor: 'pointer' }}>
                    {isLast ? '🚀 Launch My First Mission →' : 'Next →'}
                  </button>

                  <button onClick={dismiss} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '13px', cursor: 'pointer' }}>
                    Skip, I know what I\'m doing
                  </button>
                </div>
              </div>
            );
          }

          const STATUS_STYLES = {
            preparing: { bg: '#fef9c3', color: '#854d0e', label: '⚙️ Preparing' },
            in_orbit:  { bg: '#dbeafe', color: '#1e40af', label: '🛸 In Orbit' },
            complete:  { bg: '#dcfce7', color: '#166534', label: '✅ Mission Complete' },
          };
          return (
            <div style={{ paddingBottom: '40px' }}>
              {/* Header */}
              <div style={{ background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)', borderRadius: '16px', padding: '24px', marginBottom: '24px', color: 'white', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '-20px', right: '-20px', fontSize: '120px', opacity: 0.08 }}>🚀</div>
                <h2 style={{ margin: '0 0 6px', fontSize: '22px', fontWeight: '800' }}>🚀 Mission Control</h2>
                <p style={{ margin: 0, opacity: 0.75, fontSize: '13px' }}>Drop your payload, set your orbit, and let Blinkapost do the rest.</p>
                <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: '800' }}>{missions.length}</div>
                    <div style={{ fontSize: '11px', opacity: 0.7 }}>Total Missions</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: '800' }}>{missions.filter(m => m.status === 'in_orbit').length}</div>
                    <div style={{ fontSize: '11px', opacity: 0.7 }}>In Orbit</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: '800' }}>{missions.reduce((a, m) => a + (m.launched_count || 0), 0)}</div>
                    <div style={{ fontSize: '11px', opacity: 0.7 }}>Posts Launched</div>
                  </div>
                </div>
              </div>

              {missionStep === 'dashboard' && (
                <>
                  <button onClick={() => setMissionStep('payload')} style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg, #f97316, #ef4444)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', marginBottom: '20px', letterSpacing: '0.5px' }}>
                    🚀 New Mission
                  </button>

                  {missionLoading && <p style={{ textAlign: 'center', color: '#888' }}>Loading missions...</p>}
                  {!missionLoading && missions.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '48px 0', color: '#aaa' }}>
                      <p style={{ fontSize: '48px', margin: '0 0 12px' }}>🛸</p>
                      <p style={{ fontSize: '15px', margin: 0 }}>No missions yet — launch your first one!</p>
                    </div>
                  )}

                  {missions.map(m => {
                    const s = STATUS_STYLES[m.status] || STATUS_STYLES.preparing;
                    const progress = m.total_posts > 0 ? Math.round((m.launched_count / m.total_posts) * 100) : 0;
                    return (
                      <div key={m.id} style={{ background: 'white', borderRadius: '14px', marginBottom: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setExpandedMission(expandedMission === m.id ? null : m.id)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                              <span style={{ fontWeight: '700', fontSize: '15px', color: '#111' }}>{m.name}</span>
                              <span style={{ background: s.bg, color: s.color, fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '99px' }}>{s.label}</span>
                              {abortConfirmId === m.id ? (
                                <div style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
                                  <button onClick={() => deleteMission(m.id)} style={{ padding: '2px 10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}>Confirm abort</button>
                                  <button onClick={() => setAbortConfirmId(null)} style={{ padding: '2px 10px', background: 'white', color: '#555', border: '1px solid #e5e7eb', borderRadius: '6px', fontWeight: '600', fontSize: '11px', cursor: 'pointer' }}>Cancel</button>
                                </div>
                              ) : (
                                <button onClick={e => { e.stopPropagation(); setAbortConfirmId(m.id); }} style={{ background: 'none', border: '1px solid #fee2e2', color: '#ef4444', padding: '2px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>🛑 Abort</button>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>{m.launched_count || 0} / {m.total_posts} posts launched</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '8px' }}>
                              {(m.platforms || []).map(platform => {
                                const isComplete = m.status === 'complete';
                                const hasStarted = (m.launched_count || 0) > 0;
                                return (
                                  <span key={platform} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: '700', background: isComplete ? '#dcfce7' : hasStarted ? '#dbeafe' : '#f3f4f6', color: isComplete ? '#15803d' : hasStarted ? '#1e40af' : '#6b7280', border: `1px solid ${isComplete ? '#bbf7d0' : hasStarted ? '#bfdbfe' : '#e5e7eb'}` }}>
                                    {isComplete ? '✓' : hasStarted ? '🛸' : '○'} {platform}
                                  </span>
                                );
                              })}
                            </div>
                            <div style={{ height: '4px', background: '#f3f4f6', borderRadius: '99px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #f97316, #ef4444)', borderRadius: '99px', transition: 'width 0.4s' }} />
                            </div>
                          </div>
                          <span onClick={() => setExpandedMission(expandedMission === m.id ? null : m.id)} style={{ color: '#bbb', fontSize: '18px', marginLeft: '12px', cursor: 'pointer' }}>{expandedMission === m.id ? '▲' : '▼'}</span>
                        </div>
                        {expandedMission === m.id && (() => {
                          const PLATFORM_COLORS_M = { instagram: '#E1306C', linkedin: '#0A66C2', facebook: '#1877F2', tiktok: '#010101', google_business: '#34A853' };
                          const missionPosts = scheduledPosts.filter(p => p.mission_id === m.id);
                          // Group by date
                          const byDate = {};
                          missionPosts.forEach(p => {
                            const d = new Date(p.scheduled_at);
                            const key = d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
                            if (!byDate[key]) byDate[key] = [];
                            byDate[key].push(p);
                          });
                          return (
                            <div style={{ borderTop: '1px solid #f0f0f0', padding: '16px 20px' }}>
                              <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#555' }}>🗓 Start date: {m.start_date ? new Date(m.start_date).toLocaleDateString() : '—'}</p>
                              <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#555' }}>📡 Platforms: {(m.platforms || []).join(', ')}</p>

                              {/* Scheduled posts grouped by date */}
                              {missionPosts.length === 0 ? (
                                <p style={{ fontSize: '13px', color: '#aaa', margin: '0 0 16px' }}>No posts found for this mission.</p>
                              ) : (
                                <div style={{ marginBottom: '16px' }}>
                                  {Object.entries(byDate).map(([dateLabel, posts]) => (
                                    <div key={dateLabel} style={{ marginBottom: '12px' }}>
                                      <p style={{ margin: '0 0 8px', fontWeight: '700', fontSize: '12px', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {dateLabel} · {posts.length} post{posts.length > 1 ? 's' : ''}
                                      </p>
                                      {posts.map(item => {
                                        const due = new Date(item.scheduled_at);
                                        const isPast = due < new Date();
                                        return (
                                          <div key={item.id} style={{ background: item.status === 'posted' ? '#f0fdf4' : item.status === 'failed' ? '#fff5f5' : 'white', borderRadius: '12px', marginBottom: '8px', border: `1px solid ${item.status === 'posted' ? '#bbf7d0' : item.status === 'failed' ? '#fecaca' : isPast ? '#fde68a' : '#e0e7ff'}`, padding: '12px 14px' }}>
                                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                              {(item.image_url || item.video_url) && (
                                                <div style={{ width: '72px', minHeight: '72px', alignSelf: 'stretch', flexShrink: 0, borderRadius: '8px', overflow: 'hidden', background: '#f3f4f6', position: 'relative' }}>
                                                  {item.video_url ? (
                                                    <>
                                                      <video src={item.video_url} muted style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                                                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                                                        <span style={{ fontSize: '18px', color: 'white' }}>▶</span>
                                                      </div>
                                                    </>
                                                  ) : (
                                                    <img src={item.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                                                  )}
                                                </div>
                                              )}
                                              <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: item.status === 'posted' ? '#16a34a' : item.status === 'failed' ? '#dc2626' : (PLATFORM_COLORS_M[item.platform?.toLowerCase()] || '#6366f1'), flexShrink: 0 }} />
                                                    <span style={{ fontWeight: '700', fontSize: '12px', color: '#1f2937', textTransform: 'capitalize' }}>{item.platform.replace('_',' ')}</span>
                                                    {item.status === 'posted' && <span style={{ fontSize: '10px', fontWeight: '700', color: '#15803d', background: '#dcfce7', borderRadius: '99px', padding: '1px 6px' }}>✓ Posted</span>}
                                                    {item.status === 'failed' && <span style={{ fontSize: '10px', fontWeight: '700', color: '#dc2626', background: '#fee2e2', borderRadius: '99px', padding: '1px 6px' }}>✗ Failed</span>}
                                                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>{due.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                                                  </div>
                                                  {item.status === 'failed' && (
                                                    <button onClick={() => retryPost(item)} disabled={retryingPostId === item.id} style={{ background: retryingPostId === item.id ? '#a5b4fc' : '#4F46E5', color: 'white', border: 'none', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', flexShrink: 0 }}>
                                                      {retryingPostId === item.id ? '...' : 'Retry'}
                                                    </button>
                                                  )}
                                                </div>
                                                {editingScheduledId === item.id ? (
                                                  <>
                                                    <textarea value={editingScheduledText} onChange={e => setEditingScheduledText(e.target.value)} rows={3} style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #c7d2fe', fontSize: '12px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', margin: '6px 0' }} />
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                      <button onClick={() => saveScheduledPostEdit(item.id)} className="action-btn action-btn-primary" style={{ fontSize: '11px', padding: '5px 12px' }}>Save</button>
                                                      <button onClick={() => { setEditingScheduledId(null); setEditingScheduledText(''); }} className="action-btn action-btn-ghost" style={{ fontSize: '11px', padding: '5px 12px' }}>Cancel</button>
                                                    </div>
                                                  </>
                                                ) : (
                                                  <p style={{ margin: '4px 0 0', color: '#555', fontSize: '12px', lineHeight: '1.5' }}>{item.content}</p>
                                                )}
                                                {editingScheduledId !== item.id && (
                                                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                                                    {item.status === 'pending' && (
                                                      <button onClick={() => { setEditingScheduledId(item.id); setEditingScheduledText(item.content); }} style={{ background: 'none', border: '1px solid #e0e7ff', padding: '3px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#6366f1' }}>✏️ Edit</button>
                                                    )}
                                                    <button onClick={() => deleteScheduledPost(item.id)} style={{ background: 'none', border: '1px solid #eee', padding: '3px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#999' }}>Delete</button>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </>
              )}

              {['payload','edit','brief','preview','schedule'].includes(missionStep) && (() => {
                const WIZARD_STEPS = [
                  { key: 'payload', label: 'Load Payload' },
                  { key: 'edit',    label: 'Prepare' },
                  { key: 'brief',   label: 'Mission Brief' },
                  { key: 'preview', label: 'Preview' },
                  { key: 'schedule',label: 'Set Orbit' },
                ];
                const currentIdx = WIZARD_STEPS.findIndex(s => s.key === missionStep);
                const PLATFORM_BEST_HOURS = { Instagram: '09:00', LinkedIn: '08:00', Facebook: '13:00', TikTok: '07:00', 'Google Business': '10:00' };
                return (
                  <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #e5e7eb' }}>
                    {/* Back button */}
                    <button onClick={() => {
                      const prev = WIZARD_STEPS[currentIdx - 1];
                      if (prev) setMissionStep(prev.key); else setMissionStep('dashboard');
                    }} style={{ background: 'none', border: 'none', color: '#6366f1', fontWeight: '600', cursor: 'pointer', fontSize: '14px', padding: 0, marginBottom: '16px' }}>
                      ← {currentIdx === 0 ? 'Mission Control' : WIZARD_STEPS[currentIdx - 1].label}
                    </button>

                    {/* Step progress bar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '24px' }}>
                      {WIZARD_STEPS.map((s, i) => (
                        <React.Fragment key={s.key}>
                          <div onClick={() => { const canJump = (s.key === 'preview' || s.key === 'schedule') ? missionGeneratedPosts.length > 0 : true; if (canJump) setMissionStep(s.key); }}
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: ((s.key === 'preview' || s.key === 'schedule') ? missionGeneratedPosts.length > 0 : true) ? 'pointer' : 'default', flex: 1 }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: i < currentIdx ? '#6366f1' : i === currentIdx ? 'linear-gradient(135deg,#f97316,#ef4444)' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: i <= currentIdx ? 'white' : '#aaa', marginBottom: '4px', boxShadow: i === currentIdx ? '0 2px 8px rgba(249,115,22,0.4)' : 'none' }}>
                              {i < currentIdx ? '✓' : i + 1}
                            </div>
                            <div style={{ fontSize: '9px', fontWeight: '600', color: i === currentIdx ? '#f97316' : i < currentIdx ? '#6366f1' : '#bbb', textAlign: 'center', lineHeight: 1.2 }}>{s.label}</div>
                          </div>
                          {i < WIZARD_STEPS.length - 1 && (
                            <div style={{ height: '2px', flex: 1, background: i < currentIdx ? '#6366f1' : '#e5e7eb', marginBottom: '18px' }} />
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    {/* ── STEP 1: Load Payload ── */}
                    {missionStep === 'payload' && (
                      <>
                        <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '800' }}>📦 Load Payload</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#888' }}>Add up to 20 photos for your mission</p>
                        <div style={{ border: '2px dashed #c7d2fe', borderRadius: '12px', padding: '24px', textAlign: 'center', marginBottom: '16px', background: '#f5f3ff' }}>
                          <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#6366f1', fontWeight: '600' }}>📷 {missionMedia.length}/20 loaded</p>
                          <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#888' }}>Tap to select images from your device</p>
                          <input type="file" accept="image/*" multiple onChange={handleMissionMediaAdd} style={{ display: 'none' }} id="missionMediaInput" />
                          <label htmlFor="missionMediaInput" style={{ background: '#6366f1', color: 'white', padding: '10px 24px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700' }}>Select Images</label>
                        </div>
                        {missionMedia.length > 0 && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
                            {missionMedia.map((item, i) => (
                              <div key={i} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', aspectRatio: '1' }}>
                                <img src={item.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <button onClick={() => setMissionMedia(prev => prev.filter((_, j) => j !== i))} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <button onClick={() => setMissionStep('edit')} disabled={missionMedia.length === 0}
                          style={{ width: '100%', padding: '14px', background: missionMedia.length === 0 ? '#e5e7eb' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: missionMedia.length === 0 ? '#aaa' : 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: missionMedia.length === 0 ? 'not-allowed' : 'pointer' }}>
                          Prepare Payload →
                        </button>
                      </>
                    )}

                    {/* ── STEP 2: Prepare Payload ── */}
                    {missionStep === 'edit' && (
                      <>
                        <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '800' }}>🎨 Prepare Payload</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#888' }}>Apply colour grading and stamp your logo on every image</p>

                        {/* Filter preview */}
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '10px' }}>🎞 Colour Grading — applies to all images</label>
                        {missionMedia[0] && (
                          <div style={{ borderRadius: '10px', overflow: 'hidden', width: '100%', aspectRatio: '16/7', marginBottom: '12px', position: 'relative' }}>
                            <img src={missionMedia[0].url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: missionBatchFilter === 'none' ? 'none' : missionBatchFilter }} />
                            <div style={{ position: 'absolute', bottom: '8px', left: '8px', background: 'rgba(0,0,0,0.5)', color: 'white', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', fontWeight: '600' }}>Preview (first image)</div>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px', marginBottom: '20px' }}>
                          {CAMERA_FILTERS.map(f => (
                            <button key={f.value} onClick={() => setMissionBatchFilter(f.value)}
                              style={{ flexShrink: 0, padding: '8px 16px', borderRadius: '99px', border: `2px solid ${missionBatchFilter === f.value ? '#6366f1' : '#e5e7eb'}`, background: missionBatchFilter === f.value ? '#ede9fe' : 'white', color: missionBatchFilter === f.value ? '#4f46e5' : '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                              {f.label}
                            </button>
                          ))}
                        </div>

                        {/* Logo embed */}
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', marginBottom: '20px', background: missionLogoEnabled ? '#f5f3ff' : '#fafafa' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: missionLogoEnabled ? '14px' : 0 }}>
                            <div>
                              <div style={{ fontWeight: '700', fontSize: '14px', color: '#111' }}>🏷️ Embed Logo <span style={{ fontSize: '11px', fontWeight: '400', color: '#9ca3af' }}>— optional</span></div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Stamp your logo onto every image</div>
                            </div>
                            <button onClick={() => setMissionLogoEnabled(v => !v)} style={{ width: '44px', height: '24px', borderRadius: '99px', background: missionLogoEnabled ? '#6366f1' : '#d1d5db', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                              <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'white', position: 'absolute', top: '3px', left: missionLogoEnabled ? '23px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                            </button>
                          </div>
                          {missionLogoEnabled && (
                            <>
                              {savedLogos.length > 0 && (
                                <>
                                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Choose from saved logos:</div>
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                                    {savedLogos.map(logo => (
                                      <div key={logo.name} onClick={() => setMissionLogoSource(logo)}
                                        style={{ width: '52px', height: '52px', borderRadius: '10px', border: `2px solid ${missionLogoSource?.name === logo.name ? '#6366f1' : '#e5e7eb'}`, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', padding: '4px' }}>
                                        <img src={logo.url} alt={logo.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                                <input type="file" accept="image/*" id="missionLogoInput" style={{ display: 'none' }} onChange={handleMissionLogoUpload} />
                                <label htmlFor="missionLogoInput" style={{ padding: '7px 14px', border: '1px dashed #c7d2fe', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#6366f1', cursor: 'pointer', background: 'white' }}>
                                  {missionLogoUploading ? 'Uploading...' : '+ Upload new logo'}
                                </label>
                                {missionLogoSource && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                                    <img src={missionLogoSource.url} alt="" style={{ width: '24px', height: '24px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #e5e7eb' }} />
                                    ✓ Selected
                                  </div>
                                )}
                              </div>
                              <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Logo position:</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', maxWidth: '220px' }}>
                                {[
                                  { label: '↖', value: 'top-left' }, { label: '', value: null }, { label: '↗', value: 'top-right' },
                                  { label: '', value: null }, { label: '✛', value: 'center' }, { label: '', value: null },
                                  { label: '↙', value: 'bottom-left' }, { label: '', value: null }, { label: '↘', value: 'bottom-right' },
                                ].map((pos, i) => (
                                  pos.value ? (
                                    <button key={i} onClick={() => setMissionLogoPosition(pos.value)}
                                      style={{ padding: '8px', borderRadius: '8px', border: `2px solid ${missionLogoPosition === pos.value ? '#6366f1' : '#e5e7eb'}`, background: missionLogoPosition === pos.value ? '#ede9fe' : 'white', color: missionLogoPosition === pos.value ? '#4f46e5' : '#555', fontSize: '16px', cursor: 'pointer', fontWeight: '700' }}>
                                      {pos.label}
                                    </button>
                                  ) : <div key={i} />
                                ))}
                              </div>
                            </>
                          )}
                        </div>

                        <button onClick={() => setMissionStep('brief')}
                          style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' }}>
                          Write Mission Brief →
                        </button>
                      </>
                    )}

                    {/* ── STEP 3: Mission Brief ── */}
                    {missionStep === 'brief' && (
                      <>
                        <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '800' }}>📋 Mission Brief</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#888' }}>Name your mission, describe your goals, and choose your satellites</p>

                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>Mission Name</label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', alignItems: 'center' }}>
                          <input value={missionName} onChange={e => setMissionName(e.target.value)} placeholder='e.g. "Café Spring Campaign"' style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box' }} />
                          <button onClick={toggleMissionVoice} title="Name your mission by voice" style={{ width: '44px', height: '44px', borderRadius: '10px', border: `2px solid ${isListeningMission ? '#ef4444' : '#4F46E5'}`, background: isListeningMission ? '#fff1f2' : '#4F46E5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                            <img src="/Blinkapost bulb.png" alt="Voice" style={{ width: '26px', height: '26px', objectFit: 'contain' }} />
                            {isListeningMission && <div style={{ position: 'absolute', inset: '-4px', borderRadius: '14px', border: '2px solid #ef4444', animation: 'micPulse 1s ease-in-out infinite' }} />}
                          </button>
                        </div>
                        {isListeningMission && <p style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600', margin: '-14px 0 16px', textAlign: 'center' }}>🎙️ Listening... say your mission name</p>}

                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>📋 Describe your product & post goals <span style={{ color: '#aaa', fontWeight: '400' }}>(optional)</span></label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' }}>
                          <textarea value={missionContext} onChange={e => setMissionContext(e.target.value)} placeholder='e.g. "MMA gym, focus on strength training and discipline. Motivational tone."' rows={3} style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
                          <button onClick={toggleMissionContextVoice} title="Describe your mission by voice" style={{ width: '44px', height: '44px', borderRadius: '10px', border: `2px solid ${isListeningMissionContext ? '#ef4444' : '#4F46E5'}`, background: isListeningMissionContext ? '#fff1f2' : '#4F46E5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                            <img src="/Blinkapost bulb.png" alt="Voice" style={{ width: '26px', height: '26px', objectFit: 'contain' }} />
                            {isListeningMissionContext && <div style={{ position: 'absolute', inset: '-4px', borderRadius: '14px', border: '2px solid #ef4444', animation: 'micPulse 1s ease-in-out infinite' }} />}
                          </button>
                        </div>
                        {isListeningMissionContext && <p style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600', margin: '-2px 0 16px', textAlign: 'center' }}>🎙️ Listening... describe your mission</p>}

                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '8px', marginTop: '12px' }}>📡 Select Satellites</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
                          {MISSION_PLATFORMS.map(p => (
                            <button key={p} onClick={() => setMissionPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
                              style={{ padding: '6px 14px', borderRadius: '99px', border: `2px solid ${missionPlatforms.includes(p) ? '#6366f1' : '#e5e7eb'}`, background: missionPlatforms.includes(p) ? '#ede9fe' : 'white', color: missionPlatforms.includes(p) ? '#4f46e5' : '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                              {missionPlatforms.includes(p) ? '✓ ' : ''}{p}
                            </button>
                          ))}
                        </div>

                        {/* Per-platform account picker — only shown where more than one account is connected */}
                        {missionPlatforms.some(p => getAccountsForPlatform(p.toLowerCase().replace(' ', '_')).length > 1) && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#555' }}>🎯 Choose account per satellite</label>
                            {missionPlatforms.map(p => {
                              const key = p.toLowerCase().replace(' ', '_');
                              const accs = getAccountsForPlatform(key);
                              if (accs.length < 2) return null;
                              return (
                                <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#4f46e5', minWidth: '110px' }}>{p}</span>
                                  <select
                                    value={missionAccountIds[key] || accs[0].id}
                                    onChange={e => setMissionAccountIds(prev => ({ ...prev, [key]: e.target.value }))}
                                    style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', background: 'white', cursor: 'pointer' }}
                                  >
                                    {accs.map(a => <option key={a.id} value={a.id}>{a.page_name || a.page_id}</option>)}
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <button onClick={() => { if (!missionName.trim()) { showToast('Give your mission a name.'); return; } if (missionPlatforms.length === 0) { showToast('Select at least one satellite.'); return; } generateMissionPosts(); }}
                          disabled={missionGenerating}
                          style={{ width: '100%', padding: '14px', background: missionGenerating ? '#a5b4fc' : 'linear-gradient(135deg,#f97316,#ef4444)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: missionGenerating ? 'not-allowed' : 'pointer' }}>
                          {missionGenerating ? MISSION_GEN_MSGS[missionGenMsgIdx] : '🛰 Generate Transmissions →'}
                        </button>
                        {missionGenerating && <p style={{ textAlign: 'center', fontSize: '12px', color: '#888', marginTop: '8px' }}>Preparing for launch — this takes ~30s per image</p>}
                      </>
                    )}

                    {/* ── STEP 4: Preview Transmissions ── */}
                    {missionStep === 'preview' && (
                      <>
                        <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '800' }}>🛰 Preview Transmissions</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#888' }}>Review and edit your generated posts before launch</p>
                        {missionGeneratedPosts.map((item, i) => {
                          return (
                            <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: '12px', marginBottom: '16px', overflow: 'hidden' }}>
                              <div style={{ display: 'flex', gap: '12px', padding: '12px', background: '#fafafa', borderBottom: '1px solid #e5e7eb' }}>
                                <img src={item.imageUrl} alt="" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />
                                <div style={{ fontSize: '13px', color: '#555', fontWeight: '600', alignSelf: 'center' }}>Image {i + 1} of {missionGeneratedPosts.length}</div>
                              </div>
                              <div style={{ padding: '12px' }}>
                                {missionPlatforms.map(p => {
                                  const key = p.toLowerCase().replace(' ', '_');
                                  const text = item.generated?.[key] || item.generated?.instagram || '';
                                  return (
                                    <div key={p} style={{ marginBottom: '12px' }}>
                                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#6366f1', marginBottom: '4px' }}>{p}</div>
                                      <textarea
                                        value={text}
                                        onChange={e => setMissionGeneratedPosts(prev => prev.map((it, j) => j !== i ? it : { ...it, generated: { ...it.generated, [key]: e.target.value } }))}
                                        rows={3}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                        <button onClick={() => setMissionStep('schedule')}
                          style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' }}>
                          Set Orbit →
                        </button>
                      </>
                    )}

                    {/* ── STEP 5: Set Orbit ── */}
                    {missionStep === 'schedule' && (
                      <>
                        <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '800' }}>🌍 Set Orbit</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#888' }}>Choose when your posts enter orbit</p>

                        {/* Date + posts per day */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>🗓 Launch Date</label>
                            <input type="date" value={missionStartDate} onChange={e => setMissionStartDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box' }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>📶 Posts Per Day</label>
                            <select value={missionPostsPerDay} onChange={e => setMissionPostsPerDay(Number(e.target.value))} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box' }}>
                              {[1,2,3].map(n => <option key={n} value={n}>{n} per day</option>)}
                            </select>
                          </div>
                        </div>

                        {/* Timing mode toggle */}
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '8px' }}>⏰ Posting Time</label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                          {[{ key: 'same', label: '🌍 Same for all' }, { key: 'individual', label: '📡 Per satellite' }].map(opt => (
                            <button key={opt.key} onClick={() => setMissionTimingMode(opt.key)}
                              style={{ flex: 1, padding: '8px', borderRadius: '8px', border: `2px solid ${missionTimingMode === opt.key ? '#6366f1' : '#e5e7eb'}`, background: missionTimingMode === opt.key ? '#ede9fe' : 'white', color: missionTimingMode === opt.key ? '#4f46e5' : '#555', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                              {opt.label}
                            </button>
                          ))}
                        </div>

                        {missionTimingMode === 'same' && (
                          <div style={{ marginBottom: '4px' }}>
                            <input type="time" value={missionLaunchTime} onChange={e => setMissionLaunchTime(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box', marginBottom: '8px' }} />
                            {missionPlatforms.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
                                {missionPlatforms.map(p => (
                                  <button key={p} onClick={() => setMissionLaunchTime(PLATFORM_BEST_HOURS[p] || '09:00')}
                                    style={{ padding: '4px 10px', borderRadius: '99px', border: `2px solid ${missionLaunchTime === (PLATFORM_BEST_HOURS[p] || '09:00') ? '#6366f1' : '#e5e7eb'}`, background: missionLaunchTime === (PLATFORM_BEST_HOURS[p] || '09:00') ? '#ede9fe' : 'white', color: missionLaunchTime === (PLATFORM_BEST_HOURS[p] || '09:00') ? '#4f46e5' : '#555', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                                    Best: {p} ({PLATFORM_BEST_HOURS[p] || '09:00'})
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {missionTimingMode === 'individual' && (
                          <div style={{ marginBottom: '4px' }}>
                            {missionPlatforms.map(p => (
                              <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#555', minWidth: '110px' }}>{p}</span>
                                <input type="time" value={missionPlatformTimes[p] || PLATFORM_BEST_HOURS[p] || '09:00'}
                                  onChange={e => setMissionPlatformTimes(prev => ({ ...prev, [p]: e.target.value }))}
                                  style={{ flex: 1, padding: '7px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', boxSizing: 'border-box' }} />
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Timezone */}
                        <select value={missionTimezone} onChange={e => setMissionTimezone(e.target.value)}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #c7d2fe', fontSize: '13px', color: '#4f46e5', background: '#ede9fe', fontWeight: '600', cursor: 'pointer', boxSizing: 'border-box', marginTop: '8px', marginBottom: '4px' }}>
                          {!COMMON_TIMEZONES.find(t => t.value === missionTimezone) && (
                            <option value={missionTimezone}>📍 {missionTimezone}</option>
                          )}
                          {COMMON_TIMEZONES.map(tz => (
                            <option key={tz.value} value={tz.value}>📍 {tz.label}</option>
                          ))}
                        </select>
                        <p style={{ margin: '4px 0 20px', fontSize: '11px', color: '#aaa' }}>Detected from your device — change if your business is in a different timezone.</p>

                        {/* Post count */}
                        {missionGeneratedPosts.length > 0 && missionPlatforms.length > 0 && (
                          <p style={{ textAlign: 'center', fontSize: '13px', color: '#888', marginBottom: '16px' }}>
                            {missionGeneratedPosts.length} images × {missionPlatforms.length} satellites = <strong style={{ color: '#6366f1' }}>{missionGeneratedPosts.length * missionPlatforms.length} posts</strong> will enter orbit
                          </p>
                        )}

                        {/* Launch button */}
                        <button onClick={handleMissionLaunch} disabled={missionLaunching}
                          style={{ width: '100%', padding: '16px', background: missionLaunching ? '#a5b4fc' : 'linear-gradient(135deg,#f97316,#ef4444)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: missionLaunching ? 'not-allowed' : 'pointer', letterSpacing: '0.5px' }}>
                          {missionLaunching ? MISSION_LAUNCH_MSGS[missionMsgIdx] : '🚀 Launch Mission'}
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {!showOnboarding && activeTab === 'history' && (
          <>
            <h2 style={{ color: '#333', marginBottom: '8px', marginTop: 0 }}>Post history</h2>
            <p style={{ color: '#888', marginBottom: '32px', fontSize: '14px', marginTop: 0 }}>All your previously generated posts</p>
            {historyLoading && <p style={{ textAlign: 'center', color: '#888' }}>Loading...</p>}
            {!historyLoading && history.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>
                <p style={{ fontSize: '40px', margin: '0 0 12px' }}>📋</p>
                <p style={{ fontSize: '15px', margin: 0 }}>No posts yet — create some on the Create tab!</p>
              </div>
            )}
            {history.map(item => (
              <div key={item.id} style={{ background: 'white', borderRadius: '12px', marginBottom: '12px', border: '1px solid #eee', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer' }} onClick={() => setExpandedPost(expandedPost === item.id ? null : item.id)}>
                  <div>
                    <p style={{ margin: 0, fontSize: '14px', color: '#333', fontWeight: '500' }}>{formatDate(item.created_at)}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#999' }}>Instagram · LinkedIn · Facebook · TikTok</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button onClick={e => { e.stopPropagation(); deleteFromHistory(item.id); }} style={{ background: 'none', border: '1px solid #eee', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#999' }}>Delete</button>
                    <span style={{ color: '#bbb', fontSize: '18px' }}>{expandedPost === item.id ? '▲' : '▼'}</span>
                  </div>
                </div>
                {expandedPost === item.id && (
                  <div style={{ borderTop: '1px solid #f0f0f0', padding: '16px 20px' }}>
                    {[['Instagram', item.instagram], ['LinkedIn', item.linkedin], ['Facebook', item.facebook], ['TikTok', item.tiktok]].map(([platform, post]) => (
                      <div key={platform} style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#555' }}>{platform}</span>
                          <button onClick={() => navigator.clipboard.writeText(post)} style={{ background: 'none', border: '1px solid #ddd', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#666' }}>Copy</button>
                        </div>
                        <p style={{ margin: 0, color: '#555', fontSize: '13px', lineHeight: '1.6' }}>{post}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* ── Hamburger menu drawer ── */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div onClick={() => { setShowMenu(false); setMenuSection(null); setDeleteConfirm(false); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />

          {/* Drawer */}
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '280px', maxWidth: '85vw', background: 'white', zIndex: 1001, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)' }}>

            {/* Drawer header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid #f0f0f0' }}>
              {menuSection ? (
                <button onClick={() => setMenuSection(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4F46E5', fontSize: '14px', fontWeight: '600', padding: 0 }}>← Back</button>
              ) : (
                <span style={{ fontWeight: '700', fontSize: '16px', color: '#111' }}>Menu</span>
              )}
              <button onClick={() => { setShowMenu(false); setMenuSection(null); setDeleteConfirm(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#888', lineHeight: 1 }}>✕</button>
            </div>

            {/* Main menu list */}
            {!menuSection && (
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {[
                  { icon: '🔗', label: 'Accounts', badge: (() => { const n = ['linkedin','facebook','instagram','tiktok'].filter(p => getAccountsForPlatform(p).length > 0).length + (gbConnected ? 1 : 0); return n || null; })(), action: () => { setActiveTab('accounts'); setShowMenu(false); } },
                  { icon: '📋', label: 'History',  badge: history.length || null,           action: () => { setActiveTab('history');  setShowMenu(false); } },
                  { icon: '⚙️', label: 'Settings',  badge: null,  action: () => { setMenuSection('settings'); setSettingsTab('language'); } },
                  { icon: '🚀', label: 'Pro Plan',  badge: 'NEW', action: () => setMenuSection('pro') },
                  { icon: '💳', label: 'Subscription', badge: null, action: () => setMenuSection('subscription') },
                  { icon: '🔒', label: 'Privacy',  badge: null, action: () => setMenuSection('privacy') },
                  { icon: 'ℹ️',  label: 'About',    badge: null, action: () => setMenuSection('about')   },
                  ...(user?.email === 'briel.ptcast@gmail.com' ? [{ icon: '🛠️', label: 'Admin', badge: null, action: () => setMenuSection('admin') }] : []),
                ].map(item => (
                  <button key={item.label} onClick={item.action}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px', background: 'none', border: 'none', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontSize: '20px', width: '28px', textAlign: 'center' }}>{item.icon}</span>
                    <span style={{ flex: 1, fontSize: '15px', fontWeight: '500', color: '#222' }}>{item.label}</span>
                    {item.badge && (typeof item.badge === 'string' ? <span style={{ background: 'linear-gradient(135deg, #f97316, #ef4444)', color: 'white', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em' }}>{item.badge}</span> : item.badge > 0 ? <span style={{ background: '#4F46E5', color: 'white', borderRadius: '12px', padding: '2px 8px', fontSize: '12px', fontWeight: '600' }}>{item.badge}</span> : null)}
                    <span style={{ color: '#ccc', fontSize: '16px' }}>›</span>
                  </button>
                ))}

                <div style={{ borderTop: '1px solid #f0f0f0', marginTop: '8px', padding: '8px 0' }}>
                  <button onClick={handleLogout}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontSize: '20px', width: '28px', textAlign: 'center' }}>🚪</span>
                    <span style={{ fontSize: '15px', fontWeight: '500', color: '#ef4444' }}>Log out</span>
                  </button>
                </div>
              </div>
            )}

            {/* Privacy sub-section */}
            {menuSection === 'privacy' && (
              <iframe src="/privacy" title="Privacy Policy"
                style={{ flex: 1, border: 'none', width: '100%' }} />
            )}

            {/* About sub-section */}
            {menuSection === 'about' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <img src="/Blinkapost logo.png" alt="Blinkapost" style={{ height: '48px', objectFit: 'contain', marginBottom: '12px' }} />
                  <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#111', margin: '0 0 8px' }}>Blinkapost</h2>
                  <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>AI-powered social media posting</p>
                </div>

                <div style={{ background: '#F5F3FF', borderRadius: '14px', padding: '18px', marginBottom: '16px' }}>
                  <p style={{ fontSize: '14px', color: '#4F46E5', fontWeight: '600', margin: '0 0 8px' }}>What is Blinkapost?</p>
                  <p style={{ fontSize: '14px', color: '#555', margin: 0, lineHeight: '1.6' }}>
                    Drop a photo or video and watch the magic happen — our AI instantly writes scroll-stopping captions for Instagram, LinkedIn, Facebook, TikTok and Google Business. Edit, brand it, and post everywhere in one tap.
                  </p>
                </div>

                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '14px', padding: '18px', marginBottom: '16px' }}>
                  <p style={{ fontSize: '14px', color: '#16a34a', fontWeight: '600', margin: '0 0 6px' }}>Why Blinkapost?</p>
                  <p style={{ fontSize: '13px', color: '#555', margin: 0, lineHeight: '1.6' }}>
                    Buffer, Hootsuite, and Metricool let you schedule posts — but you still have to write them. Blinkapost writes them <em>for you</em>, from your own images and videos.
                  </p>
                </div>

                <div style={{ borderRadius: '14px', overflow: 'hidden', marginBottom: '16px' }}>
                  {[
                    { icon: '📸', color: '#6366f1', bg: '#eef2ff', step: '1', title: 'Upload Your Media', desc: 'Photo or video from your device' },
                    { icon: '✏️', color: '#8b5cf6', bg: '#f5f3ff', step: '2', title: 'Edit & Describe', desc: 'Crop, filter, add your logo & context' },
                    { icon: '🤖', color: '#a855f7', bg: '#faf5ff', step: '3', title: 'AI Writes Your Posts', desc: 'Captions for every platform instantly' },
                    { icon: '🚀', color: '#ec4899', bg: '#fdf2f8', step: '4', title: 'Post Everywhere', desc: 'Publish to all platforms in one tap' },
                  ].map(({ icon, color, bg, step, title, desc }) => (
                    <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '14px', background: bg, padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>{icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>{title}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{desc}</div>
                      </div>
                      <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '800', color: 'white', flexShrink: 0 }}>{step}</div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { setMenuSection(null); setShowMenu(false); setTutorialSlide(0); setOnboardingStep(0); setShowOnboarding(true); }}
                  style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '8px' }}
                >
                  ▶ Replay tutorial
                </button>

                <p style={{ textAlign: 'center', fontSize: '12px', color: '#ccc', marginTop: '24px' }}>Version 1.0 · blinkapost.com</p>
              </div>
            )}

            {/* Settings section */}
            {menuSection === 'settings' && (() => {
              const filteredLangs = langSearch
                ? LANGUAGES.filter(l => l.name.toLowerCase().includes(langSearch.toLowerCase()))
                : LANGUAGES;
              const priorityLangs = filteredLangs.filter(l => l.priority);
              const otherLangs    = filteredLangs.filter(l => !l.priority);
              return (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  {/* Tab bar */}
                  <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
                    {[
                      { key: 'language',  label: '🌍 Lang' },
                      { key: 'tone',      label: '🎭 Tone' },
                      { key: 'poststyle', label: '✍️ Style' },
                      { key: 'hashtags',  label: '# Tags' },
                    ].map(tab => (
                      <button key={tab.key} onClick={() => setSettingsTab(tab.key)}
                        style={{ flex: 1, padding: '12px 4px', background: 'none', border: 'none', borderBottom: settingsTab === tab.key ? '2px solid #4F46E5' : '2px solid transparent', color: settingsTab === tab.key ? '#4F46E5' : '#9ca3af', fontSize: '11px', fontWeight: settingsTab === tab.key ? '700' : '500', cursor: 'pointer' }}>
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Tab content */}
                  <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>

                    {/* ── Language tab ── */}
                    {settingsTab === 'language' && (
                      <>
                        <p style={{ fontSize: '12px', color: '#888', margin: '0 0 10px' }}>AI posts will be written in your chosen language.</p>
                        <input
                          type="text"
                          placeholder="Search languages..."
                          value={langSearch}
                          onChange={e => setLangSearch(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', marginBottom: '8px', outline: 'none', boxSizing: 'border-box' }}
                        />
                        <div style={{ border: '1px solid #f0f0f0', borderRadius: '10px', overflow: 'hidden' }}>
                          {priorityLangs.map(lang => (
                            <button key={lang.name} onClick={() => setUserLanguage(lang.name)}
                              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 12px', background: userLanguage === lang.name ? '#F5F3FF' : 'white', border: 'none', borderLeft: userLanguage === lang.name ? '3px solid #4F46E5' : '3px solid transparent', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', textAlign: 'left' }}>
                              <span style={{ fontSize: '18px' }}>{lang.flag}</span>
                              <span style={{ flex: 1, fontSize: '13px', fontWeight: userLanguage === lang.name ? '700' : '400', color: '#1f2937' }}>{lang.name}</span>
                              {userLanguage === lang.name && <span style={{ color: '#4F46E5', fontSize: '14px' }}>✓</span>}
                            </button>
                          ))}
                          {!langSearch && priorityLangs.length > 0 && otherLangs.length > 0 && (
                            <div style={{ height: '1px', background: '#f0f0f0' }} />
                          )}
                          {otherLangs.map(lang => (
                            <button key={lang.name} onClick={() => setUserLanguage(lang.name)}
                              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 12px', background: userLanguage === lang.name ? '#F5F3FF' : 'white', border: 'none', borderLeft: userLanguage === lang.name ? '3px solid #4F46E5' : '3px solid transparent', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', textAlign: 'left' }}>
                              <span style={{ fontSize: '18px' }}>{lang.flag}</span>
                              <span style={{ flex: 1, fontSize: '13px', fontWeight: userLanguage === lang.name ? '700' : '400', color: '#1f2937' }}>{lang.name}</span>
                              {userLanguage === lang.name && <span style={{ color: '#4F46E5', fontSize: '14px' }}>✓</span>}
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                    {/* ── AI Tone tab ── */}
                    {settingsTab === 'tone' && (
                      <>
                        <p style={{ fontSize: '12px', color: '#888', margin: '0 0 4px' }}>
                          {aiTone ? 'Your AI will default to this tone for every post.' : 'No preference set — AI uses its own judgment based on your content.'}
                        </p>
                        {aiTone && (
                          <button onClick={() => setAiTone(null)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '12px', cursor: 'pointer', padding: '0 0 10px', textDecoration: 'underline' }}>Clear selection</button>
                        )}
                        {!aiTone && <div style={{ marginBottom: '12px' }} />}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          {AI_TONES.map(tone => (
                            <button key={tone.slug} onClick={() => setAiTone(aiTone === tone.slug ? null : tone.slug)}
                              style={{ padding: '14px 10px', borderRadius: '12px', border: aiTone === tone.slug ? '2px solid #4F46E5' : '2px solid #e5e7eb', background: aiTone === tone.slug ? '#F5F3FF' : 'white', cursor: 'pointer', textAlign: 'left' }}>
                              <div style={{ fontSize: '22px', marginBottom: '6px' }}>{tone.icon}</div>
                              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', marginBottom: '3px' }}>{tone.label}</div>
                              <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.4 }}>{tone.desc}</div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                    {/* ── Post Style tab ── */}
                    {settingsTab === 'poststyle' && (() => {
                      const filled = [bizName, bizIndustry, bizDescription, bizGoal, bizAudience, contentPillars, preferredCta, keyProducts, postingStyle, postingExamples].filter(Boolean).length;
                      const totalFields = 10;
                      return (
                        <>
                          {/* Voice Setup CTA */}
                          <button onClick={startVoiceSetup}
                            style={{ width: '100%', padding: '14px', background: 'linear-gradient(145deg, #0f0c29, #302b63)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '14px' }}>
                            <img src="/Blinkapost bulb.png" alt="mic" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                            Set Up With Voice
                          </button>
                          <p style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', margin: '-8px 0 14px' }}>Answer 5 questions out loud — or fill in manually below</p>

                          {/* Completion bar */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', background: '#F5F3FF', borderRadius: '10px', padding: '10px 12px' }}>
                            <div style={{ flex: 1, height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${(filled / totalFields) * 100}%`, background: 'linear-gradient(90deg, #4F46E5, #7c3aed)', borderRadius: '3px', transition: 'width 0.3s' }} />
                            </div>
                            <span style={{ fontSize: '12px', color: '#4F46E5', fontWeight: '600', whiteSpace: 'nowrap' }}>{filled}/{totalFields} — more detail = better posts</span>
                          </div>

                          {/* Business name + industry row */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                            <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px' }}>
                              <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Business name</p>
                              <input value={bizName} onChange={e => setBizName(e.target.value)} maxLength={80}
                                style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                                placeholder="e.g. Iron Peak Gym" />
                            </div>
                            <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px' }}>
                              <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Industry / niche</p>
                              <input value={bizIndustry} onChange={e => setBizIndustry(e.target.value)} maxLength={80}
                                style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                                placeholder="e.g. Fitness & Wellness" />
                            </div>
                          </div>

                          {/* Business description */}
                          <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
                            <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Tell me about your business</p>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>What you do, what you sell, what makes you different</p>
                            <textarea value={bizDescription} onChange={e => setBizDescription(e.target.value)} maxLength={500} rows={3}
                              style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                              placeholder="e.g. We're a Sydney-based gym offering strength training for busy professionals..." />
                            <div style={{ textAlign: 'right', fontSize: '11px', color: bizDescription.length > 450 ? '#ef4444' : bizDescription.length > 380 ? '#f59e0b' : '#9ca3af', marginTop: '3px' }}>{bizDescription.length}/500</div>
                          </div>

                          {/* Key products/services */}
                          <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
                            <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Key products / services</p>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>What do you actually sell or offer?</p>
                            <textarea value={keyProducts} onChange={e => setKeyProducts(e.target.value)} maxLength={300} rows={2}
                              style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                              placeholder="e.g. 6-week strength program, personal training sessions, online coaching..." />
                            <div style={{ textAlign: 'right', fontSize: '11px', color: keyProducts.length > 265 ? '#ef4444' : keyProducts.length > 225 ? '#f59e0b' : '#9ca3af', marginTop: '3px' }}>{keyProducts.length}/300</div>
                          </div>

                          {/* Goals */}
                          <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
                            <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>What do you look to achieve?</p>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>Brand awareness, sales, leads, community?</p>
                            <textarea value={bizGoal} onChange={e => setBizGoal(e.target.value)} maxLength={300} rows={2}
                              style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                              placeholder="e.g. Drive new memberships and build a community of consistent trainers..." />
                            <div style={{ textAlign: 'right', fontSize: '11px', color: bizGoal.length > 265 ? '#ef4444' : bizGoal.length > 225 ? '#f59e0b' : '#9ca3af', marginTop: '3px' }}>{bizGoal.length}/300</div>
                          </div>

                          {/* Target audience */}
                          <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
                            <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Who is your target market?</p>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>Age, location, interests, profession</p>
                            <textarea value={bizAudience} onChange={e => setBizAudience(e.target.value)} maxLength={300} rows={2}
                              style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                              placeholder="e.g. Men and women aged 25–45, professionals in Sydney CBD, interested in fitness..." />
                            <div style={{ textAlign: 'right', fontSize: '11px', color: bizAudience.length > 265 ? '#ef4444' : bizAudience.length > 225 ? '#f59e0b' : '#9ca3af', marginTop: '3px' }}>{bizAudience.length}/300</div>
                          </div>

                          {/* Content pillars */}
                          <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
                            <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Content pillars</p>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>3–5 recurring themes your posts revolve around</p>
                            <textarea value={contentPillars} onChange={e => setContentPillars(e.target.value)} maxLength={300} rows={2}
                              style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                              placeholder="e.g. Client results, gym culture, fitness tips, behind the scenes, promotions..." />
                            <div style={{ textAlign: 'right', fontSize: '11px', color: contentPillars.length > 265 ? '#ef4444' : contentPillars.length > 225 ? '#f59e0b' : '#9ca3af', marginTop: '3px' }}>{contentPillars.length}/300</div>
                          </div>

                          {/* Preferred CTA + words to avoid row */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                            <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px' }}>
                              <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Preferred CTA</p>
                              <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>What action should people take?</p>
                              <input value={preferredCta} onChange={e => setPreferredCta(e.target.value)} maxLength={100}
                                style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                                placeholder="e.g. DM us, link in bio, book a call" />
                            </div>
                            <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px' }}>
                              <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Words to avoid</p>
                              <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>Phrases that feel off-brand</p>
                              <input value={avoidWords} onChange={e => setAvoidWords(e.target.value)} maxLength={150}
                                style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                                placeholder="e.g. hustle, grind, crushing it" />
                            </div>
                          </div>

                          {/* Posting style */}
                          <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
                            <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Describe your posting style</p>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>Direct? Storytelling? Humour? Inspirational?</p>
                            <textarea value={postingStyle} onChange={e => setPostingStyle(e.target.value)} maxLength={500} rows={3}
                              style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                              placeholder="e.g. Punchy and direct. Short sentences. Real talk, no fluff. Occasional humour..." />
                            <div style={{ textAlign: 'right', fontSize: '11px', color: postingStyle.length > 450 ? '#ef4444' : postingStyle.length > 380 ? '#f59e0b' : '#9ca3af', marginTop: '3px' }}>{postingStyle.length}/500</div>
                          </div>

                          {/* Writing examples */}
                          <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
                            <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Paste captions you love <span style={{ fontWeight: '400', color: '#9ca3af' }}>(optional)</span></p>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 4px' }}>Your own past posts, or ones you wish you'd written</p>
                            <p style={{ fontSize: '11px', color: '#4F46E5', fontWeight: '600', margin: '0 0 8px' }}>★ Most powerful field — teaches the AI your vocabulary, rhythm and style</p>
                            <textarea value={postingExamples} onChange={e => setPostingExamples(e.target.value)} maxLength={1000} rows={4}
                              style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                              placeholder="Paste 2–3 example captions here..." />
                            <div style={{ textAlign: 'right', fontSize: '11px', color: postingExamples.length > 900 ? '#ef4444' : postingExamples.length > 750 ? '#f59e0b' : '#9ca3af', marginTop: '3px' }}>{postingExamples.length}/1000</div>
                          </div>
                        </>
                      );
                    })()}

                    {/* ── Hashtags tab ── */}
                    {settingsTab === 'hashtags' && (
                      <>
                        <p style={{ fontSize: '12px', color: '#888', margin: '0 0 12px' }}>Add your go-to hashtags. AI will prioritise them when "Add hashtags" is on.</p>

                        {/* Saved hashtags textarea */}
                        <div style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', margin: '0 0 2px' }}>Your hashtags</p>
                          <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>Separate with spaces or commas</p>
                          <textarea value={savedHashtags} onChange={e => setSavedHashtags(e.target.value)} maxLength={500} rows={4}
                            style={{ width: '100%', padding: '9px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                            placeholder="#fitness #gym #sydneygym #strengthtraining #personaltraining..." />
                          <div style={{ textAlign: 'right', fontSize: '11px', color: savedHashtags.length > 450 ? '#ef4444' : '#9ca3af', marginTop: '3px' }}>{savedHashtags.length}/500</div>
                        </div>

                        {/* AI suggest button */}
                        <button onClick={suggestHashtags} disabled={hashtagsLoading}
                          style={{ width: '100%', padding: '12px', background: hashtagsLoading ? '#e5e7eb' : 'linear-gradient(135deg, #f97316, #ea580c)', color: hashtagsLoading ? '#9ca3af' : 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: hashtagsLoading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '14px' }}>
                          {hashtagsLoading ? (
                            <><div style={{ width: '14px', height: '14px', border: '2px solid #ccc', borderTop: '2px solid #666', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />Thinking...</>
                          ) : (
                            <><img src="/Blinkapost bulb.png" alt="" style={{ width: '16px', height: '16px', objectFit: 'contain' }} />✨ Suggest hashtags with AI</>
                          )}
                        </button>

                        {/* Suggested chips */}
                        {suggestedHashtags.length > 0 && (
                          <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '12px', marginBottom: '12px' }}>
                            <p style={{ fontSize: '12px', fontWeight: '600', color: '#4F46E5', margin: '0 0 10px' }}>Tap to add to your list:</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {suggestedHashtags.map(tag => {
                                const already = savedHashtags.toLowerCase().includes(tag.toLowerCase());
                                return (
                                  <button key={tag} onClick={() => { if (!already) setSavedHashtags(s => s ? `${s.trim()} ${tag}` : tag); }}
                                    style={{ padding: '6px 12px', borderRadius: '99px', border: already ? '1.5px solid #10b981' : '1.5px solid #4F46E5', background: already ? '#ecfdf5' : '#F5F3FF', color: already ? '#059669' : '#4F46E5', fontSize: '12px', fontWeight: '600', cursor: already ? 'default' : 'pointer' }}>
                                    {already ? '✓ ' : '+ '}{tag}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Sticky save button */}
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
                    <button onClick={() => saveUserSettings()}
                      style={{ width: '100%', padding: '13px', background: settingsSaved ? '#10b981' : 'linear-gradient(135deg, #4F46E5, #7c3aed)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', transition: 'background 0.3s' }}>
                      {settingsSaved ? '✓ Saved' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Subscription section */}
            {/* Pro Plan section */}
            {menuSection === 'pro' && (
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {/* Hero */}
                <div style={{ background: 'linear-gradient(145deg, #0f0c29, #302b63, #24243e)', padding: '32px 20px 28px', color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: '-10px', right: '-10px', fontSize: '100px', opacity: 0.06 }}>🚀</div>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>🚀</div>
                  <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '800' }}>Blinkapost Pro</h2>
                  <p style={{ margin: '0 0 16px', opacity: 0.7, fontSize: '13px', lineHeight: 1.6 }}>Your autonomous content engine. Set it in orbit and let it fly.</p>
                  <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.1)', borderRadius: '99px', padding: '6px 16px', fontSize: '13px', fontWeight: '600' }}>Coming Soon · $19/mo</div>
                </div>

                <div style={{ padding: '24px 20px' }}>
                  {/* Feature list */}
                  {[
                    { icon: '🛸', title: 'Mission Control', desc: 'Bulk upload 20 images, AI generates all posts, auto-schedules and posts everything autonomously.' },
                    { icon: '📊', title: 'Analytics Dashboard', desc: 'See your best performing posts, engagement rates and platform breakdowns.' },
                    { icon: '🌏', title: 'Best Times by Country', desc: 'Get region-specific posting time recommendations for AU, US, UK, CA and more.' },
                    { icon: '♻️', title: 'Evergreen Recycling', desc: 'Automatically re-post your top performing content on a rolling schedule.' },
                    { icon: '👥', title: 'Agency & Team Mode', desc: 'Manage multiple clients, approval workflows and seat-based billing.' },
                    { icon: '🏷️', title: 'White Label', desc: 'Full rebrand with your logo, colours and custom domain. Resell to your clients.' },
                  ].map(f => (
                    <div key={f.title} style={{ display: 'flex', gap: '14px', marginBottom: '20px', alignItems: 'flex-start' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>{f.icon}</div>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#111', marginBottom: '3px' }}>{f.title}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.6 }}>{f.desc}</div>
                      </div>
                    </div>
                  ))}

                  {/* Pricing cards */}
                  <div style={{ marginTop: '8px', marginBottom: '20px' }}>
                    {[
                      { name: 'Pro', price: '$19', period: '/mo', desc: 'For creators & businesses', color: '#6366f1', features: ['Mission Control', 'Analytics', 'Country best times', 'All platforms'] },
                      { name: 'Agency', price: '$49', period: '/mo + $9/seat', desc: 'For agencies & freelancers', color: '#f97316', features: ['Everything in Pro', 'Client management', 'Approval workflow', 'Seat billing'] },
                      { name: 'White Label', price: '$99', period: '/mo + $9/seat', desc: 'Full rebrand', color: '#8b5cf6', features: ['Everything in Agency', 'Custom domain', 'Your logo & colours', 'Remove Blinkapost branding'] },
                    ].map(plan => (
                      <div key={plan.name} style={{ border: `2px solid ${plan.color}33`, borderRadius: '14px', padding: '16px', marginBottom: '12px', background: `${plan.color}08` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                          <div>
                            <div style={{ fontWeight: '800', fontSize: '16px', color: '#111' }}>{plan.name}</div>
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>{plan.desc}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <span style={{ fontWeight: '800', fontSize: '20px', color: plan.color }}>{plan.price}</span>
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>{plan.period}</span>
                          </div>
                        </div>
                        {plan.features.map(f => <div key={f} style={{ fontSize: '12px', color: '#374151', marginBottom: '4px' }}>✓ {f}</div>)}
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div style={{ background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', borderRadius: '14px', padding: '20px', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 6px', fontWeight: '700', fontSize: '14px', color: '#4f46e5' }}>🛸 Be first on the launchpad</p>
                    <p style={{ margin: '0 0 14px', fontSize: '12px', color: '#6b7280', lineHeight: 1.6 }}>Pro is launching soon. Early users get a free month and locked-in pricing forever.</p>
                    <button onClick={() => { showToast("You're on the list! We'll notify you at launch. 🚀"); }} style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
                      Notify Me at Launch
                    </button>
                  </div>
                </div>
              </div>
            )}

            {menuSection === 'subscription' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#111', margin: '0 0 4px' }}>Subscription</h2>
                <p style={{ fontSize: '13px', color: '#888', margin: '0 0 24px' }}>Manage your Blinkapost plan</p>

                {/* Current plan card */}
                <div style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', borderRadius: '16px', padding: '22px 20px', marginBottom: '16px', color: 'white' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', opacity: 0.8 }}>CURRENT PLAN</span>
                    <span style={{ fontSize: '11px', fontWeight: '700', background: '#86efac', color: '#14532d', borderRadius: '999px', padding: '3px 10px' }}>● Active</span>
                  </div>
                  <div style={{ fontSize: '26px', fontWeight: '800', marginBottom: '4px' }}>Free</div>
                  <div style={{ fontSize: '13px', opacity: 0.75 }}>Unlimited AI post generation</div>
                </div>

                {/* What's included */}
                <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px 18px', marginBottom: '16px' }}>
                  <p style={{ fontSize: '12px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 12px' }}>What's included</p>
                  {[
                    '✓  AI post generation from images & videos',
                    '✓  Post to Instagram, Facebook, LinkedIn, TikTok',
                    '✓  Post history',
                    '✓  Brand voice settings',
                    '✓  Logo upload',
                  ].map(item => (
                    <div key={item} style={{ fontSize: '13px', color: '#374151', marginBottom: '8px' }}>{item}</div>
                  ))}
                </div>

                {/* Paid plans coming soon */}
                <div style={{ background: '#f5f3ff', border: '1px solid #e0e7ff', borderRadius: '12px', padding: '16px 18px', marginBottom: '24px' }}>
                  <p style={{ fontSize: '13px', fontWeight: '700', color: '#6366f1', margin: '0 0 4px' }}>Pro plan coming soon</p>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, lineHeight: 1.6 }}>Scheduled posting, analytics, bulk upload and team collaboration. You'll be notified when it launches.</p>
                </div>

                {/* Delete account */}
                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '20px' }}>
                  <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px', lineHeight: 1.6 }}>Want to leave? Deleting your account permanently removes all your data, connected accounts, and post history.</p>
                  {!deleteConfirm ? (
                    <button
                      onClick={() => setDeleteConfirm(true)}
                      style={{ width: '100%', padding: '12px', background: 'none', border: '1px solid #fca5a5', borderRadius: '10px', fontSize: '13px', fontWeight: '600', color: '#ef4444', cursor: 'pointer' }}
                    >
                      Delete my account & data
                    </button>
                  ) : (
                    <div style={{ background: '#fff1f2', border: '1px solid #fca5a5', borderRadius: '12px', padding: '16px' }}>
                      <p style={{ fontSize: '13px', fontWeight: '700', color: '#dc2626', margin: '0 0 6px' }}>Are you sure?</p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 14px', lineHeight: 1.6 }}>This cannot be undone. All your data will be permanently deleted.</p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={async () => {
                            try {
                              const API = process.env.REACT_APP_API_URL || 'https://blinkapost.onrender.com';
                              const res = await fetch(`${API}/delete-account`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ userId: user.id })
                              });
                              if (!res.ok) throw new Error('Failed');
                              await supabase.auth.signOut();
                            } catch {
                              showToast('Error deleting account — please contact support', 6000);
                              setDeleteConfirm(false);
                            }
                          }}
                          style={{ flex: 1, padding: '11px', background: '#dc2626', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', color: 'white', cursor: 'pointer' }}
                        >
                          Yes, delete everything
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(false)}
                          style={{ flex: 1, padding: '11px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Admin panel — briel.ptcast@gmail.com only */}
            {menuSection === 'admin' && user?.email === 'briel.ptcast@gmail.com' && (
              <AdminPanel supabase={supabase} />
            )}
          </div>
        </>
      )}
      {/* Global assistant shortcut — visible on every tab except assistant itself */}
      {!showOnboarding && activeTab !== 'assistant' && (
        <button onClick={() => setActiveTab('assistant')} style={{ position: 'fixed', bottom: '24px', right: '16px', zIndex: 800, background: '#f97316', color: '#000', border: 'none', borderRadius: '22px', padding: '10px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', boxShadow: '0 4px 16px rgba(249,115,22,0.4)', whiteSpace: 'nowrap' }}>
          <img src="/Blinkapost bulb.png" alt="" style={{ width: '18px', height: '18px', objectFit: 'contain' }} />
          Assistant
        </button>
      )}
    </div>
  );
}

export default App;
